import ControlProposalCard, { ControlProposalEntry } from "./ControlProposalCard.jsx";
import PolicyChatCard from "../pages/agentPolicy/chat/PolicyChatCard.jsx";
import { restoreSessionsFrom } from "../pages/agentPolicy/chat/session.js";
import { migrateLegacyPolicyDrafts, presentWorkspacePolicy, workspacePolicyContext, workspacePolicyToolsWhile, showControlOptions, RECOMMEND_CONTROLS_PROMPT } from "../lib/workspacePolicy.js";
// Cmd/Ctrl-K floating chat console. Wraps the in-browser Pi agent
// (src/lib/piChat.js) so questions about the analytics graph can be
// asked from any page. Visual language mirrors the Matthew overlay
// in App.jsx — fixed full-screen scrim, rectangular dialog, eyebrow
// + footer rows.

import { Suspense, forwardRef, lazy, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useAnalyticsClient } from "../lib/analytics/react.jsx";
import { settingForView } from "../lib/settingsModel.js";
import { chatTurnTrigger, createTurnStops, startChatTurn } from "../lib/analytics/chatTurn.js";
import { CHAT_TRIGGER, EVENT_NAME } from "../lib/analytics/surfaceVocabulary.js";
import { createRoot } from "react-dom/client";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { getDockBodyHost, getDockAvailable, getDockHostPage, subscribeDock, setChatBar } from "../lib/dockStore.js";
import { subscribeChatSeed, takeChatSeed } from "../lib/chatSeedStore.js";
import { registerTriageChatHost } from "../lib/triageChatStore.js";
import { createTriageChatHost } from "../lib/triageChatHost.js";
import { restoreInvestigateChat } from "../lib/investigateCoordinator.js";
import { restoreStandardSuggestionChat } from "../lib/standardSuggest.js";
import { restoreStandardTuningChat } from "../lib/standardTune.js";
import { chatAutoDockEnabled, tailoredStartersEnabled } from "../lib/featureFlags.js";
import { createChatAgent, compactMessages, compactionOptionsFor, convertToLlm, estimateTokens } from "../lib/piChat.js";
import { workspaceToolsWhile } from "../lib/workspaceChatTools.js";
import { chatSubject, describeSubject, openBoardTraces, openBoardWalkHeld, subjectToolsWhile, untoldWalks } from "../lib/chatSubject.js";
import { restoreChatWorkspaceNotes } from "../lib/chatWorkspaceNotes.js";
import { setTranscript, transcriptOf } from "../lib/agentTranscript.js";

import {
    listPrompts,
    findPromptByCommand,
    savePrompt,
    deletePrompt,
    sanitizeCommand,
} from "../lib/userPrompts.js";
import { KernelBlock } from "./KernelPrimitives.jsx";

import { readCanvas as readCanvasStore, subscribeAnyCanvas, destroyCanvas, writeCanvas as writeCanvasStore, cloneCanvas as cloneCanvasStore } from "../lib/canvasStore.js";
import { getStarterCards, subscribeStarterCards, configureStarterCardsTenant } from "../lib/starterCardsStore.js";
import { harnessMemory } from "../lib/harnessMemoryStore.js";
import { describeDiff } from "../lib/harnessMemoryCapture.js";

import { summarizeScoutStep, buildScoutTrace } from "../lib/scoutSteps.js";

import { loadAllChats, loadAllChatMeta, backfillChatMeta, loadChat, saveChat, deleteChat, saveLargeRows, loadLargeRows, purgeLegacyChats, subscribeChatsCleared, ensureIdFence } from "../lib/chatStorage.js";
import { visibleChatMetas, maxChatIdFromMetas } from "../lib/chatIds.js";
import { getCurrentTenantIdSync, getCurrentUser } from "../lib/userInfo.js";
import { serializeChatForStorage, chatSaveFingerprint, hydrateChatFromStorage, isSupportedSchemaVersion, isLoadableChatRecord, INTERRUPTED_BANNER } from "../lib/chatSerializer.js";
import { snapshotForPersistence, loadStore, cloneStore } from "../lib/toolResultStore.js";
import { graphReplies, nextReplyId, replyProgress } from "../lib/graphReplies.js";
import { chatPersistenceEnabled } from "../lib/featureFlags.js";
import { mintServerChatId } from "../lib/chatServerId.js";
import { syncStoredChat, deleteChatEverywhere, markChatDirty, syncResultPatch, planCrossTabDelete, planSaveBoundarySync, planChatClose, classifyChatSyncMessage, hydrateRestoredBlobs } from "../lib/chatSyncEngine.js";
import { restoreTranscript, restoreServerBodyIfNeeded, mergeHydratedItems, mapWithConcurrency, hasBlobPlaceholders } from "../lib/chatRestore.js";
import { runChatPersistenceHydration } from "../lib/chatPersistenceHydrate.js";
import { prepareImage } from "../lib/imagePrepare.js";
import {
    needsContinuationRebuild,
    claimContinuationRebuild,
    installContinuationContext,
    continuationIsDegraded,
} from "../lib/chatContinuation.js";
import { getChatContinuation } from "../lib/chatPersistenceApi.js";
import { onAuthIdentityChange } from "../lib/authTokenStore.js";
import { subscribeChatSync } from "../lib/chatSyncBroadcast.js";

import {
    RUN_SURFACES,
    createLiveRunContext,
} from "../lib/kernelResolver.js";

import { withInteractiveConsent } from "../lib/runConsent.js";

import { useRunConsentBroker } from "./RunConsentProvider.jsx";

import { startChatAudit } from "../lib/chatAuditAgent.js";

import { finalizeRunTranscript, visibleTurnChildren, isTurnHidden } from "../lib/chatTranscriptFinalize.js";
import ExportableBlock, { triggerExportPng } from "./ExportableBlock.jsx";
import { decodeScopePath, DIMENSIONS, useLabels, scopeStepLabel } from "../lib/dimensionDefs.js";
import { requestNavigation, subscribeNavigation } from "../lib/navigationStore.js";
import { openKernelLink } from "../lib/kernelLinkNavigation.js";
import { decodeTab, scopePathFor } from "../lib/tabModel.js";
import { rowsToObjects } from "../lib/analyticsClient.js";

import { useMyPermissions } from "../lib/permissions.js";
import { getScopeDimForColumn, pickLabelColumnForDim } from "../lib/analyticsTool.js";
import { useDataTableColumns } from "../lib/columnSelection.js";

import DataTable from "./DataTable.jsx";
import ScrollArea from "./ScrollArea.jsx";
import Markdown from "./Markdown.jsx";
import TabStrip from "./TabStrip.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import InvestigationGraphPill from "./InvestigationGraphPill.jsx";
import { applyHarnessNoteToChat, runHarnessContinue } from "../lib/harnessTurn.js";
import { claimDisplayRows } from "../lib/investigationGraphView.js";
import { expandProgressLabel, graphKeyFor, setActiveGraphChat } from "../lib/investigationGraphRun.js";
import { describeRecipe, recipeProgressLabel } from "../lib/graphRecipes.js";
import { createRecipeStore } from "../lib/recipeStore.js";
import { syncOrgRecipes } from "../lib/orgRecipes.js";
import { GraphBlock, GraphDeltaMarker, addedText } from "./InvestigationGraphView.jsx";
import Select from "./Select.jsx";
import Button from "./ui/Button.jsx";
import { FIELD, MENU, MENU_ITEM_ON } from "./ui/styles.js";
import { onChatSettingsChanged } from "../lib/chatSettingsChanges.js";

import { makeClaimRefLinkifier } from "./claimRefLink.jsx";
import { useSessionLinkifier } from "./proseSessionLink.jsx";
import { latestAnswerText } from "../lib/answerEvidence.js";
import { publishChatAnswer, useBoardClaims } from "../lib/workspaceStore.js";

import { getChatCreds, isChatConfigured } from "../lib/chatCreds.js";
import ChatSettingsDialog from "./settings/ChatSettingsDialog.jsx";
import { findCuratedProvider, defaultThinkingLevelFor, mergeLoadedThinkingLevel, modelMandatesThinking, resolveThinkingLevel } from "../lib/modelCatalog.js";
import { composerPlaceholder, computeComposerGate } from "../lib/composerGate.js";
import { createStallWatchdog } from "../lib/stallWatchdog.js";
import ChatStarterQuestions from "./ChatStarterQuestions.jsx";
import ChatQuestionCard, { ChatReplyContext } from "./ChatQuestionCard.jsx";
import ChatSteering from "./ChatSteering.jsx";
import { StandardSuggestionCard } from "./StandardSuggestCards.jsx";
import { StandardRevisionCard } from "./StandardRevisionCard.jsx";
import ChatPinsCard, { chatPinsOf, pinsHeading } from "./ChatPinsCard.jsx";
import WalkthroughCard from "./Walkthrough.jsx";
import ReadingCard from "./ReadingCard.jsx";
import BoardCasesCard from "./BoardCasesCard.jsx";
import { buildStarterPlaybook } from "../lib/tenantScoutPlaybook.js";
import { describeOpenCanvas, setTraceCanvasChat, useTraceCanvas, useTraceCanvasOpen } from "../lib/traceCanvasStore.js";
import { ADDED_ASK, addedOf, chatTray, registerChat, takeChatTray, trayPrompt } from "../lib/chatContextStore.js";
import { appendChatJob, onChatJob, onChatJobPatch, patchJobRows } from "../lib/chatJobs.js";
import { useWalkthrough } from "../lib/traceWalkthrough.js";
import ChatTray, { AddedList } from "./ChatTray.jsx";
import { DEFAULT_RANGE } from "../lib/dateRangePresets.js";

const LIVE_COMMAND_RUN_CONTEXT = createLiveRunContext({
    surface: RUN_SURFACES.CHAT,
    agentStepApproved: true,
});

// Suppress the horizontal axis on the transcript: nothing inside it
// (markdown, code blocks, charts) is meant to scroll sideways. Long
// runs that would otherwise trigger overflow are caught by
// `overflow-wrap: anywhere` on text and `overflow-x-auto` on
// individual `<pre>` blocks.
const TRANSCRIPT_OS_OPTIONS = Object.freeze({ overflow: { x: "hidden" } });
// Stable empty list so the MCP-failure notice memo/compare doesn't churn.

// Mirror ContextPanel's helper — OverlayScrollbars puts the actual
// scrollable element inside the host (the data attribute or .os-viewport).
function getOverlayViewport(node) {
    return (
        node?.querySelector("[data-overlayscrollbars-viewport]") ||
        node?.querySelector(".os-viewport") ||
        node ||
        null
    );
}

// Default layer palette for stacked charts when the agent doesn't pick
// colors. Sticks to dashboard tokens to stay visually consistent.
function useGlobalHotkey(onToggle) {
    useEffect(() => {
        const handler = (e) => {
            const isK = e.key === "k" || e.key === "K";
            if (isK && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                onToggle();
            }
        };
        // Custom event so anything in the app can flip the chat open
        // without importing this module. The sidebar's chat button
        // dispatches "origin-chat-toggle".
        const evt = () => onToggle();
        window.addEventListener("keydown", handler);
        window.addEventListener("origin-chat-toggle", evt);
        return () => {
            window.removeEventListener("keydown", handler);
            window.removeEventListener("origin-chat-toggle", evt);
        };
    }, [onToggle]);
}

// Event → transcript-tree reducer. Pi emits a turn-based event stream;
// we fold it into a tree that mirrors the agent's actual structure:
//
//   items: top-level array, alternating user prompts and turns
//     { kind: "user", text }
//     { kind: "turn", id, status, children: [ ...assistant/thinking/tool ] }
//       children:
//         { kind: "thinking", text }
//         { kind: "assistant", text }
//         { kind: "tool", id, name, args, status, summary, details }
//
// Each `prompt()` produces one or more turns (LLM round-trips). A turn
// can contain thinking deltas, streamed assistant text, and any number
// of tool calls before the next turn begins.
// Active-chat resolution NEVER falls back to another chat. Transient windows
// where `activeId` isn't in `chats` are real (hydrate swap, tab close,
// cross-tab delete), and the old `|| chats[0]` fallback rendered the OLDEST
// chat's transcript under the new chat's highlighted tab for that window.
// Null makes the view render its empty/loading state instead. Exported for
// unit testing — the rule, not the render, is what regressed.
// The chat a staged first message lands in: an existing empty chat, unless
// that chat holds unsent text the seed would overwrite; otherwise a new one.
export function seededChatTarget(chats, seedText, nextId) {
    const seed = typeof seedText === "string" && seedText.trim() ? seedText : null;
    const empty = chats.find((c) => !(c.hasItems || c.items?.length) && (!seed || !c.input));
    return { id: empty ? empty.id : nextId, create: !empty, seed };
}

// How a staged message brings the chat on screen, the same way the hotkey
// does: nothing when the chat is already docked, an activation when the dock
// exists but is not showing the chat, the floating overlay otherwise.
export function seedSurface({ dockAvailable, dockBodyHost, autoDock }) {
    if (dockAvailable && dockBodyHost) return "docked";
    if (dockAvailable && autoDock) return "activate";
    return "overlay";
}

// Where a run's transcript opens from another surface (the Reviews lens's
// "Open the transcript"): the page's chat column whenever the page can host
// one, and the floating overlay only where there is no dock host. Docked
// means the column is open AND showing the chat; the chat's host element
// stays bound while the column is collapsed, so the host alone does not
// mean the chat is on screen. Anything short of that activates, which
// opens a collapsed column and flips it to chat. Reading a long transcript
// belongs beside the lens, not over it.
export function transcriptSurface({ dockHost, dockOpen, dockBodyHost }) {
    if (dockOpen && dockBodyHost) return "docked";
    if (dockHost) return "activate";
    return "overlay";
}

// A question another surface asks in a chat it staged. submit() acts on the
// active chat, and the staged chat was only just made active, so the ref is
// pointed at it here rather than waiting for the effect.
// `mode` "followup" queues the question behind a run already going in that
// chat instead of steering into it (Investigate's next dig waits its turn).
// The chat's items with each tool row whose sub-agent work still runs in
// the background (a spawn, or a dispatch, that yielded to the person's
// message) showing that work's live state. Tool rows sit inside turns. The
// same array when no row has such work.
export function withChildDetails(items, registry) {
    let changed = false;
    // Spread the live state into details on a fresh reference so React sees
    // a new identity for the row; state.messages is not deep-copied (the
    // spawn's onUpdate path surfaces the live array too).
    const fresh = (it) => {
        if (it?.kind !== "tool") return it;
        const handle = registry.get(`sub_${it.id}`) || registry.get(`batch_${it.id}`);
        if (!handle) return it;
        changed = true;
        return { ...it, details: { ...handle.state, abort: null } };
    };
    const next = (items || []).map((it) => {
        if (it?.kind !== "turn" || !Array.isArray(it.children)) return fresh(it);
        const children = it.children.map(fresh);
        return children.some((child, i) => child !== it.children[i]) ? { ...it, children } : it;
    });
    return changed ? next : items;
}

export function stagedPromptSender(activeIdRef, submit) {
    return (id, prompt, mode = null) => {
        activeIdRef.current = id;
        return submit(mode ? { explicitPrompt: prompt, mode } : { explicitPrompt: prompt });
    };
}

export function resolveActiveChat(chats, activeId) {
    return chats.find((c) => c.id === activeId) || null;
}

// The chat-activation reset clears a stale agent-build error from a prior
// failed activation — but hydration may have JUST set chat.error to the
// interrupted banner, and a blanket `error: null` is how the banner used to
// be silently erased on every first activation. Value rule instead of
// statement-order rule: the reset preserves the banner no matter where in
// the activation sequence it runs. Exported for unit testing.
export function clearedStaleActivationError(error) {
    return error === INTERRUPTED_BANNER ? error : null;
}

export function applyAgentEvent(items, event, runStartTurns = 0) {
    switch (event.type) {
        case "turn_start": {
            // Positional id, but skip ids already present: the finalize
            // pass drops a run's empty turns WITHOUT renumbering the
            // survivors (chatTranscriptFinalize.js), so a bare
            // turn-count mint re-issues a surviving turn's id. Turn ids
            // are React keys and the item_ui_state key, so a collision
            // makes two turns render and toggle as one. Same invariant
            // repair as reidMergedTail in chatSyncEngine.js.
            const taken = new Set(items.map((it) => it?.id).filter(Boolean));
            let turnIdx = items.filter((it) => it.kind === "turn").length;
            while (taken.has(`turn:${turnIdx}`)) turnIdx++;
            return [
                ...items,
                { kind: "turn", id: `turn:${turnIdx}`, status: "running", children: [] },
            ];
        }

        case "turn_end":
            // Capture stopReason from the assistant message so the
            // turn header can surface "length", "error", etc. when
            // the model bailed for a non-normal reason.
            return updateLastTurn(items, (t) => ({
                ...t,
                status: "done",
                stopReason: event.message?.stopReason || t.stopReason || null,
            }));

        case "message_start":
            // No-op: assistant/thinking nodes are created lazily on
            // their first delta so the tree order matches arrival
            // order. Eagerly inserting an empty assistant placeholder
            // here pinned text above thinking even when the model
            // emitted thinking first.
            return items;

        case "message_update": {
            // The agent emits message_update for every assistant
            // delta type — text, thinking, and tool-call args. We
            // surface them all so the tree reflects what the model is
            // actively doing.
            const inner = event.assistantMessageEvent;
            if (!inner) return items;
            // The model call restarted after the provider went quiet
            // (piStreamFn.js): the retry rewrites the message from the
            // top, so what the abandoned attempt streamed into the turn
            // is not part of the answer. Nothing else is in the turn yet;
            // tools run only once the message has completed.
            if (inner.restart) items = updateLastTurn(items, (t) => ({ ...t, children: [] }));
            if (inner.type === "text_delta") {
                return inner.delta ? extendOrPushChild(items, "assistant", inner.delta) : items;
            }
            if (inner.type === "thinking_delta") {
                return inner.delta ? extendOrPushChild(items, "thinking", inner.delta) : items;
            }
            if (inner.type === "toolcall_start") {
                // Placeholder tool node — name and toolCallId aren't
                // known until toolcall_end. Keyed by contentIndex
                // within the turn so subsequent deltas land here.
                return appendToLastTurn(items, {
                    kind: "tool",
                    id: `pending:${inner.contentIndex}`,
                    contentIndex: inner.contentIndex,
                    name: "tool",
                    args: "",
                    argsStreaming: true,
                    status: "preparing",
                    summary: "",
                    details: null,
                });
            }
            if (inner.type === "toolcall_delta") {
                return updatePendingToolByContentIndex(items, inner.contentIndex, (t) => ({
                    ...t,
                    args: (typeof t.args === "string" ? t.args : "") + (inner.delta || ""),
                }));
            }
            if (inner.type === "toolcall_end") {
                const tc = inner.toolCall || {};
                return updatePendingToolByContentIndex(items, inner.contentIndex, (t) => ({
                    ...t,
                    id: tc.id || t.id,
                    name: tc.name || t.name,
                    args: tc.arguments ?? t.args,
                    argsStreaming: false,
                    status: "queued",
                }));
            }
            return items;
        }

        case "tool_execution_start":
            // Upgrade an existing placeholder (matched by toolCallId
            // from toolcall_end) to running, otherwise append fresh —
            // providers that don't stream toolcall_* events still get
            // a sensible tool node.
            return upgradeOrAppendTool(
                items,
                event.toolCallId,
                (t) => ({
                    ...t,
                    name: event.toolName || t.name,
                    args: event.args ?? t.args,
                    argsStreaming: false,
                    status: "running",
                }),
                {
                    kind: "tool",
                    id: event.toolCallId,
                    name: event.toolName || event.name || "tool",
                    args: event.args ?? null,
                    argsStreaming: false,
                    status: "running",
                    summary: "",
                    details: null,
                },
            );

        case "tool_execution_update": {
            // Tools can stream partial results while running. Merge the
            // partial details onto the existing tool node so renderers
            // (analytics table, raw JSON) can show progress in place.
            const partial = event.partialResult;
            if (!partial) return items;
            return updateToolByCallId(items, event.toolCallId, (tool) => ({
                ...tool,
                details: partial.details ?? tool.details,
                summary: summariseResult(partial) || tool.summary,
            }));
        }

        case "tool_execution_end": {
            const text = event.result?.content?.[0]?.text;
            return updateToolByCallId(items, event.toolCallId, (tool) => ({
                ...tool,
                status: event.isError ? "error" : "done",
                summary: event.isError
                    ? text || "tool error"
                    : summariseResult(event.result),
                details: event.result?.details ?? null,
            }));
        }

        // A status row the harness writes at run end (what it is doing about

        // a recipe), updated in place by id as its work moves on.

        case "harness_note": {
            const id = event.id || "harness-note";
            if (event.remove) return items.filter((it) => !(it.kind === "system" && it.id === id));
            const node = { kind: "system", id, text: event.text || "", detail: event.detail || null, harness: true };
            const idx = items.findIndex((it) => it.kind === "system" && it.id === node.id);
            if (idx >= 0) { const next = items.slice(); next[idx] = { ...next[idx], ...node }; return next; }
            if (lastTurnIndex(items) < 0) return items;
            return [...items, node];
        }

        case "agent_end": {
            const last = event.messages?.[event.messages.length - 1];
            return finalizeRunTranscript(items, {
                runStartTurns: typeof runStartTurns === "number" ? runStartTurns : 0,
                errorMessage: last?.errorMessage || null,
            });
        }

        default:
            return items;
    }
}

function lastTurnIndex(items) {
    for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].kind === "turn") return i;
    }
    return -1;
}

function updateLastTurn(items, updater) {
    const idx = lastTurnIndex(items);
    if (idx < 0) return items;
    const next = items.slice();
    next[idx] = updater(next[idx]);
    return next;
}

function appendToLastTurn(items, child) {
    return updateLastTurn(items, (turn) => ({
        ...turn,
        children: [...turn.children, child],
    }));
}

// Extend the trailing child of the last turn if it matches `kind`,
// otherwise append a fresh node. Used for both text and thinking
// deltas — the tail-extend keeps streaming chunks coalesced, and
// the push-new path means a thinking → text → thinking sequence
// renders as three distinct blocks in the order Pi emitted them.
function extendOrPushChild(items, kind, delta) {
    return updateLastTurn(items, (turn) => {
        const last = turn.children[turn.children.length - 1];
        if (last?.kind === kind) {
            const children = turn.children.slice(0, -1);
            children.push({ ...last, text: last.text + delta });
            return { ...turn, children };
        }
        return { ...turn, children: [...turn.children, { kind, text: delta }] };
    });
}

function updateToolByCallId(items, callId, updater) {
    for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].kind !== "turn") continue;
        const turn = items[i];
        const ci = turn.children.findIndex((c) => c.kind === "tool" && c.id === callId);
        if (ci < 0) continue;
        const children = turn.children.slice();
        children[ci] = updater(children[ci]);
        const next = items.slice();
        next[i] = { ...turn, children };
        return next;
    }
    return items;
}

// Mutate the pending tool placeholder for a given content index in the
// current turn. The placeholder id is `pending:<contentIndex>` until
// toolcall_end fills in the real toolCallId.
function updatePendingToolByContentIndex(items, contentIndex, updater) {
    return updateLastTurn(items, (turn) => {
        const ci = turn.children.findIndex(
            (c) => c.kind === "tool" && c.contentIndex === contentIndex,
        );
        if (ci < 0) return turn;
        const children = turn.children.slice();
        children[ci] = updater(children[ci]);
        return { ...turn, children };
    });
}

// Find an existing tool node by toolCallId and apply the updater; if
// no match exists, append `fallback` as a fresh node. Lets
// tool_execution_start either upgrade a placeholder created during
// toolcall_* streaming or stand on its own for providers that skip
// those events.
function upgradeOrAppendTool(items, callId, updater, fallback) {
    let matched = false;
    const next = items.slice();
    for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].kind !== "turn") continue;
        const turn = next[i];
        const ci = turn.children.findIndex((c) => c.kind === "tool" && c.id === callId);
        if (ci >= 0) {
            const children = turn.children.slice();
            children[ci] = updater(children[ci]);
            next[i] = { ...turn, children };
            matched = true;
            break;
        }
    }
    if (matched) return next;
    return appendToLastTurn(items, fallback);
}

export function plainToolOutcome(details) {
    if (!details || typeof details !== "object") return "";
    // A recipe saved at run end reads as its name and what happened to it.
    if (details.kind === "save_recipe" && details.recipe) return `${details.recipe.name || details.recipe.id} \u00b7 ${details.stored?.matched === "stored" ? "generalized" : details.stored?.matched === "org" ? "adopted from the org" : "saved"}`;
    const rows = Array.isArray(details.rows) ? details.rows : Array.isArray(details.result) ? details.result : null;
    if (rows) return `${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"}`;
    if (details.kind === "claim") return [details.claimId, details.status].filter(Boolean).join(" \u00b7 ");
    if (details.kind === "kernelSuggestion" && details.command) return `/${details.command}`;
    if (details.kind === "standardSuggestion" && details.name) return details.name;
    if (details.kind === "standardRevision" && details.name) return `${details.name} v${(Number(details.fromVersion) || 1) + 1}`;
    if (details.kind === "operatorQuestion" && details.question) return details.question;
    if (typeof details.error === "string" && details.error) return details.error;
    return "";
}

function summariseResult(result) {
    if (!result) return "";
    return plainToolOutcome(result.details ?? result) || "done";
}

// A stored summary from before summaries were words is a clipped JSON dump.
const JSONISH = /^\s*[[{]/;

// Tool names as a reader says them. Unlisted names read with their
// underscores as spaces.
const TOOL_WORDS = {
    analytics_query: "Query",
    pivot: "Pivot",
    execute_python: "Python",
    run_recipe: "Recipe",
    expand: "Expand",
    expand_all: "Expand all",
    expand_preview: "Expand preview",
    claim: "Claim",
    render_block: "Chart",
    spawn_subagent: "Sub-agent",
    spawn_batch: "Batch",
    schema_search: "Schema search",
    save_recipe: "Save recipe",
    workspace_state: "Investigation",
    workspace_lay_on: "Lay on the investigation",
    workspace_take_off: "Take off the investigation",
    workspace_open: "Open on the investigation",
    workspace_hide: "Tuck away on the investigation",
    workspace_point: "Point on the investigation",
    investigate_read_turns: "Read turns",
    investigate_search: "Search traces",
    investigate_list_calls: "List calls",
    investigate_read_call: "Read call",
    investigate_machine_records: "Machine records",
    investigate_nearby_sessions: "Nearby traces",
    session_reading: "Read the reading",
    search_session: "Search the trace",
    read_trace_calls: "Read the calls",
    walk_through: "Walk through",
    board_cases: "Cases here",
    case_note: "Note on the case",
    ask_operator: "Question",
    list_findings: "Read findings",
    suggest_standard: "Suggested standard",
    revise_standard: "Revised standard",
};
export function toolWord(name) {
    const n = String(name || "");
    if (TOOL_WORDS[n]) return TOOL_WORDS[n];
    const spaced = n.replace(/__/g, " ").replace(/_/g, " ").trim();
    return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : "Tool";
}

// The questions put to the person that a message of theirs follows: answered,
// so a chat read back from storage does not offer them again.
export function answeredQuestionIds(items) {
    const out = new Set();
    let open = [];
    for (const it of items || []) {
        if (it?.kind === "user") { open.forEach((id) => out.add(id)); open = []; continue; }
        for (const c of it?.children || []) if (c?.details?.kind === "operatorQuestion" && c.id) open.push(c.id);
    }
    return out;
}

// The jobs whose results are for the reader: a walkthrough's story, a
// trace's reading, the cases touching the workspace (lib/chatSubject.js).
const JOB_CARDS = new Set(["walkthrough", "sessionReading", "boardCases"]);

const isReaderOutput = (c) => c?.kind === "tool" && c.status === "done"
    && (c.details?.kind === "kernelBlock" || c.name === "render_block" || c.details?.kind === "navSuggestion" || c.details?.kind === "navApplied"
        || c.details?.kind === "standardSuggestion" || c.details?.kind === "standardRevision" || c.details?.kind === "operatorQuestion"
        || JOB_CARDS.has(c.details?.kind) || !!chatPinsOf(c));

// A folded run's outputs as they are drawn: each on its own, but the pins of
// every call that pinned some, in one list where the first of them was.
export function foldedOutputs(outputs) {
    const out = [];
    let pins = null;
    for (const node of outputs || []) {
        const some = chatPinsOf(node);
        if (!some) { out.push({ node }); continue; }
        if (!pins) { pins = []; out.push({ pins, id: node.id }); }
        pins.push(...some);
    }
    return out;
}

// Consecutive finished turns that only did work (the turns before a run's
// answer) read as one line per run: how many steps, and what they used.
// Returns item index -> { id, first, count, names, outputs } for every
// member, `outputs` being what the run made for the reader (its charts).
export function workRuns(items) {
    const out = new Map();
    const isWork = (it) => it?.kind === "turn" && it.status === "done" && !it.final;
    let i = 0;
    while (i < items.length) {
        if (!isWork(items[i])) { i++; continue; }
        let j = i;
        const members = [];
        while (j < items.length && isWork(items[j])) {
            if (!isTurnHidden(items[j])) members.push(j);
            j++;
        }
        if (members.length) {
            const names = [...new Set(members.flatMap((k) => visibleTurnChildren(items[k])
                .filter((c) => c.kind === "tool")
                .map((c) => toolWord(c.name))))];
            const outputs = members.flatMap((k) => visibleTurnChildren(items[k]).filter(isReaderOutput));
            const run = { id: `work:${items[members[0]].id || members[0]}`, first: members[0], members, count: members.length, names, outputs };
            for (const k of members) out.set(k, run);
        }
        i = j;
    }
    return out;
}

export function WorkRunLine({ run, open, onToggle }) {
    const steps = `${run.count} step${run.count === 1 ? "" : "s"}`;
    const used = run.names.length > 4 ? `${run.names.slice(0, 4).join(", ")} +${run.names.length - 4}` : run.names.join(", ");
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            data-work-run=""
            className="flex min-w-0 max-w-full items-center gap-[6px] bg-transparent text-left text-[12px] leading-[1.3] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer"
        >
            <Chevron expanded={open} />
            <span className="truncate">{`Worked through ${steps}${used ? ` \u00b7 ${used}` : ""}`}</span>
        </button>
    );
}

// Truncate a freshly-sent user prompt down to something tab-shaped.
// Stops at the first newline and clips to ~28 chars so long prompts
// don't blow out the tab width.
function titleFromPrompt(text) {
    const firstLine = text.split(/\r?\n/, 1)[0].trim();
    if (!firstLine) return "New chat";
    return firstLine.length > 28 ? `${firstLine.slice(0, 27)}…` : firstLine;
}

// The Pi message history the LLM should be rewound to when we go back
// to just before the user item at `userIdx`: the snapshot captured at
// the most recent completed turn before it (empty if none). Shared by
// editAndRerun (replay), rewindToUser (truncate in place), and
// forkFromUser (branch to a new tab) so all three reconstruct the same
// pre-message LLM state.
function snapshotBeforeUserIdx(items, userIdx) {
    for (let i = userIdx - 1; i >= 0; i--) {
        const it = items[i];
        if (it.kind === "turn" && it.snapshot) return it.snapshot.slice();
    }
    return [];
}

// Valid Pi thinking budgets, in escalation order. The UI cycles
// through these and forwards the selection to `agent.state.thinkingLevel`.
const THINKING_LEVELS = ["off", "minimal", "low", "medium", "high", "xhigh"];

// Compact thinking-depth meter — five stacked lines lit from the bottom
// up, one per level above "off" (off = none lit, xhigh = all five). Fixed
// width so it sits beside the settings gear without the old "THINK OFF ▾"
// label ballooning the input row.
function ThinkingDepthIcon({ level }) {
    const idx = Math.max(0, THINKING_LEVELS.indexOf(level)); // 0..5
    const ys = [3, 5.5, 8, 10.5, 13]; // five lines, top → bottom
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
            {ys.map((y, i) => (
                <line key={y} x1="3.5" y1={y} x2="12.5" y2={y} opacity={(ys.length - i) <= idx ? 1 : 0.25} />
            ))}
        </svg>
    );
}

// The model the next request will actually carry: the live agent's resolved
// provider/model, falling back to the configured summary only for the window
// before the agent exists. `offDisabled` must key off the agent's pair — the
// creds summary loads on a different clock and can name a different provider
// entirely, and a mandatory-thinking model handed "off" 400s the turn.
export function thinkingPillModel(activeAgent, credsSummary) {
    const id = activeAgent?.state?.model?.id ?? credsSummary.model;
    const provider = activeAgent?.state?.model?.provider ?? credsSummary.provider;
    return { id, provider, offDisabled: modelMandatesThinking(provider, id) };
}

const SAMPLE_QUERIES = [
    {
        title: "AI activity by use case",
        blurb: "Leading use cases over the last 30 days, grouped by week.",
        body: "Group the last 30 days of activity by use case and chart the leading themes by week. Make each topic clickable for details.",
    },
    {
        title: "MCP tool adoption",
        blurb: "MCP tools spreading across endpoints over eight weeks.",
        body: "Chart the leading MCP tools by distinct endpoint each week for the last 8 weeks. Make each tool clickable.",
    },
    {
        title: "Skill adoption",
        blurb: "Agent skills appearing and disappearing over 30 days.",
        body: "Chart daily use of agent skills under `.claude/skills/<name>/` for the last 30 days. Highlight skills that appeared or disappeared, and make each skill clickable.",
    },
    {
        title: "Token usage",
        blurb: "Token usage, coverage, and highest-cost submissions over seven days.",
        body: "Break down token usage for the last 7 days by provider, model, client surface, endpoint, and registered user. Include input, output, cache, and reasoning tokens; calls_total and calls_with_tokens coverage; a byte floor for unmeasured traffic; and the 10 most expensive submissions. Show the results with charts and tables.",
    },
    {
        title: "New since last week",
        blurb: "New MCP servers, tools, models, providers, and endpoints.",
        body: "Compare this week with last week. List MCP servers, tools, models, providers, and endpoints seen for the first time, with links to their source."
    },
    {
        title: "AI usage concentration",
        blurb: "Prompt and tool-call concentration by endpoint and user.",
        body: "For the last 30 days, calculate the share of prompts and tool calls from the leading endpoints and their registered users. Make endpoint and user cells clickable."
    }
];

// The questions a chat beside a workspace opens with, about the traces on
// it: each shows a short hint and is sent whole, for the chat to answer on
// the board (the sessions it lays on, the notes it pins on turns). A
// workspace with no anchor (started from sessions, a search, or other
// workspaces) is asked about every session on it instead (`across`). Walking
// through (`walk`) is a job of the chat's (walk_through, lib/chatSubject.js),
// where a walkthrough stands beside this workspace's board.
const WALK_ASK = "Walk me through this investigation: what it set out to do, the decisions and acts that shaped it, what went wrong, and how it ended.";
const WORKSPACE_QUERIES = [
    {
        title: "Walk me through it",
        hint: "What it set out to do, and how it ended",
        body: WALK_ASK,
        across: "Walk me through the traces on this investigation, the ones inside its investigations too: what each set out to do, what it did, how it ended, and how they connect, if they do.",
    },
    {
        title: "What did the review find?",
        hint: "The findings that matter, where they happened",
        body: "Which of the review's findings on the anchor trace matter most, and why? Pin a note on the cell where each one happened.",
        across: "Which of the review's findings on the traces on this investigation matter most, and why? Pin a note on the cell where each one happened.",
    },
    {
        title: "What else was involved?",
        hint: "Traces on the same files, laid on the board",
        body: "Find other traces that worked on the same files, repositories or systems as the anchor trace around the same time, and lay the most relevant ones on the investigation.",
        across: "Find other traces that worked on the same files, repositories or systems as the traces on this investigation around the same time, and lay the most relevant ones on the investigation.",
    },
    {
        title: "Was anything risky?",
        hint: "Credentials, destructive commands, production",
        body: "Did the anchor trace do anything risky: handle credentials, run destructive commands, or change production? Pin a note on each place it did.",
        across: "Did any trace on this investigation do anything risky: handle credentials, run destructive commands, or change production? Pin a note on each place it did.",
    },
    {
        title: "Prevent this activity",
        hint: "Help protect against this behavior",
        body: "Recommend agent configuration controls to help protect against the behavior in this investigation. Use its findings and cited evidence to begin a control proposal, explain which agent types it applies to, and show the expected impact and limitations. Keep any changes as a proposal for me to review.",
        investigation: true,
    },
];

// A fresh chat's starters, as cards on the dotted diagram sheet: each shows
// its blurb (so a long, recipe-style body does not dump into the grid) and
// submits its whole body.
function StarterCards({ queries, onPick, disabled }) {
    return (
        <div className="rounded-[10px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] bg-[radial-gradient(circle,var(--diagram-dot)_0.9px,transparent_0.9px)] [background-size:7px_7px] p-[8px]">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-[6px]">
                {queries.map((q) => (
                    <button
                        key={q.title}
                        type="button"
                        onClick={() => onPick(q.body)}
                        disabled={disabled}
                        className="group flex flex-col gap-[4px] rounded-[7px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[8px] text-left transition-colors hover:border-[color:var(--line-strong)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <span className="flex items-baseline justify-between gap-[6px] text-[11px] leading-[1.2] text-[color:var(--ink)]">
                            {q.title}
                            <span className="text-[color:var(--muted-soft)] transition-colors group-hover:text-[color:var(--green)]">↗</span>
                        </span>
                        <span className="text-[11px] leading-[1.4] text-[color:var(--muted-soft)] transition-colors group-hover:text-[color:var(--muted)]">
                            {q.blurb || q.body}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

// A fresh chat beside a workspace: who it is and what it sees, over its
// questions, each a light cell with a one-line hint. The framed dock it opens
// in gives it the pane's gutters; in a dock under the board, wide and short,
// the questions run in a row beside it. Each is asked of the chat, which
// walks through the workspace as a job of its own where the board holds a
// walkthrough to run (lib/chatSubject.js walk_through).
export function WorkspaceChatStart({ onPick, disabled = false }) {
    // Asked of its anchor, or of every session where it has none.
    const open = useTraceCanvasOpen();
    const canvas = useTraceCanvas(open?.id || null);
    const anchored = !!canvas.anchor;
    return (
        <div className="@container w-full min-w-0" data-canvas-chat-empty="">
            <div className="mx-auto flex w-full max-w-[1120px] min-w-0 flex-col gap-[12px] @min-[720px]:flex-row @min-[720px]:items-center @min-[720px]:gap-[24px]">
                <div className="flex min-w-0 flex-col gap-[4px] @min-[720px]:w-[240px] @min-[720px]:shrink-0">
                    <span className="text-[12px] text-[color:var(--muted-soft)]">Analytics agent</span>
                    <span className="text-[14px] leading-[1.3] text-[color:var(--ink)]">Ask about this investigation</span>
                    <span className="text-[11.5px] leading-[1.45] text-[color:var(--muted)]">{canvas.investigationId ? "Explore the traces, investigate risk, or prepare controls in this conversation." : "It sees the traces on the board and the cell you select."}</span>
                </div>
                <div className="grid min-w-0 flex-1 grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[6px] @min-[1000px]:grid-cols-4">
                    {WORKSPACE_QUERIES.filter((q) => !q.investigation || canvas.investigationId).map((q) => (
                        <button
                            key={q.title}
                            type="button"
                            onClick={() => q.investigation ? showControlOptions() : onPick(anchored ? q.body : q.across || q.body)}
                            disabled={disabled}
                            className="flex min-w-0 flex-col gap-[2px] rounded-[7px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[7px] text-left cursor-pointer hover:border-[color:var(--line-strong)] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <span className="truncate text-[11.5px] leading-[1.3] text-[color:var(--ink)]">{q.title}</span>
                            <span className="line-clamp-2 text-[11px] leading-[1.35] text-[color:var(--muted-soft)]">{q.hint}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Shortcuts submit into the existing conversation so follow-ups retain the
// activity under discussion and any current proposal.
const WORKSPACE_ASKS = [
    { id: "prevent", word: "Prevent this activity", body: `Focus on the activity we have been discussing in this conversation. ${RECOMMEND_CONTROLS_PROMPT}`, investigation: true },
    { id: "summarize", word: "Summarize the evidence", body: "Summarize the evidence for the activity we have been discussing in this investigation. Cite the relevant findings and tool calls, distinguish confirmed facts from uncertainty, and explain what still needs investigation. Do not change the policy draft." },
];

export function WorkspaceAsks({ onPick, disabled = false, framed = false }) {
    const open = useTraceCanvasOpen();
    const canvas = useTraceCanvas(open?.id || null);
    const asks = WORKSPACE_ASKS.filter((a) => !a.investigation || canvas.investigationId);
    if (!open || !asks.length) return null;
    return (
        <div
            className={framed
                ? "-mx-[12px] flex min-w-0 items-stretch overflow-hidden [&:not(:first-child)]:mt-[8px] [&:not(:first-child)]:border-t [&:not(:first-child)]:border-[color:var(--line-soft)]"
                : "flex flex-wrap items-center gap-x-[12px] pt-[8px]"}
            data-workspace-asks=""
        >
            {asks.map((a) => (
                <button
                    key={a.id}
                    type="button"
                    onClick={() => onPick(a.body)}
                    disabled={disabled}
                    className={`flex cursor-pointer items-center text-[11px] leading-[16px] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:cursor-not-allowed disabled:text-[color:var(--muted-soft)] ${framed ? "h-[32px] min-w-[56px] shrink border-r border-[color:var(--line-soft)] px-[9px]" : "shrink-0 whitespace-nowrap"}`}
                    data-workspace-ask={a.id}
                >
                    {framed ? <OverflowTooltip text={a.word} className="min-w-0" /> : a.word}
                </button>
            ))}
        </div>
    );
}

// What /recipes shows per recipe: the stored fields plus the gate's view of
// it (confidence, archived or stale, a failure streak) as short tags.
export function recipeRowsForList(recipes) {
    return (recipes || []).map((r) => ({
        id: r.id,
        name: r.name,
        source: r.source || "distilled",
        confidence: Number.isFinite(r.confidence) ? r.confidence.toFixed(2) : null,
        status: r.status || "active",
        tags: [
            r.status === "archived" ? `archived: ${String(r.retiredReason || "").replace(/_/g, " ") || "retired"}` : null,
            r.stale ? "not in this schema" : null,
            r.streak >= 2 && r.status === "active" ? `${r.streak} failures in a row` : null,
            lastRunTag(r.lastRun),
        ].filter(Boolean).join(" \u00b7 "),
        uses: r.uses || 0,
        successes: r.successes || 0,
        savedAvg: Number.isFinite(r.savedAvg) ? Math.round(r.savedAvg) : null,
        // What other browsers recorded of it, read back from the trace store.
        orgUses: r.orgUses || 0,
        orgSuccesses: r.orgSuccesses || 0,
        orgBrowsers: r.orgBrowsers || 0,
        orgSavedAvg: Number.isFinite(r.orgSavedAvg) ? Math.round(r.orgSavedAvg) : null,
        when: r.when,
        paraphrases: Array.isArray(r.paraphrases) ? r.paraphrases.slice(0, 2) : [],
        steps: describeRecipe(r),
        params: (r.params || []).map((p) => `${p.name}${p.required ? "" : " (optional)"}`).join(", "),
        exampleTerm: r.example?.term ?? r.example?.params?.term ?? null,
        // Where the org's copy came from: an org recipe, or a local one the
        // org's store also holds.
        org: r.org ? { savedAt: r.org.savedAt || null, endpointId: r.org.endpointId || null, traceId: r.org.traceId || null, variants: r.org.variants || 1 } : null,
    }));
}

// What the last run of a recipe rested on, or why it failed, in the
// gate's words.
const BASIS_WORDS = { cited: "cited the recipe's anchor", near: "cited what the recipe's anchor led to", saved: "supported claims under the origin cost" };
export function lastRunTag(lastRun) {
    if (!lastRun) return null;
    if (lastRun.success) return `last run: ${BASIS_WORDS[lastRun.basis] || "success"}`;
    return `last run failed: ${lastRun.reason || "no reason recorded"}`;
}

// The org's copy of a recipe in one line: where it is from, when and by
// whom it was saved, and how many saves of the same plan it stands for.
export function orgProvenanceLine(org, now = Date.now()) {
    if (!org) return "";
    const parts = ["in the org's store"];
    if (org.variants > 1) parts.push(`newest of ${org.variants} saves of this plan`);
    if (org.savedAt) parts.push(`saved ${agoText(org.savedAt, now)}`);
    if (org.endpointId) parts.push(`by endpoint ${String(org.endpointId).slice(0, 8)}`);
    return parts.join(" \u00b7 ");
}

function agoText(at, now = Date.now()) {
    const ms = Math.max(0, now - at);
    const m = Math.round(ms / 60000);
    if (m < 2) return "just now";
    if (m < 90) return `${m} min ago`;
    const h = Math.round(ms / 3600000);
    if (h < 36) return `${h} h ago`;
    return `${Math.round(ms / 86400000)} d ago`;
}

// The /recipes command lists every recipe as stored, archived ones
// included, with no network call: the store is reconciled with the live
// schema when a run starts, and a recipe whose entity is gone fails in the
// executor and retires through the failure streak either way.
// `/recipes refresh` reads the org's saves now instead of waiting for the
// hourly background sync; the list follows through `notify`, since the read
// takes about twenty seconds.
export function recipesCommand({ args, append, store, notify = null, sync = null, now = Date.now }) {
    const words = String(args || "").trim().split(/\s+/).filter(Boolean);
    if (words[0] === "forget") {
        const target = words[1];
        if (!target) { append({ kind: "system", text: "Usage: `/recipes forget <id>` (ids are listed by `/recipes`) or `/recipes forget all`." }); return { forgot: 0 }; }
        if (target === "all") {
            const orgHeld = store.stats?.().orgListed || 0;
            const n = store.forgetAll();
            const hidden = orgHeld ? ` ${orgHeld} of them ${orgHeld === 1 ? "is" : "are"} the org's and ${orgHeld === 1 ? "is" : "are"} hidden on this browser; recipes the org saves later still arrive.` : "";
            append({ kind: "system", text: `Forgot ${n} recipe${n === 1 ? "" : "s"}.${hidden} New chats start with none of this browser's; a run that queries, expands and ends in a supported claim learns one.` });
            return { forgot: n };
        }
        const ok = store.forget(target);
        append({ kind: "system", text: ok ? `Forgot ${target}.` : `No recipe ${target}; \`/recipes\` lists the ids.` });
        return { forgot: ok ? 1 : 0 };
    }
    if (words[0] === "refresh") {
        if (typeof sync !== "function" || !store.org) { append({ kind: "system", text: "The org's recipes are not available on this surface." }); return { refreshed: false }; }
        append({ kind: "system", text: "Reading the org's saved recipes from the trace store\u2026" });
        const done = (result) => {
            const say = notify || append;
            if (result?.error) say({ kind: "system", text: `The org's recipes could not be read: ${result.error}` });
            else if (result?.skipped) say({ kind: "system", text: `The org's recipes were not read: ${result.skipped}.` });
            else say({ kind: "system", text: `Read ${result?.saves ?? 0} save${result?.saves === 1 ? "" : "s"} from the org's traces: ${result?.added ?? 0} new recipe${result?.added === 1 ? "" : "s"}, ${result?.updated ?? 0} updated.${result?.back?.error ? ` The week further back could not be read: ${result.back.error}.` : ""}` });
            listRecipesForChat({ append: say, store, now });
        };
        return { refreshed: true, pending: Promise.resolve().then(() => sync(store)).then(done, (err) => done({ error: String(err?.message || err) })) };
    }
    return listRecipesForChat({ append, store, now });
}

export function listRecipesForChat({ append, store, now = Date.now }) {
    const at = now();
    const recipes = recipeRowsForList(store.list({ includeArchived: true }));
    const stats = store.stats();
    const archived = stats.archived ? `, ${stats.archived} archived` : "";
    const last = typeof store.lastSettle === "function" ? store.lastSettle() : null;
    // The header line truncates in the rule row, so the outcome of the last
    // run, the part worth reading, goes on the wrapping detail line.
    const lastLine = last
        ? (last.learned ? `Last run ${last.merged ? "merged into" : "learned"} "${last.learned}".` : `Last run learned nothing: ${last.reason || "no reason recorded"}.`)
        : "";
    const split = stats.org ? ` (${stats.localActive ?? stats.active} learned here, ${stats.orgActive ?? 0} from the org)` : "";
    const org = stats.org
        ? (stats.org.lastSyncAt
            ? `The org's recipes were read from the trace store ${agoText(stats.org.lastSyncAt, at)}${stats.org.coveredDays ? `, covering the last ${stats.org.coveredDays} day${stats.org.coveredDays === 1 ? "" : "s"}` : ""}${stats.org.count > stats.org.plans ? ` (${stats.org.count} saves of ${stats.org.plans} plan${stats.org.plans === 1 ? "" : "s"})` : ""}${stats.org.lastError ? `; the last read failed: ${stats.org.lastError}` : ""}; \`/recipes refresh\` reads them now.`
            : (stats.org.lastError ? `The org's recipes could not be read yet: ${stats.org.lastError}. \`/recipes refresh\` tries again.` : "The org's recipes are being read from the trace store; `/recipes refresh` reads them now."))
        : "";
    append({
        kind: "system",
        text: `Recipes the harness runs end to end: ${stats.active} active${split}${archived}`,
        detail: [lastLine, org, "Confidence decays weekly unless a recipe keeps delivering; three failed runs in a row retire one."].filter(Boolean).join(" "),
        recipes,
        expanded: true,
    });
    return { listed: recipes.length, last };
}

const SLASH_COMMANDS = [
{
        name: "/prompts",
        description: "List your saved prompts (slash commands)",
        run: ({ append }) => {
            const tenantId = getCurrentTenantIdSync();
            if (!tenantId) {
                append({ kind: "system", text: "Saved prompts are locked until your tenant is verified." });
                return;
            }
            const list = listPrompts(tenantId);
            if (!list.length) {
                append({
                    kind: "system",
                    text: "No saved prompts. Add one with `/save <command> <body>`, then run it with `/<command>`.",
                });
                return;
            }
            const lines = list.map((p) => `  /${p.command}  ·  ${p.title}`);
            append({
                kind: "system",
                text: `Saved prompts (${list.length}):\n${lines.join("\n")}`,
            });
        },
    },
{
        name: "/save",
        description: "Save a prompt: /save <command> <body…>",
        run: ({ args, append }) => {
            const trimmed = (args || "").trim();
            if (!trimmed) {
                append({
                    kind: "system",
                    text: "Usage: `/save <command> <body>`. Body may span multiple lines.",
                });
                return;
            }
            const space = trimmed.search(/\s/);
            if (space < 0) {
                append({
                    kind: "system",
                    text: "Body required. Usage: `/save <command> <body>`.",
                });
                return;
            }
            const rawCmd = trimmed.slice(0, space);
            const body = trimmed.slice(space + 1).trim();
            try {
                const saved = savePrompt(getCurrentTenantIdSync(), { command: rawCmd, body });
                append({
                    kind: "system",
                    text: `Saved /${saved.command} (${saved.body.length} chars). Invoke with /${saved.command}.`,
                });
            } catch (err) {
                append({
                    kind: "system",
                    text: `Couldn't save: ${err.message || err}`,
                });
            }
        },
    },
{
        name: "/delete",
        description: "Delete a saved prompt: /delete <command>",
        run: ({ args, append }) => {
            const cmd = sanitizeCommand((args || "").trim());
            if (!cmd) {
                append({ kind: "system", text: "Usage: `/delete <command>`." });
                return;
            }
            try {
                const tenantId = getCurrentTenantIdSync();
                const existed = !!findPromptByCommand(tenantId, cmd);
                deletePrompt(tenantId, cmd);
                append({
                    kind: "system",
                    text: existed ? `Deleted /${cmd}.` : `No such prompt: /${cmd}.`,
                });
            } catch (err) {
                append({ kind: "system", text: `Couldn't delete: ${err.message || err}` });
            }
        },
    },
{
        name: "/audit",
        description: "Review a chat with an audit sub-agent and propose bugs or improvements. Usage: /audit [chat-id] (default: current chat)",
        run: ({ args, chat }) => {
            const target = (args || "").trim();
            const targetId = target || (chat?.id != null ? String(chat.id) : "");
            if (!targetId) {
                return; // no current chat (shouldn't happen)
            }
            return { pending: { kind: "audit", targetChatId: targetId } };
        },
    },
{
        name: "/export-audits",
        description: "Download the audit findings from this window's chats. Usage: /export-audits [this] [json|md] (default: all chats, markdown)",
        run: ({ chat, getAllChats, collectChats, notify, args }) => {
            const tokens = (args || "")
                .trim()
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);
            const scopeThis = tokens.includes("this");
            const format = tokens.includes("json") ? "json" : "md";
            // Everything below runs async because audits live in chat
            // `items`, and chats restored from `chatMeta` but never opened
            // this session carry empty `items` until their body is loaded.
            // We must thicken BEFORE collecting/counting — otherwise audits
            // sitting on disk in an un-opened chat are silently dropped
            // (and the count reads 0 → "nothing to export"). The result
            // message therefore also has to be async (via `notify`, since
            // the sync `append` is spread into the chat before this
            // resolves). See O-1746 review.
            (async () => {
                try {
                    const { collectAudits, downloadAudits } = await import("../lib/exportAudits.js");
                    // Full transcripts of every open chat (lazy bodies
                    // loaded). Doubles as the referencePool so cross-chat
                    // `target_chat_id` references resolve even for chats
                    // the user never opened.
                    const allChats = typeof collectChats === "function"
                        ? await collectChats()
                        : (typeof getAllChats === "function" ? getAllChats() : [chat].filter(Boolean));
                    const chats = scopeThis ? [chat].filter(Boolean) : allChats;
                    const audits = collectAudits(chats);
                    if (!audits.length) {
                        notify?.({
                            kind: "system",
                            text: "No audit results to export. Run /audit first, then run /export-audits again.",
                        });
                        return;
                    }
                    downloadAudits(chats, { format, referencePool: allChats });
                    notify?.({
                        kind: "system",
                        text: `Exporting ${audits.length} audit${audits.length === 1 ? "" : "s"} as ${format === "json" ? "JSON" : "Markdown"}… (check your Downloads folder)`,
                    });
                } catch (err) {
                    console.error("[export-audits] failed:", err);
                }
            })();
        },
    },
{
        name: "/export-chats",
        description: "Download a JSON dump of every open chat in this window (transcripts + tool details + sub-agent traces)",
        run: ({ append, chat, getAllChats, collectChats, args }) => {
            // Default scope is all open chats; `/export-chats this`
            // narrows to just the active one, which is the common
            // stress-testing case ("dump just this loop").
            const scope = (args || "").trim().toLowerCase();
            const isThis = scope === "this";
            // Count synchronously for an accurate confirmation message
            // before the (async) body load + download resolve.
            const initial = isThis
                ? [chat].filter(Boolean)
                : (typeof getAllChats === "function" ? getAllChats() : [chat].filter(Boolean));
            const count = Array.isArray(initial) ? initial.length : 0;
            if (!count) {
                append({ kind: "system", text: "No open chats to export." });
                return;
            }
            // Fire-and-forget: kick off the lazy import + download
            // outside the sync slash handler. Any failure surfaces as
            // a separate system message; the immediate confirmation
            // tells the user the export started.
            (async () => {
                try {
                    // The active chat ("this") already has its body in
                    // memory; the all-chats scope must thicken any chats
                    // whose transcript was never loaded this session.
                    const chats = isThis
                        ? initial
                        : (typeof collectChats === "function" ? await collectChats() : initial);
                    const { downloadChatsBundle } = await import("../lib/exportChatsBundle.js");
                    downloadChatsBundle(chats);
                } catch (err) {
                    console.error("[export-chats] failed:", err);
                }
            })();
            append({
                kind: "system",
                text: `Exporting ${count} chat${count === 1 ? "" : "s"} to a JSON file… (check your Downloads folder)`,
            });
        },
    },
{
        name: "/context",
        description: "Preview the messages the LLM will see on the next turn",
        run: ({ agent, append }) => {
            const raw = agent.state.messages || [];
            const transformed = convertToLlm(compactMessages(raw, compactionOptionsFor(agent)));
            const tokens = estimateTokens(transformed);
            append({
                kind: "system",
                text: `Context preview · ~${tokens.toLocaleString()} tokens`,
                snapshot: transformed,
            });
        },
    },
{
        name: "/compact",
        description: "Compact older messages to free up context",
        run: ({ agent, append }) => {
            const before = agent.state.messages || [];
            const beforeTokens = estimateTokens(before);
            const compacted = compactMessages(before, { ...compactionOptionsFor(agent), force: true });
            const afterTokens = estimateTokens(compacted);
            setTranscript(agent, compacted);
            append({
                kind: "system",
                text: `Compacted context · ~${beforeTokens.toLocaleString()} → ${afterTokens.toLocaleString()} tokens`,
                snapshot: compacted,
            });
        },
    },
{
        name: "/lessons",
        description: "List what the harness has learned about the analytics API",
        run: ({ append }) => {
            const entries = harnessMemory.list();
            const stats = harnessMemory.stats();
            if (!entries.length) {
                append({
                    kind: "system",
                    text: "No lessons yet. The harness records one when a query fails and a later query on the same entity succeeds, or when you run `/remember <rule>`.",
                });
                return;
            }
            const lessons = entries.map((e) => {
                const tags = [];
                if (e.kind === "pitfall") tags.push("pitfall, no fix yet");
                if (e.source === "user") tags.push("yours");
                if (e.source === "agent") tags.push("agent");
                if (e.contradictions) tags.push(`${e.contradictions} contradiction${e.contradictions === 1 ? "" : "s"}`);
                if (e.schemaHash && stats.schemaHash && e.schemaHash !== stats.schemaHash) tags.push("older schema");
                return {
                    id: e.id,
                    confidence: harnessMemory.effectiveConfidence(e).toFixed(2),
                    scope: e.scope?.entity ? e.scope.entity.replace("ENTITY_TYPE_", "") : "any entity",
                    errorClass: e.scope?.errorClass || null,
                    tags: tags.join(" \u00b7 "),
                    rule: e.rule,
                    fix: e.example?.diff?.length ? describeDiff(e.example.diff) : null,
                    confirmations: e.confirmations || 0,
                    lastConfirmedAt: e.lastConfirmedAt || null,
                };
            });
            const stale = stats.stale ? `, ${stats.stale} learned against an older schema` : "";
            append({
                kind: "system",
                text: `Lessons: ${stats.active} active, ${stats.archived} archived${stale}. Confidence decays weekly unless a lesson keeps proving useful. /forget <id> drops one, /forget all drops everything, /remember <rule> adds one.`,
                lessons,
                expanded: true,
            });
        },
    },
{
        name: "/recipes",
        description: "List the recipes the harness can run end to end (run_recipe); /recipes forget <id>|all drops them",
        run: ({ args, append, notify }) => recipesCommand({ args, append, notify, store: createRecipeStore(), sync: (store) => syncOrgRecipes({ cache: store.org, force: true, browserId: store.browserId?.() || null }) }),
    },
{
        name: "/remember",
        description: "Teach the harness a rule about the analytics API: /remember <rule>",
        run: ({ args, append }) => {
            const rule = (args || "").trim();
            if (!rule) {
                append({ kind: "system", text: "Usage: `/remember <one-sentence rule about how the analytics API must be called>`." });
                return;
            }
            const entry = harnessMemory.remember({ rule, source: "user" });
            if (!entry) {
                append({ kind: "system", text: "Nothing stored: lessons need a verified tenant and user identity." });
                return;
            }
            append({ kind: "system", text: `Stored ${entry.id} (confidence ${entry.confidence.toFixed(2)}). New chats will see it; this chat picks it up on its next run.` });
        },
    },
{
        name: "/forget",
        description: "Forget a lesson: /forget <id> or /forget all",
        run: ({ args, append }) => {
            const target = (args || "").trim();
            if (!target) {
                append({ kind: "system", text: "Usage: `/forget <id>` (ids are listed by `/lessons`) or `/forget all`." });
                return;
            }
            const removed = harnessMemory.forget(target);
            append({
                kind: "system",
                text: removed
                    ? `Forgot ${removed === 1 && target !== "all" ? target : `${removed} lesson${removed === 1 ? "" : "s"}`}.`
                    : `No lesson with id ${target}.`,
            });
        },
    },
{
        name: "/cost",
        description: "Show the total LLM cost for this session",
        run: ({ agent, append }) => {
            const msgs = agent.state.messages || [];
            const totals = {
                input: 0, output: 0, cacheRead: 0, cacheWrite: 0, totalTokens: 0,
                cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, total: 0 },
            };
            let turns = 0;
            for (const m of msgs) {
                if (m.role !== "assistant" || !m.usage) continue;
                turns += 1;
                totals.input += m.usage.input || 0;
                totals.output += m.usage.output || 0;
                totals.cacheRead += m.usage.cacheRead || 0;
                totals.cacheWrite += m.usage.cacheWrite || 0;
                totals.totalTokens += m.usage.totalTokens || 0;
                const c = m.usage.cost || {};
                totals.cost.input += c.input || 0;
                totals.cost.output += c.output || 0;
                totals.cost.cacheRead += c.cacheRead || 0;
                totals.cost.cacheWrite += c.cacheWrite || 0;
                totals.cost.total += c.total || 0;
            }
            append({
                kind: "system",
                text: `Session cost · ${formatUsd(totals.cost.total)} over ${turns} ${turns === 1 ? "turn" : "turns"}`,
                cost: totals,
            });
        },
    }
];

function formatUsd(n) {
    if (!Number.isFinite(n) || n === 0) return "$0.00";
    if (n < 0.01) return `$${n.toFixed(4)}`;
    return `$${n.toFixed(2)}`;
}

// Merge built-in commands with the user's saved prompts so both flow
// through the same lookup + autocomplete path. User prompts get a
// `userPrompt` reference attached; the submit handler uses that
// marker to expand their body into an LLM turn instead of running a
// local action.
export function allSlashCommands() {
    const tenantId = getCurrentTenantIdSync();
    const userEntries = listPrompts(tenantId).map((p) => ({
        name: `/${p.command}`,
        description: p.title || (p.body.length > 60 ? `${p.body.slice(0, 60)}…` : p.body),
        userPrompt: p,
    }));
    return [...SLASH_COMMANDS, ...userEntries];
}

// Parse `cmd args…` from the trimmed input and resolve the command.
// Args is the trailing free-form string (may be empty). Returns null
// if the input isn't a recognised slash command.
function findSlashCommand(text) {
    const match = /^(\/[a-z0-9][a-z0-9-_]*)(?:\s+([\s\S]*))?$/i.exec(text);
    if (!match) return null;
    const cmdName = match[1].toLowerCase();
    const command = allSlashCommands().find((c) => c.name.toLowerCase() === cmdName);
    if (!command) return null;
    return { command, args: match[2] || "" };
}

// Returns the list of commands whose name starts with the typed
// token, or null if the input isn't currently a slash-command query.
// We only show the picker when the input is a single `/foo` token —
// once the user types a space, they've moved on to arguments and
// the picker would be in the way.
function matchSlashCommands(text) {
    if (!text || text[0] !== "/") return null;
    if (/\s/.test(text)) return null;
    const lower = text.toLowerCase();
    return allSlashCommands().filter((c) => c.name.toLowerCase().startsWith(lower));
}

// Parse the user's current dashboard state out of the URL — the
// range and the active scope path. The structured shape is reused
// twice: as a visible "auto-context" chip in the chat UI, and as
// the string we inject into the LLM transcript before each prompt
// via `stringifyDashboardContext`.
// A chat's run carries the workspace's tools only while a workspace is open
// as it starts (they act on that one for the whole run), beside whatever
// tools a triage kit hands the chat; a chat with none open is offered none of
// them. Each chat's are its own, so the notes they pin say which chat left
// them (`chat`). After them, the tools of what the chat is about
// (`subject`, lib/chatSubject.js).
function withWorkspaceTools(options, chat, subject = null) {
    const o = options || {};
    return { ...o, toolsWhile: [...(o.toolsWhile || []), workspaceToolsWhile({ chat }), ...(subject ? [subject] : [])] };
}

// What a chat's agent is built with beyond its own: the kit another surface
// handed it (`host`, the triage chat host), the workspace's tools, and the
// tools of what the chat is about: the things its messages added, with what
// the message being sent adds (`sent`, by chat, kept as it is sent, before
// the transcript has it), and the traces on the workspace open as a run
// starts.
function chatToolOptions(id, { host, chatsRef, sent }) {
    const chatOf = () => (chatsRef.current || []).find((c) => c.id === id);
    const source = () => chatNoteSource(chatOf(), id);
    const options = host.optionsFor(id) || {};
    const equipped = { ...options,
        toolsWhile: [...(options.toolsWhile || []), workspacePolicyToolsWhile({ chatId: id })],
        systemPromptEachRun: () => [options.systemPromptEachRun?.(), workspacePolicyContext()].filter(Boolean).join("\n\n"),
    };
    return withWorkspaceTools(equipped, source, subjectToolsWhile({
        chatId: String(id),
        items: () => subjectItems(chatOf()?.items, sent.get(id)),
        chat: source,
    }));
}

// A chat's transcript with the things the message being sent adds, as what
// the chat is about is read from it.
const subjectItems = (items, sent) => (sent?.length ? [...(items || []), { kind: "user", added: sent }] : items || []);

function buildDashboardContext(searchParams) {
    const tabsParam = searchParams.get("tabs");
    const scopeParam = searchParams.get("scope");
    const activeIdx = Number(searchParams.get("active") || 0);
    const range = searchParams.get("range") || DEFAULT_RANGE;

    let scopePath = [];
    if (tabsParam) {
        const list = tabsParam.split(";");
        const enc = list[Math.min(Math.max(0, activeIdx), list.length - 1)];
        if (enc) {
            try { scopePath = scopePathFor(decodeTab(enc)) || []; } catch { scopePath = []; }
        }
    } else if (scopeParam) {
        try { scopePath = decodeScopePath(scopeParam) || []; } catch { scopePath = []; }
    }

    const scope = scopePath.map((step) => ({
        dim: step.dim,
        dimLabel: DIMENSIONS[step.dim]?.label || step.dim,
        id: step.id,
        label: scopeStepLabel(step) || step.id,
    }));

    const workspace = describeOpenCanvas();
    return { range, scope, workspace };
}

// Stringify the structured dashboard-context for the LLM. Returns
// null when there's nothing useful to convey — the agent does fine
// without an empty stub line, and piChat.convertToLlm skips
// notification entries with no text.
function stringifyDashboardContext(ctx) {
    if (!ctx) return null;
    const parts = [`time range: ${ctx.range}`];
    if (ctx.scope.length) {
        parts.push(`scope: ${ctx.scope.map((s) => `${s.dimLabel}=${s.label}`).join(" / ")}`);
    } else {
        parts.push("scope: overview (no filters)");
    }
    if (ctx.workspace) parts.push(ctx.workspace);
    return parts.join("; ");
}

// Title shown on a kind:"scout" (tenant-profile) chat. Single source of
// truth so the cold-load landing logic can recognise a scout tab from
// its lightweight metadata even before its body (and thus `kind`) loads.
const SCOUT_TITLE = "Tenant profile";

function makeChat(id) {
    return {
        id,
        // Server chat UUID (chat persistence). Minted eagerly at creation when
        // the flag is on so it's stable from the first LLM call (proxy audit
        // attribution) and the first sync. Null when persistence is off —
        // the credential + sync layers both no-op on a null id, so behavior is
        // bit-for-bit today's.
        serverChatId: chatPersistenceEnabled() ? mintServerChatId() : null,
        // Last-activity time: creation here, bumped on send, seeded from the
        // persisted savedAt on restore. Surfaced in the dashboard chat list.
        updatedAt: Date.now(),
        // null for a normal chat; "scout" for a tenant-profiling run,
        // which renders as a profile view instead of a raw transcript.
        kind: null,
        title: "New chat",
        items: [],
        input: "",
        busy: false,
        error: null,
        // A brand-new chat's transcript lives entirely in memory, so it
        // counts as fully loaded. Chats restored from the lightweight
        // `chatMeta` index start `bodyLoaded: false` and flip true once
        // their transcript is read from IndexedDB on first activation
        // (loadChatBodyIntoState). Transient/UI-only — never persisted.
        bodyLoaded: true,
        // Mirror of "this chat has any transcript items", carried in the
        // tab metadata so the tab strip can reason about content before
        // the body is loaded. Once the body loads, `items.length` is
        // authoritative; this is just the pre-load hint.
        hasItems: false,
        toggled: {},
        // Per-chat reasoning budget forwarded to the Pi agent. null =
        // not yet chosen → the model's default depth applies (see
        // defaultThinkingLevelFor); an explicit value, including "off",
        // sticks once the user picks one.
        thinkingLevel: null,
        // Images pasted/dropped into the input — sent with the next
        // prompt and then cleared. Each entry is the Pi ImageContent
        // shape: { type: "image", data, mimeType } plus a UI-only id.
        staged: [],
        // A notification another surface staged to ride ahead of this
        // chat's next prompt, once: the triage case an investigation
        // opened on. Transient like `staged`: a reload before the first
        // question drops it, and the note that announced it stays.
        pendingContext: null,
        // Work another surface staged in this chat for the person to steer
        // and start (an investigation; see lib/triageChatHost.js). Transient
        // too: a reload before it starts drops it.
        steering: null,
        // When true, drop the dashboard scope from the auto-context
        // injected ahead of each prompt. Editable only before the
        // first prompt is sent; after that the chat locks into one
        // of the modes below.
        skipScope: false,
        // Snapshot of how the chat treats scope, set on the first
        // prompt and immutable thereafter:
        //   null      pre-first-prompt; user can still toggle
        //   "include" first prompt went out with scope — keep
        //             auto-injecting it (chip stays, no × button)
        //   "exclude" first prompt was sent with scope skipped —
        //             never inject scope again, chip is hidden
        scopeMode: null,
    };
}

// Per-kind defaults when the user hasn't explicitly toggled a node.
// A chat with nothing folded or opened yet: one object, so the transcript's
// memoized items are handed the same one on every render.
const NO_TOGGLES = Object.freeze({});

// Turns, thinking, and tools start expanded so the interesting parts
// (results, chips, tables) are visible without a click. Args start
// collapsed — they're useful for debugging but rarely what the user
// is actually reading. Compaction banners start collapsed (hide the
// older history under a single line) so long chats stay scrollable.
const DEFAULT_EXPANDED = {
    turn: true,
    // A finished turn that only did work (no closing answer) folds to its
    // one-line summary; the run's answer is what reads.
    turnWork: false,
    thinking: false,
    // A call the agent made for itself folds to its line; one whose output
    // is for the reader (a chart, a save card, a navigation) stays open.
    tool: false,
    toolOpen: true,
    args: false,
    compaction: false,
};

// Once a chat's transcript grows past this many top-level items
// (user prompts + agent turns), fold the older prefix behind a
// "compacted N earlier exchanges" banner. Independent from the
// LLM-side compaction in piChat.js — they cover different concerns:
// UI keeps the scroll surface short; LLM keeps the context window
// bounded.
const UI_COMPACT_TRIGGER = 16;
const UI_COMPACT_KEEP_TAIL = 12;

// Stall watchdog threshold: an active agent run is flagged as
// "stalled" if no pi-agent-core event arrives for this long.
// Generous enough to clear Pyodide first-load + pandas import + a
// reasoning pause without false-positiving; tight enough that a
// Vertex AI mid-stream stall (the most common upstream failure
// mode) surfaces within 1.5 minutes instead of the user staring
// at a wall of keepalives indefinitely.
const STALL_THRESHOLD_MS = 90_000;

// Which id the console keys a chat's knowledge graph by: the persistent
// chat id once the chat has one, else the local id. Must match what the
// agent's attachLiveGraph resolves to.
export function graphChatIdFor(chat, localId) {
    return chat?.serverChatId ?? localId ?? null;
}

// The key a chat's graph goes by; with its title, what a note the chat
// leaves on a trace says it came from.
const chatGraphKey = (chat, localId) => graphKeyFor(graphChatIdFor(chat, localId));
const chatNoteSource = (chat, localId) => ({ key: chatGraphKey(chat, localId), title: chat?.title || "" });

// The open chat whose knowledge graph a trace view names by its key.
export function chatForGraphKey(chats, graphKey) {
    if (graphKey == null || graphKey === "") return null;
    return (chats || []).find((c) => String(graphChatIdFor(c, c.id)) === String(graphKey)) || null;
}

// What to do next with a reply waiting for its chat: nothing to send it to;
// build the chat's agent in the background; wait for that build, or for the
// run the chat is in to end; give up on a build that left no agent; or send.
export function replyNextStep(chat, { agentReady = false, building = false, buildTried = false } = {}) {
    if (!chat) return "missing";
    if (!agentReady) {
        if (building) return "starting";
        return buildTried ? "failed" : "build";
    }
    return chat.busy ? "busy" : "send";
}

// How long a reply waits for its chat's agent to be built before it is left in
// that chat's composer.
export const REPLY_START_MS = 60_000;

// How a reply is submitted: to its own chat, whichever is on screen, never at
// the cost of the composer's draft, tagged so its card can follow it, and as a
// follow-up should the chat have started a run since the reply was queued.
export function replySubmitOptions(chat, reply) {
    return {
        explicitPrompt: reply.text,
        mode: "followup",
        keepDraft: true,
        chatId: chat.id,
        graphReply: { id: reply.id, claimIds: reply.claimIds || [], at: reply.at ?? null, words: reply.words || reply.text },
        ...(reply.added?.length ? { added: reply.added } : {}),
    };
}

// One pass over the replies waiting for their chats, in the order they were
// typed and one per chat at a time: the replies still waiting, and what to do
// now -- publish a reply's state, build its chat's agent in the background,
// send it, or leave it in its chat's composer (no chat, a build that left no
// agent, or one that took too long).
export function planReplyQueue(queue, { chatOf, agentReady, building, now = Date.now(), startMs = REPLY_START_MS }) {
    const held = new Set();
    const keep = [];
    const actions = [];
    for (const r of queue || []) {
        if (held.has(r.chatId)) {
            keep.push(r);
            continue;
        }
        const chat = chatOf(r.chatId) || null;
        const next = replyNextStep(chat, { agentReady: agentReady(r.chatId), building: building(r.chatId), buildTried: r.buildTried });
        if (next === "missing") {
            actions.push({ type: "missing", reply: r });
            continue;
        }
        if (next === "failed" || (next === "starting" && now - r.at > startMs)) {
            actions.push({ type: "leave", reply: r, error: chat?.error || null });
            continue;
        }
        held.add(r.chatId);
        if (next === "send") {
            actions.push({ type: "send", reply: r, chat });
            continue;
        }
        if (next === "build") actions.push({ type: "build", reply: r });
        actions.push({ type: "wait", reply: r, activity: next === "busy" ? "busy" : "starting" });
        keep.push(next === "build" ? { ...r, buildTried: true } : r);
    }
    return { keep, actions };
}

// One run per key at a time: a caller arriving while a run is in flight gets
// that run, so every caller waits on the same work. `start` is called
// synchronously, so whatever it claims up front is claimed before this returns.
export function sharedRun(inflight, key, start) {
    const cur = inflight.get(key);
    if (cur) return cur;
    const run = Promise.resolve(start()).finally(() => {
        if (inflight.get(key) === run) inflight.delete(key);
    });
    inflight.set(key, run);
    return run;
}

// The chats a read across every transcript has to wait for: those whose body
// is not in state yet, and those whose load another caller already started --
// that one is no longer marked as needed, and skipping it would read a chat
// that has not landed.
export function bodiesToAwait(chats, needsBodyLoad, inflight) {
    return (chats || []).map((c) => c.id).filter((id) => needsBodyLoad?.has(id) || inflight?.has(id));
}

// The chats whose history a card is waiting on: each watched graph's chat
// whose body has not been read into state yet.
export function watchedBodyLoads(keys, chats, needsBodyLoad) {
    const out = [];
    for (const key of keys || []) {
        const chat = chatForGraphKey(chats, key);
        if (chat && needsBodyLoad?.has(chat.id) && !out.includes(chat.id)) out.push(chat.id);
    }
    return out;
}

// The composer once a message has gone: emptied, unless the message came from
// somewhere else and the composer holds a draft of its own.
/**
 * What the composer sends with what the reader added from the workspace
 * (lib/chatContextStore.js): the words (a question about what was added,
 * where none were typed), the notification that rides ahead of them, and the
 * one message a send queued behind a run makes of the two.
 */
export function addedToSend(prompt, added) {
    const words = prompt || (added?.length ? ADDED_ASK : "");
    const about = added?.length ? trayPrompt(added) : "";
    return { words, about, queued: about ? `${about}\n\n${words}` : words };
}

export function composerAfterSend(chat, keepDraft) {
    return keepDraft ? { input: chat.input, staged: chat.staged } : { input: "", staged: [] };
}

export function isNodeExpanded(kind, id, toggled) {
    if (toggled && Object.prototype.hasOwnProperty.call(toggled, id)) {
        return !!toggled[id];
    }
    return !!DEFAULT_EXPANDED[kind];
}

export default function ChatConsole({ onOpenSettings, onOpenPage = null, pageVisible = true, wanted = true } = {}) {
    const caps = useMyPermissions();
    const runConsentBroker = useRunConsentBroker();
    const [open, setOpen] = useState(false);
    const [engaged, setEngaged] = useState(false);
    useEffect(() => {
        if (open || pageVisible || wanted) setEngaged(true);
    }, [open, pageVisible, wanted]);
    const openPageRef = useRef(onOpenPage);
    openPageRef.current = onOpenPage;
    // Prototype dock: when the Explore right column flips to "Chat" it
    // publishes a body-host node via dockStore. When present, we render
    // this (same) chat instance into that node instead of the centered
    // overlay scrim — and WITHOUT our own tab strip, because the chat's
    // session tabs are rendered on the column's rail (ScopedPage) from
    // the chatBar model we publish below. We subscribe to the host only
    // (not the whole store) so our own chatBar publish can't loop us.
    const dockHost = useSyncExternalStore(subscribeDock, getDockBodyHost, () => null);
    // `dockAvailable` is false unless Explore is the VISIBLE surface — its
    // ScopedPage (and thus the dockHost) stays mounted but hidden under the
    // Studio/Settings overlay. Gating `docked` on it means that off Explore
    // we fall back to the floating overlay instead of portaling the chat
    // into the hidden Explore layer (where ⌘K appeared to do nothing).
    const dockAvailable = useSyncExternalStore(subscribeDock, getDockAvailable, () => false);
    const docked = pageVisible && !!dockHost && dockAvailable;
    // Docked in a framed pane (a workspace's dock): the pane's frame is the
    // chat's outline, so its chrome lies against the frame in cells divided
    // by hairlines, as the pane's own tab row does, rather than in boxes a
    // few pixels inside it (which read as stacked borders).
    const framed = docked && !!dockHost?.hasAttribute?.("data-dock-framed");
    const dockedRef = useRef(docked);
    const analytics = useAnalyticsClient();
    const [expanded, setExpanded] = useState(false);
    // User-set size from resize handles. When null, the dialog uses
    // its default compact dimensions. The dialog is always centered
    // by the scrim's grid layout — there is no position state.
    const [size, setSize] = useState(null);
    const dialogRef = useRef(null);
    // Caller-captured rect used as the FLIP source. Set synchronously
    // by toggleExpanded before the className flip so getBoundingClientRect
    // reflects the pre-toggle layout.
    const flipFromRef = useRef(null);
    const flipTokenRef = useRef(0);
    // Tracks an active resize gesture.
    const resizeRef = useRef(null);
    // Mirrors `size` so the FLIP cleanup (whose closure captures the
    // value at the time the effect ran) can restore the latest size
    // even if it changed during the animation.
    const sizeRef = useRef(size);
    useEffect(() => { sizeRef.current = size; }, [size]);

    const [flipInProgress, setFlipInProgress] = useState(false);

    const toggleExpanded = useCallback(() => {
        if (dialogRef.current) {
            flipFromRef.current = dialogRef.current.getBoundingClientRect();
        }
        setFlipInProgress(true);
        setExpanded((v) => !v);
    }, []);

    // Tracks which handle the mouse is currently over and which (if
    // any) is being dragged. Both feed the edge-indicator divs that
    // render directly on top of the dialog border. We keep them as
    // React state (not a ref) because the indicators rerender from
    // them; the active state survives mouseleave during a drag so
    // the user gets continuous feedback while resizing.
    const [hoveredHandle, setHoveredHandle] = useState(null);
    const [activeHandle, setActiveHandle] = useState(null);

    const beginResize = useCallback((dir) => (e) => {
        if (expanded) return;
        if (!dialogRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = dialogRef.current.getBoundingClientRect();
        resizeRef.current = {
            dir,
            startX: e.clientX,
            startY: e.clientY,
            startWidth: rect.width,
            startHeight: rect.height,
        };
        setActiveHandle(dir);
        document.body.style.userSelect = "none";
    }, [expanded]);

    useEffect(() => {
        const MIN_W = 480;
        const MIN_H = 260;
        const onMove = (e) => {
            const s = resizeRef.current;
            if (!s) return;
            const dx = e.clientX - s.startX;
            const dy = e.clientY - s.startY;
            const d = s.dir;
            let width = s.startWidth;
            let height = s.startHeight;
            // Symmetric growth around the center — every pixel the
            // cursor moves expands/contracts both sides equally so the
            // dialog stays centered without any position tracking.
            if (d.includes("e")) width = Math.max(MIN_W, Math.min(window.innerWidth, s.startWidth + 2 * dx));
            if (d.includes("w")) width = Math.max(MIN_W, Math.min(window.innerWidth, s.startWidth - 2 * dx));
            if (d.includes("s")) height = Math.max(MIN_H, Math.min(window.innerHeight, s.startHeight + 2 * dy));
            if (d.includes("n")) height = Math.max(MIN_H, Math.min(window.innerHeight, s.startHeight - 2 * dy));
            setSize({ width, height });
        };
        const onUp = () => {
            if (!resizeRef.current) return;
            resizeRef.current = null;
            setActiveHandle(null);
            document.body.style.userSelect = "";
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, []);

    // FLIP between the compact dialog rect and the fullscreen rect.
    // We pin top/left/width/height (not transform) so inner content
    // reflows naturally as the dialog grows — no scale distortion of
    // text or tables, no inverse-scaling tricks.
    useLayoutEffect(() => {
        const dialog = dialogRef.current;
        const from = flipFromRef.current;
        flipFromRef.current = null;
        if (!dialog || !from) return;

        const token = ++flipTokenRef.current;
        const to = dialog.getBoundingClientRect();
        const easing = "cubic-bezier(0.4, 0, 0.2, 1)";
        const duration = 380;

        dialog.style.position = "fixed";
        dialog.style.margin = "0";
        dialog.style.top = `${from.top}px`;
        dialog.style.left = `${from.left}px`;
        dialog.style.width = `${from.width}px`;
        dialog.style.height = `${from.height}px`;
        dialog.style.maxHeight = "none";
        dialog.style.transition = "none";
        dialog.style.zIndex = "10001";
        void dialog.offsetHeight;

        dialog.style.transition = [
            `top ${duration}ms ${easing}`,
            `left ${duration}ms ${easing}`,
            `width ${duration}ms ${easing}`,
            `height ${duration}ms ${easing}`,
        ].join(", ");
        dialog.style.top = `${to.top}px`;
        dialog.style.left = `${to.left}px`;
        dialog.style.width = `${to.width}px`;
        dialog.style.height = `${to.height}px`;

        const settle = () => {
            // After the animation, React's inline style (driven by
            // size) is the source of truth when not expanded. Wipe
            // FLIP styles, then re-apply size so we don't flash back
            // to the default compact dimensions for a paint.
            dialog.style.cssText = "";
            const sz = sizeRef.current;
            if (!expanded && sz) {
                dialog.style.width = `${sz.width}px`;
                dialog.style.height = `${sz.height}px`;
                dialog.style.maxHeight = "none";
            }
            setFlipInProgress(false);
        };
        const onEnd = (e) => {
            if (e.target !== dialog || e.propertyName !== "width") return;
            if (token !== flipTokenRef.current) return;
            dialog.removeEventListener("transitionend", onEnd);
            settle();
        };
        dialog.addEventListener("transitionend", onEnd);
        const timer = setTimeout(() => {
            if (token !== flipTokenRef.current) return;
            dialog.removeEventListener("transitionend", onEnd);
            settle();
        }, duration + 80);
        return () => {
            dialog.removeEventListener("transitionend", onEnd);
            clearTimeout(timer);
        };
    }, [expanded]);

    // Monotonic chat id. Survives state churn so closed-then-recreated
    // tabs never collide with lingering agent entries in agentsRef.
    // Bumped past max(savedIds) by the hydrate effect below so
    // restored chats don't collide with future newChat ids.
    const nextChatIdRef = useRef(2);
    const [chats, setChats] = useState(() => [makeChat(1)]);
    const [activeId, setActiveId] = useState(1);
    const [closedChatIds, setClosedChatIds] = useState(() => new Set());
    useEffect(() => {
        setClosedChatIds((cur) => {
            if (!cur.has(activeId)) return cur;
            const next = new Set(cur);
            next.delete(activeId);
            return next;
        });
    }, [activeId]);

    const pendingHydrationRef = useRef(new Map());
    const needsBodyLoadRef = useRef(new Set());
    // Ids of chats restored from the server that still need their agent
    // messages rebuilt from the conversation store before they can be
    // continued. The transcript renders without this; only SENDING needs it,
    // so the read is deferred to the first send rather than paid on every
    // opened tab (see ensureContinuationContext).
    const needsContinuationRef = useRef(new Set());
    // Active tenant id, resolved once in the hydrate effect from
    // /v1/me. Chat persistence is scoped per tenant (composite IDB
    // keys + a tenant-namespaced active-id LS key) so a browser that
    // has signed into more than one tenant never mixes their chats.
    // Held in a ref because the synchronous save path (flushPendingSaves,
    // fired from unload handlers) reads it without re-running.
    const tenantIdRef = useRef(null);
    const [verifiedTenantId, setVerifiedTenantId] = useState(null);
    const [hydrated, setHydrated] = useState(false);
    const [tenantLoadError, setTenantLoadError] = useState(null);
    const [tenantLoadAttempt, setTenantLoadAttempt] = useState(0);
    const lastSavedJsonRef = useRef(new Map());
    const lastSavedLargeFpRef = useRef(new Map());
    const lastChatRefsRef = useRef(new Map());

    const lastFenceTargetRef = useRef(0);

    // Gate: setHydrated(true) at the end. The agent-mount effect
    // early-returns until then so the default chat doesn't waste a
    // createChatAgent call against an id that's about to be replaced.
    useEffect(() => {
        if (!engaged) return undefined;
        let cancelled = false;
        setTenantLoadError(null);
        setVerifiedTenantId(null);
        (async () => {
            // Resolve the active tenant before touching storage so every
            // read/write is scoped to it. /v1/me is cached after the
            // first hit, so this resolves immediately on warm mounts.
            const me = await getCurrentUser().catch(() => null);
            if (cancelled) return;
            const tenantId = me?.currentTenant?.tenantId ?? null;
            tenantIdRef.current = tenantId;
            // Authenticated persistence has no safe fallback namespace. If
            // `/v1/me` failed or returned no tenant, remain unhydrated and do
            // not touch the shared legacy/default bucket.
            if (!tenantId) {
                setTenantLoadError("Your tenant could not be verified. Chat storage remains locked.");
                return;
            }
            setVerifiedTenantId(tenantId);
            // One-time deletion of any pre-tenant-scoping chats (no-op
            // once done / on fresh installs).
            await purgeLegacyChats();
            if (cancelled) return;

            // Cheap read: just the tab metadata.
            let metas = await loadAllChatMeta(tenantId);
            if (cancelled) return;

            // Migration / self-heal: a pre-v3 user has bodies in `chats`
            // but nothing projected into `chatMeta` yet. Pay the old
            // full read ONCE, project metadata so every later load is
            // cheap, then proceed on the lightweight path. backfillChatMeta
            // is atomic, so if it fails we simply take this branch again
            // next mount — never a half-indexed (chats-vanish) list.
            if (metas.length === 0) {
                const records = (await loadAllChats(tenantId)) || [];
                if (cancelled) return;
                const validRecords = records.filter(isLoadableChatRecord);
                if (validRecords.length > 0) {
                    backfillChatMeta(
                        tenantId,
                        validRecords.map((r) => ({ chatId: r.chatId, payload: r })),
                    ).catch(() => { /* logged in chatStorage; retried next load */ });
                    metas = validRecords.map((r) => ({
                        chatId: r.chatId,
                        schemaVersion: r.schemaVersion,
                        kind: r.kind ?? null,
                        title: r.title,
                        hasItems: Array.isArray(r.items) && r.items.length > 0,
                        busyAtClose: !!r.busyAtClose,
                    }));
                }
            }

            // The next-chat-id counter reads ALL real chat rows — every
            // schemaVersion, never the version-filtered set (counting
            // only what this build can RENDER is how a stale build
            // mints an id that collides with, and saveChat-overwrites,
            // a hidden newer-schema chat). Fence rows are excluded —
            // counting the fence would ratchet it +1 per cold load;
            // see maxChatIdFromMetas in chatIds.js.
            nextChatIdRef.current = Math.max(nextChatIdRef.current, maxChatIdFromMetas(metas) + 1);

            // Arm the fence BEFORE this session's first save can
            // migrate any record to schemaVersion 2 — the fence must
            // already be on disk by the time a v1-only build could
            // observe a store containing v2 chats. Idempotent when a
            // prior session's fence already covers the ceiling.
            if (metas.length > 0) {
                lastFenceTargetRef.current = Math.max(lastFenceTargetRef.current, nextChatIdRef.current);
                ensureIdFence(tenantId, nextChatIdRef.current).catch(() => { /* logged in chatStorage */ });
            }

            // Chat persistence (flag-gated). On first login after ship, backfill
            // pre-existing local chats to PG, pull the server chat list onto this
            // device (fresh-device restore), adopt-latest for conflict-parked
            // chats, then drain the outbox in the background. Both backfill and
            // reconcile seed IDB so the lightweight tab-strip path below renders
            // them; reconcile allocates local ids through the same fence-
            // respecting counter. Sequencing + cancellation + jitter live in
            // runChatPersistenceHydration (unit-tested). All best-effort — a
            // failure leaves IDB as the truth.
            const hydration = await runChatPersistenceHydration({
                tenantId,
                allocLocalId: () => nextChatIdRef.current++,
                isCancelled: () => cancelled,
                // reconcile may have advanced the id counter — keep the fence above it.
                afterReconcile: () => {
                    if (nextChatIdRef.current > lastFenceTargetRef.current) {
                        lastFenceTargetRef.current = nextChatIdRef.current;
                        ensureIdFence(tenantId, nextChatIdRef.current).catch(() => {});
                    }
                },
            });
            if (cancelled) return;
            if (hydration.ran) {
                metas = await loadAllChatMeta(tenantId);
                if (cancelled) return;
            }

            const valid = visibleChatMetas(metas, isSupportedSchemaVersion);
            if (valid.length > 0) {
                const lightweightChats = valid.map((m) => {
                    // Mirror hydrateChatFromStorage's id coercion so a
                    // numeric local id round-trips to a number.
                    const idNum = Number(m.chatId);
                    const id = Number.isFinite(idNum) && String(idNum) === String(m.chatId)
                        ? idNum
                        : m.chatId;
                    needsBodyLoadRef.current.add(id);
                    return {
                        ...makeChat(id),
                        // makeChat mints a fresh server id; a RESTORED chat must
                        // keep its persisted one (carried in the meta projection)
                        // so it maps onto the right server row. Null-carrying
                        // metas (pre-persistence) get their real id when the body
                        // loads, or from backfill.
                        serverChatId: m.serverChatId ?? null,
                        // Seed last-activity from the persisted savedAt so the
                        // dashboard chat list shows a real time before any body load.
                        updatedAt: m.savedAt ?? Date.now(),
                        // `kind` now rides along in chatMeta (projectChatMeta)
                        // so a scout/tenant-profile tab is identifiable from
                        // the lightweight index alone — the landing logic
                        // below needs it before any body loads. Older meta
                        // records predate the field; fall back to the scout
                        // title (one save self-heals them).
                        kind: m.kind ?? (m.title === SCOUT_TITLE ? "scout" : null),
                        title: m.title || "New chat",
                        hasItems: !!m.hasItems,
                        bodyLoaded: false,
                    };
                });
                let restored = lightweightChats;
                const scout = lightweightChats.find((c) => c.kind === "scout");
                let nextActive;
                if (scout) {
                    nextActive = scout.id;
                } else {
                    const blank = [...lightweightChats]
                        .reverse()
                        .find((c) => c.kind == null && !c.hasItems);
                    if (blank) {
                        nextActive = blank.id;
                    } else {
                        const fresh = makeChat(nextChatIdRef.current++);
                        restored = [...lightweightChats, fresh];
                        nextActive = fresh.id;
                    }
                }
                setChats(restored);
                setActiveId(nextActive);
            }
            setHydrated(true);
        })();
        return () => { cancelled = true; };
    }, [tenantLoadAttempt, engaged]);

    // The footer reflects the configuration owned by the shared Settings tab.
    // `loading` is true until the first getChatCreds() resolves. It
    // gates the composer (you can't dispatch a prompt before we know
    // the tenant's provider/model/key), distinct from `configured`
    // (loaded, but no key on file → first-run setup). `configured`
    // stays optimistically true during the load so a returning,
    // already-configured user never flashes the setup takeover.
    const [credsSummary, setCredsSummary] = useState({ provider: "", model: "", url: "", configured: true, loading: true });

    // Per-chat agent instances. Created lazily on first need and kept
    // around for the lifetime of the component so closing/reopening
    // the overlay preserves the LLM context — otherwise /compact and
    // continue() have nothing to operate on after a reopen.
    const agentsRef = useRef(new Map());
    const settingsDirtyAgentsRef = useRef(new Set());
    const [settingsEpoch, setSettingsEpoch] = useState(0);
    // agentsRef is a ref (non-reactive), but the composer's enabled
    // state and the thinking-depth pill must reflect "is this chat's
    // agent built yet". Bump this epoch on every agentsRef install /
    // teardown so the render reads below re-evaluate when readiness
    // flips. Sending before the agent exists is what produced the
    // "Agent not ready / no model configured" error the user hit by
    // typing during the initial config + agent build.
    const [agentReadyEpoch, setAgentReadyEpoch] = useState(0);
    // Per-chat unsubscribe handles for the agent's _childHandleRegistry
    // subscription. We listen to the registry so the chat UI keeps
    // reflecting still-running sub-agents' state after their spawn
    // tool returned (interrupted spawns). Keyed by chat id; cleared
    // on chat close / agent reload.
    const childRegistryUnsubsRef = useRef(new Map());
    // Set of chat ids whose agent build is currently in flight. The
    // agent-mount effect skips re-entering a build for an id already
    // here — without this guard, rapid switching between chats (X →
    // Y → X) can spawn two concurrent createChatAgent calls for X,
    // leaving the loser's runtime + child-registry subscription
    // orphaned in memory.
    const agentBuildingRef = useRef(new Set());
    // Per-chat handle to the in-flight run's idempotent teardown, keyed
    // by chat id. Lets the STOP handlers force a run closed even when
    // pi's abort can't unstick a hung upstream/tool promise — without
    // it, runAgentCall's finally never fires and the UI wedges "busy".
    const activeRunsRef = useRef(new Map());
    const turnStopsRef = useRef(createTurnStops());
    // Per-submit capability context. Agents are long-lived, but consent is
    // not: execute_python reads this only while the user-initiated prompt
    // that disclosed the action is actively running.
    const interactiveAgentRunContextsRef = useRef(new Map());
    // Chats the Reviews lens opens here (lib/triageChatHost.js): per chat id
    // it keeps the options every build path folds into that chat's agent
    // constructor (a case's reading kit, an investigation's coordination
    // tools), and runs the turns the lens tells a chat (a coordinator hearing
    // from its investigators). Built once over getters, so the closures below
    // stay current.
    const triageHostDepsRef = useRef({});
    const triageHostRef = useRef(null);
    if (!triageHostRef.current) {
        triageHostRef.current = createTriageChatHost({
            nextChatId: () => nextChatIdRef.current++,
            makeChat,
            addChat: (chat) => setChats((cur) => [...cur, chat]),
            updateChat: (id, fn) => triageHostDepsRef.current.updateChat?.(id, fn),
            findChat: (pred) => (chatsRef.current || []).find(pred) || null,
            ensureAgentFor: (id) => triageHostDepsRef.current.ensureAgentFor?.(id),
            activateChat: (id) => setActiveId(id),
            // Through the deps ref, so a hot-reloaded rule applies without a
            // page reload; the host itself is built once.
            surface: () => triageHostDepsRef.current.surface?.() ?? "overlay",
            showOverlay: () => { if (openPageRef.current) openPageRef.current(); else setOpen(true); },
            // Docked, the console stays open: open is what builds the chat
            // put on screen there (a reloaded chat loads its transcript and
            // gets its agent), as after a navigation.
            hideOverlay: () => { if (!dockedRef.current) setOpen(false); },
            focusComposer: () => triageHostDepsRef.current.focusComposer?.(),
            sendPrompt: (id, prompt, mode) => triageHostDepsRef.current.sendPrompt?.(id, prompt, mode),
            activateDock: () => window.dispatchEvent(new CustomEvent("origin-chat-activate")),
            // A chat Investigate opened (on a case, or to coordinate a
            // scope's investigation), or one suggesting or tuning standards, gets its
            // kit back when it is reloaded.
            restore: (chat) => restoreInvestigateChat(chat) || restoreStandardSuggestionChat(chat) || restoreStandardTuningChat(chat),
            runTurn: (id, agent, label, messages) => triageHostDepsRef.current.runTurn?.(id, agent, label, messages),
            // The chat in front, which takes on a surface's tools rather than
            // a new chat being opened for them, its agent built again with them.
            current: () => {
                const chat = (chatsRef.current || []).find((c) => c.id === activeIdRef.current) || null;
                return chat ? { chatId: chat.id, serverChatId: chat.serverChatId ?? null, busy: !!chat.busy } : null;
            },
            rebuildAgent: (id) => triageHostDepsRef.current.reloadActive?.(id),
        });
    }
    // Forked chats stash the snapshot they want their agent to inherit
    // here; the agent-creation effect reads it on first build.
    // (pendingHydrationRef + hydrated + lastSavedJsonRef live further
    //  up alongside the chats useState so the hydrate effect can see
    //  them at declaration order — TDZ-sensitive.)
    const pendingForkSnapshotRef = useRef(new Map());
    const inputRef = useRef(null);
    const transcriptHostRef = useRef(null);

    // Refs that mirror current chats/activeId so async submit handlers
    // can read the latest values without re-creating the callback.
    const chatsRef = useRef(chats);
    useEffect(() => { chatsRef.current = chats; }, [chats]);
    // The things each chat's messages added, as each is sent: what the chat is
    // about counts them from the moment it is sent, before the transcript the
    // agent reads it from has them (chatToolOptions).
    const sentAddedRef = useRef(new Map());
    const activeIdRef = useRef(activeId);
    useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

    // DevTools-console hook for stress-testing: call
    // `__exportChats()` to download a JSON dump of every open chat
    // (items, tool details, sub-agent transcripts). Returns the
    // bundle so `await __exportChats()` can be piped through
    // copy(JSON.stringify(...)) for one-liners.
    useEffect(() => {
        if (typeof window === "undefined") return;
        window.__exportChats = async ({ download = true } = {}) => {
            const { bundleChats, downloadChatsBundle } = await import("../lib/exportChatsBundle.js");
            // Thicken any lazily-loaded chats so the dump isn't blank for
            // chats that were restored from metadata but never opened.
            const chats = await collectChatsForExport();
            return download
                ? downloadChatsBundle(chats)
                : bundleChats(chats);
        };
        return () => {
            if (window.__exportChats) delete window.__exportChats;
        };
    }, []);

    // Cross-chat registry: lets audit tools (and any future
    // cross-chat agent) reach chat state + per-chat agent instances
    // without prop-drilling. Singleton lives in lib/chatRegistry;
    // we install the closures on mount and clear them on unmount.
    useEffect(() => {
        let cancelled = false;
        import("../lib/chatRegistry.js").then(({ setChatRegistry }) => {
            if (cancelled) return;
            setChatRegistry({
                getChats: () => chatsRef.current,
                getAgents: () => agentsRef.current,
            });
        });
        return () => {
            cancelled = true;
            import("../lib/chatRegistry.js").then(({ setChatRegistry }) => {
                setChatRegistry(null);
            });
        };
    }, []);

    const activeChat = resolveActiveChat(chats, activeId);
    // Tenant-tailored starter cards from the "Tailor these to your data"
    // playbook (set_starter_cards → starterCardsStore). Falls back to the
    // static SAMPLE_QUERIES until a profiling run has produced a set.
    const tailoredStarters = useSyncExternalStore(subscribeStarterCards, getStarterCards, () => null);
    // True while a background tenant-profiling run is in flight (its tab
    // carries kind:"scout"). Drives the trigger's progress label so the
    // user sees progress without leaving their current chat.
    const runningScout = chats.find((c) => c.kind === "scout" && c.busy) || null;
    useEffect(() => {
        if (verifiedTenantId) configureStarterCardsTenant(verifiedTenantId);
    }, [verifiedTenantId]);
    const activeChatId = activeChat?.id;
    useEffect(() => {
        if (activeChatId != null && !activeChat?.busy) presentWorkspacePolicy(activeChatId).catch(() => {});
    }, [activeChatId, activeChat?.busy]);
    // What the reader adds from the workspace goes to the chat shown here
    // when the dock's chat tab is in front (lib/chatContextStore.js).
    useEffect(() => (activeChatId != null ? registerChat(`console:${activeChatId}`) : undefined), [activeChatId]);
    // The graph is keyed by the persistent chat id when there is one, so a
    // reloaded chat finds the graph its agent saved.
    const graphChatKeyId = graphChatIdFor(activeChat, activeChatId);
    useEffect(() => { setActiveGraphChat(graphChatKeyId); }, [graphChatKeyId]);
    // The notes a chat left on traces come back from its transcript once it
    // is loaded, onto the traces they annotate.
    useEffect(() => {
        for (const chat of chats) restoreSessionsFrom(chat.items);
        migrateLegacyPolicyDrafts(chats);
        restoreChatWorkspaceNotes(chats.map((c) => ({ ...chatNoteSource(c, c.id), items: c.items })));
    }, [chats]);
    const updateChat = useCallback((id, updater) => {
        setChats((cur) => cur.map((c) => (c.id === id ? updater(c) : c)));
    }, []);

    // Read + hydrate one chat's persisted transcript body from
    // IndexedDB. Returns the hydrator's { chat, hydration } (chat carries
    // the restored items / toggled / scope / error banner; hydration
    // carries the agent-replay payload) or null when there's nothing
    // valid to restore.
    const readChatBody = useCallback(async (id) => {
        const record = await loadChat(tenantIdRef.current, id);
        if (!isLoadableChatRecord(record)) return null;
        // Seed the save-boundary fingerprint from what is already on disk.
        // `lastSavedJsonRef` starts empty on every page load, so without this the
        // first boundary for each opened chat compares against nothing, reports
        // "changed", and POSTs a head-only no-op that bumps the server revision -
        // once per chat, per load, per tab, for chats nobody touched.
        //
        // Safe in both directions: durability does not rest on this ref (the
        // outbox dirty flag plus the app-open drain do), and the one record shape
        // that deliberately fails to round-trip is an interrupted chat, where
        // hydrate appends a refresh notification - real new history whose sync
        // must still fire. See chatSerializer.test.js.
        lastSavedJsonRef.current.set(id, chatSaveFingerprint(record));
        return hydrateChatFromStorage(record);
    }, []);

    // Lazily fetch a chat's body the first time it's needed and patch the
    // restored transcript into React state, stashing the agent-replay
    // payload for the agent-mount effect to consume (mirrors the eager
    // hydrate that used to run for every chat on mount). Claims the id
    // synchronously from needsBodyLoadRef so concurrent callers (a tab
    // switch racing an export/audit) never double-load.
    const readBodyIntoState = useCallback(async (id) => {
        if (!needsBodyLoadRef.current.has(id)) return;
        needsBodyLoadRef.current.delete(id);
        // Fresh-device restore: a chat seeded from the server list has a
        // serverChatId + an empty local body (needsServerBody). Pull its
        // transcript from PG before hydrating so the tab renders the real
        // conversation rather than an empty placeholder. On failure we re-arm
        // the load and bail rather than caching the empty stub as final (see
        // restoreServerBodyIfNeeded for why that would strand the transcript).
        const proceed = await restoreServerBodyIfNeeded({
            id,
            tenantId: tenantIdRef.current,
            persistenceOn: chatPersistenceEnabled(),
            deps: { loadChat, restoreTranscript },
        });
        if (!proceed) {
            needsBodyLoadRef.current.add(id);
            return;
        }
        let res;
        try {
            res = await readChatBody(id);
        } catch (err) {
            // A real READ failure (transient IDB error), distinct from a
            // missing record. Leave the chat unloaded: keeping
            // bodyLoaded=false holds the persistence loop's skip guard
            // engaged so the next save can't overwrite the still-intact
            // on-disk transcript with our empty placeholder. The chat
            // stays in its restoring state until a reload re-arms and
            // retries — never silent data loss. (O-1746 review.)
            console.warn("[chat] body load failed for chat", id, err);
            return;
        }
        if (!res) {
            // Genuinely absent (or storage unavailable, in which case
            // saveChat no-ops too) — safe to mark loaded so the chat
            // renders as an empty transcript rather than a perpetual
            // spinner, and becomes persistable.
            updateChat(id, (c) => (c.bodyLoaded ? c : { ...c, bodyLoaded: true }));
            return;
        }
        pendingHydrationRef.current.set(id, res.hydration);
        // A chat restored from the server renders from its persisted turns but
        // carries no agent messages - those never left the browser that ran it.
        // Arm the continuation rebuild so the first send has the conversation
        // the model saw, instead of starting the model over on an empty
        // history in front of a full transcript.
        if (needsContinuationRebuild({
            persistenceOn: chatPersistenceEnabled(),
            serverChatId: res.chat.serverChatId,
            itemCount: res.chat.items.length,
            agentMessageCount: (res.hydration.agentMessages || []).length,
        })) {
            needsContinuationRef.current.add(id);
        }
        updateChat(id, (c) => ({
            ...c,
            // Restore the chat "kind" from the persisted body. The
            // lightweight chatMeta the tab was rebuilt from on mount has
            // no `kind` (it's a tab-strip projection), so without this a
            // restored scout tab would render as a raw transcript instead
            // of the profile view once its body loads.
            kind: res.chat.kind ?? null,
            title: res.chat.title,
            items: res.chat.items,
            toggled: res.chat.toggled,
            thinkingLevel: mergeLoadedThinkingLevel(c.thinkingLevel, res.chat.thinkingLevel),
            serverChatId: res.chat.serverChatId ?? c.serverChatId ?? null,
            scopeMode: res.chat.scopeMode,
            skipScope: res.chat.skipScope,
            error: res.chat.error,
            hasItems: res.chat.items.length > 0,
            bodyLoaded: true,
        }));

        // Lazily hydrate externalized blobs (frozen tables/charts, pasted
        // images) in the background so the transcript renders immediately and
        // heavy content streams in after. Only for a restored chat whose items
        // still carry blob placeholders; a locally-authored chat has its real
        // content inline. Best-effort — an unresolved blob renders a notice.
        if (chatPersistenceEnabled() && res.chat.serverChatId) {
            const items = res.chat.items || [];
            if (items.some(hasBlobPlaceholders)) {
                (async () => {
                    try {
                        // Hydrate the restored snapshot's blob placeholders with a
                        // bounded worker pool (hydrateItemsBounded — unit-tested),
                        // then merge the results back by OBJECT IDENTITY rather
                        // than replacing c.items: the chat is continuable while
                        // this network work is in flight, so a wholesale replace
                        // would clobber turns appended meanwhile. Identity (not
                        // id) so an id-less item still merges, and an item the
                        // user replaced during hydration (new object) is untouched.
                        // hydrateRestoredBlobs also records what it downloaded
                        // into the sync engine's dedup cache, before returning:
                        // the state update below is what flips the save
                        // fingerprint and schedules the boundary that would
                        // otherwise re-upload every one of those blobs.
                        const hydratedByOriginal = await hydrateRestoredBlobs({
                            tenantId: tenantIdRef.current,
                            localId: id,
                            serverChatId: res.chat.serverChatId,
                            items,
                        });
                        if (hydratedByOriginal.size === 0) return;
                        // Identity-based fold (mergeHydratedItems — unit-tested) so
                        // turns appended during hydration aren't clobbered.
                        updateChat(id, (c) => {
                            const merged = mergeHydratedItems(c.items, hydratedByOriginal);
                            return merged === c.items ? c : { ...c, items: merged };
                        });
                    } catch { /* leave placeholders as load notices */ }
                })();
            }
        }
    }, [readChatBody, updateChat]);
    // One load per chat at a time. A card watching the chat's graph can start it
    // while the chat is being opened or its agent built, and every caller waits
    // on the same load: the build reads the hydration the load leaves behind,
    // and a caller that saw the load already claimed would otherwise go on
    // without it.
    const bodyLoadsRef = useRef(new Map());
    const loadChatBodyIntoState = useCallback((id) => {
        if (!bodyLoadsRef.current.has(id) && !needsBodyLoadRef.current.has(id)) return Promise.resolve();
        return sharedRun(bodyLoadsRef.current, id, () => readBodyIntoState(id));
    }, [readBodyIntoState]);

    // Ensure EVERY open chat's body is loaded into state — used before
    // operations that read across all transcripts at once (cross-chat
    // audit). Cheap once warmed; the per-chat claim skips loaded chats.
    const ensureAllBodiesLoaded = useCallback(async () => {
        const ids = bodiesToAwait(chatsRef.current, needsBodyLoadRef.current, bodyLoadsRef.current);
        if (ids.length === 0) return;
        // Bound the fan-out: a tab strip can hold up to max_chats_per_user (2000)
        // restored stubs, each a transcript fetch (+ lazy blob GETs). An unbounded
        // Promise.all would burst them all at once — cap it like the blob pool.
        await mapWithConcurrency(ids, 4, (cid) => loadChatBodyIntoState(cid));
    }, [loadChatBodyIntoState]);

    // Read-only variant for export: returns a chats array with every
    // transcript present WITHOUT mutating React state (an export
    // shouldn't permanently de-lazify chats the user never opened).
    // Already-loaded chats pass through with their live, possibly-unsaved
    // edits; un-opened chats are read straight from IndexedDB.
    const collectChatsForExport = useCallback(async () => {
        const live = chatsRef.current || [];
        return Promise.all(live.map(async (c) => {
            if (c.bodyLoaded !== false) return c;
            let res = null;
            try { res = await readChatBody(c.id); }
            catch { /* fall through to the lightweight chat */ }
            return res ? { ...c, ...res.chat } : c;
        }));
    }, [readChatBody]);

    // Wire a chat's agent._childHandleRegistry to live UI updates:
    // whenever a still-running sub-agent's state mutates after its
    // spawn tool returned (interrupted spawn), update the matching
    // chat.items tool node so the rendered details reflect the live
    // child rather than the frozen snapshot at interrupt time.
    //
    // Throttled to ~80ms (leading + trailing edge) so streaming text
    // deltas — which fire many times a second — don't churn React.
    // Mirrors the throttle cadence of makeThrottledEmit in
    // subAgentTools.js so behavior before and after interrupt is
    // visually identical.
    const wireChildRegistrySync = useCallback((chatId, agent) => {
        const registry = agent?._childHandleRegistry;
        if (!registry) return;
        let scheduled = false;
        let dirty = false;
        const runSync = () => {
            updateChat(chatId, (c) => {
                const items = withChildDetails(c.items, registry);
                return items === c.items ? c : { ...c, items };
            });
        };
        const sync = () => {
            if (scheduled) {
                dirty = true;
                return;
            }
            scheduled = true;
            try { runSync(); } catch { /* registry sync mustn't break the chat */ }
            setTimeout(() => {
                scheduled = false;
                if (dirty) {
                    dirty = false;
                    sync();
                }
            }, 80);
        };
        const unsubscribe = registry.subscribe(sync);
        childRegistryUnsubsRef.current.set(chatId, unsubscribe);
    }, [updateChat]);

    const teardownChildRegistrySync = useCallback((chatId) => {
        const off = childRegistryUnsubsRef.current.get(chatId);
        if (off) {
            try { off(); } catch { /* noop */ }
            childRegistryUnsubsRef.current.delete(chatId);
        }
    }, []);

    const handleChatSyncResult = useCallback((id, result) => {
        if (!result) return;
        updateChat(id, (c) => {
            const patch = syncResultPatch(result.status, c, result.kind);
            return patch ? { ...c, ...patch } : c;
        });
    }, [updateChat]);

    const flushPendingSaves = useCallback((opts) => {
        if (!hydrated) return;
        const exiting = !!opts?.exiting;
        const currentChats = chatsRef.current || [];
        // Boundary-sync POSTs are collected here and drained through a bounded
        // worker pool after the loop (mirrors the restore fan-out cap), so a
        // flush with many simultaneously-dirty chats can't fire one POST per chat
        // at once and monopolize the backend's shared SyncChat concurrency budget.
        const syncTargets = [];
        for (const chat of currentChats) {
            if (!chat) continue;
            // Body not loaded yet (restored from metadata, never opened
            // this session): the in-memory `items` is an empty
            // placeholder, NOT the real transcript. Persisting it would
            // clobber the on-disk body with nothing. Skip — the stored
            // record is already current, and saveChat keeps its meta
            // in sync.
            if (chat.bodyLoaded === false) continue;
            const agent = agentsRef.current.get(chat.id);
            if (!agent && pendingHydrationRef.current.has(chat.id)) {
                continue;
            }
            let canvas = null;
            try { canvas = readCanvasStore(chat.id); } catch { /* noop */ }

            const messages = agent?.state?.messages;
            const refs = {
                items: chat.items,
                toggled: chat.toggled,
                canvas,
                messages,
                messagesLen: messages?.length || 0,
                title: chat.title,
                thinkingLevel: chat.thinkingLevel,
                scopeMode: chat.scopeMode,
                skipScope: chat.skipScope,
                busy: chat.busy,
            };
            const prev = lastChatRefsRef.current.get(chat.id);
            if (prev
                && prev.items === refs.items
                && prev.toggled === refs.toggled
                && prev.canvas === refs.canvas
                && prev.messages === refs.messages
                && prev.messagesLen === refs.messagesLen
                && prev.title === refs.title
                && prev.thinkingLevel === refs.thinkingLevel
                && prev.scopeMode === refs.scopeMode
                && prev.skipScope === refs.skipScope
                && prev.busy === refs.busy
            ) {
                continue;
            }
            lastChatRefsRef.current.set(chat.id, refs);

            // Split the chat's toolResultStore into the small entries
            // (inlined in the chat record) and large ones (written to
            // the separate largeRows store). The namespace key for the
            // tool store matches piChat's getNamespaceKey — String(id).
            let storeSnapshot = { small: {}, large: [] };
            try {
                storeSnapshot = snapshotForPersistence(String(chat.id));
            } catch (err) {
                console.warn("[chat] snapshotForPersistence failed for chat", chat.id, err);
            }

            let payload;
            try {
                payload = serializeChatForStorage(chat, {
                    agent,
                    canvas,
                    smallToolResults: storeSnapshot.small,
                });
            } catch (err) {
                console.warn("[chat] serialize failed for chat", chat.id, err);
                continue;
            }
            if (!payload) continue;
            // Content-only fingerprint (excludes the serializer's fresh
            // `savedAt`); see chatSaveFingerprint for why comparing raw payload
            // JSON would make every boundary look changed.
            const json = chatSaveFingerprint(payload);
            if (json == null) {
                console.warn("[chat] payload fingerprint failed for chat", chat.id);
                continue;
            }
            const prevHash = lastSavedJsonRef.current.get(chat.id);
            const chatChanged = prevHash !== json;
            if (chatChanged) {
                lastSavedJsonRef.current.set(chat.id, json);
                saveChat(tenantIdRef.current, chat.id, payload).catch(() => { /* logged in chatStorage */ });
            }

            // Large rows fingerprint: ids + recordedAt are enough —
            // entries are immutable once recorded (overwritten only
            // on a fresh recordResult, which bumps recordedAt). Skip
            // the chunky saveLargeRows write when unchanged.
            const largeFp = storeSnapshot.large
                .map(({ id, entry }) => `${id}:${entry.recordedAt || 0}`)
                .sort()
                .join(",");
            const prevLargeFp = lastSavedLargeFpRef.current.get(chat.id);
            if (largeFp !== prevLargeFp) {
                lastSavedLargeFpRef.current.set(chat.id, largeFp);
                saveLargeRows(tenantIdRef.current, chat.id, storeSnapshot.large).catch(() => { /* logged */ });
            }

            const syncPlan = planSaveBoundarySync({
                payload,
                chatChanged,
                exiting,
                busy: !!chat.busy,
                persistenceOn: chatPersistenceEnabled(),
            });
            // Mark the outbox dirty whenever there's anything to push — a content
            // change, OR an interrupted exit flush (sync-only). This is the
            // durability backstop: the boundary POST below is bounded + deferred,
            // so if it doesn't complete (e.g. the tab is torn down at unload), the
            // dirty entry guarantees the next app-open drain re-syncs it (carrying
            // the interrupted marker). Without marking the sync-only exit case
            // dirty, an undelivered interrupted POST would be lost. Cheap IDB write.
            if (syncPlan.markDirty || syncPlan.sync) {
                markChatDirty({ tenantId: tenantIdRef.current, localId: chat.id, serverChatId: payload.serverChatId })
                    .catch(() => {});
            }
            if (syncPlan.sync) {
                syncTargets.push({
                    localId: chat.id,
                    serverChatId: payload.serverChatId,
                    storedPayload: payload,
                    interrupted: syncPlan.interrupted,
                    // Wire exchanges: the live agent's history plus every
                    // sub-agent transcript hanging off the rendered tool nodes.
                    // Read from the LIVE chat, not `payload` — the serialized
                    // record pools messages into index refs and byte-clips tool
                    // details, both of which would corrupt the captured
                    // conversation. The engine turns this into the
                    // since-last-ack delta against the outbox cursor, so a long
                    // chat re-sends each LLM call exactly once.
                    exchangeSource: {
                        agentMessages: messages || [],
                        systemPrompt: agent?.state?.systemPrompt || null,
                        items: chat.items || [],
                    },
                });
            }
        }

        // Drain the boundary syncs through a bounded worker pool (cap 4, matching
        // the restore fan-out). Fire-and-forget: IDB is the truth and a sync
        // failure is never user-blocking; the per-item catch keeps one failure
        // from aborting the rest of the pool.
        if (syncTargets.length) {
            const tenantId = tenantIdRef.current;
            mapWithConcurrency(syncTargets, 4, async (t) => {
                try {
                    const result = await syncStoredChat({ tenantId, ...t });
                    handleChatSyncResult(t.localId, result);
                } catch { /* logged in the engine; IDB stays the truth */ }
            }).catch(() => {});
        }

        // Keep the id fence above every live chat id so an old
        // (schemaVersion-1-only) build cold-loading this tenant's
        // store can never mint a colliding id — see chatIds.js.
        const maxLiveId = Math.max(
            0,
            ...currentChats.map((c) => Number(c?.id)).filter((n) => Number.isFinite(n)),
        );
        const fenceTarget = maxLiveId + 1;
        if (maxLiveId > 0 && fenceTarget > lastFenceTargetRef.current) {
            lastFenceTargetRef.current = fenceTarget;
            ensureIdFence(tenantIdRef.current, fenceTarget).catch(() => { /* logged in chatStorage */ });
        }
    }, [hydrated, handleChatSyncResult]);

    const [canvasTick, setCanvasTick] = useState(0);
    useEffect(() => {
        return subscribeAnyCanvas(() => setCanvasTick((t) => (t + 1) | 0));
    }, []);

    // Debounced auto-save: every chat-state change kicks a ~1s tail
    // timer; rapid changes (streaming text, child-registry sync
    // bursts) coalesce into a single write. Note: deps include
    // `chats` so the timer is re-armed on every state mutation, and
    // the cleanup cancels the prior timer — classic debounce shape.
    useEffect(() => {
        if (!hydrated) return;
        const timer = setTimeout(() => {
            flushPendingSaves();
        }, 1000);
        return () => clearTimeout(timer);
    }, [chats, hydrated, flushPendingSaves, canvasTick]);

    // Flush on tab hide / unload so a refresh or close doesn't drop
    // up to a second of pending state. `visibilitychange` fires more
    // reliably than `beforeunload` on mobile + tab-discard, so we
    // wire both.
    useEffect(() => {
        if (!hydrated) return;
        const onHide = () => {
            if (typeof document !== "undefined" && document.visibilityState === "hidden") {
                flushPendingSaves({ exiting: true });
            }
        };
        const onBeforeUnload = () => { flushPendingSaves({ exiting: true }); };
        document.addEventListener("visibilitychange", onHide);
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => {
            document.removeEventListener("visibilitychange", onHide);
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [hydrated, flushPendingSaves]);

    // React to an out-of-band chat wipe (Settings -> Clear chat history,
    // or LogoutDialog). chatStorage just deleted every origin-chat row
    // for this tenant; if we don't also drop the in-memory transcript
    // here, the very next flushPendingSaves (debounce tick or the
    // beforeunload above) re-persists it and the wipe looks no-op'd.
    // Reset to a fresh empty chat with the same ids the initial mount
    // would have produced, then reset the save bookkeeping so the
    // next save compares against the new empty payload, not the
    // pre-wipe content.
    useEffect(() => {
        return subscribeChatsCleared(() => {
            setChats([makeChat(1)]);
            setActiveId(1);
            nextChatIdRef.current = 2;
            pendingHydrationRef.current.clear();
            needsBodyLoadRef.current.clear();
            lastSavedJsonRef.current.clear();
            lastSavedLargeFpRef.current.clear();
            lastChatRefsRef.current.clear();
            agentsRef.current.clear();
            triageHostRef.current.clear();
        });
    }, []);

    // Cross-tab chat-sync coherence. Same-browser siblings share IDB but not
    // in-memory state; adopt their sync/delete events so a chat synced or
    // deleted in another tab reflects here without a spurious "updated
    // elsewhere" prompt. A cross-DEVICE conflict still surfaces via the
    // revision guard (handleChatSyncResult).
    useEffect(() => {
        if (!chatPersistenceEnabled()) return undefined;
        return subscribeChatSync((msg) => {
            const routed = classifyChatSyncMessage(msg, chatsRef.current);
            if (routed.kind === "ignore") return;
            const local = routed.local;
            if (routed.kind === "delete") {
                // Sibling already tombstoned + removed the shared IDB row; just
                // drop this tab's in-memory copy. Functional updater (mirrors
                // closeChat): it receives React's accumulated state, so several
                // back-to-back sibling deletes compose correctly — a chatsRef
                // snapshot would lag a passive-effect flush and resurrect an
                // already-removed tab. The removal/reassignment DECISION is the
                // pure, unit-tested planCrossTabDelete; the makeChat/setActiveId
                // side effects it drives run inside the updater as in closeChat.
                setChats((cur) => {
                    const plan = planCrossTabDelete(cur, local.id, activeIdRef.current);
                    if (!plan) return cur;
                    if (plan.needsFresh) {
                        const fresh = makeChat(nextChatIdRef.current++);
                        setActiveId(fresh.id);
                        return [fresh];
                    }
                    if (plan.activeId !== activeIdRef.current) setActiveId(plan.activeId);
                    return plan.chats;
                });
                teardownChildRegistrySync(local.id);
                try { agentsRef.current.get(local.id)?.releaseGraph?.(); } catch { /* release is best-effort */ }
                agentsRef.current.delete(local.id);
            } else if (routed.kind === "synced") {
                // A sibling advanced this chat; clear any stale conflict banner
                // (same patch an "ok" sync result applies).
                updateChat(local.id, (c) => {
                    const patch = syncResultPatch("ok", c);
                    return patch ? { ...c, ...patch } : c;
                });
            }
        });
    }, [teardownChildRegistrySync, updateChat]);

    const newChat = useCallback((seedText = null) => {
        // Reuse an existing empty chat instead of stacking blank "New chat"
        // sessions (the overlay + the docked strip). Only spin up a fresh one
        // when every chat already has messages. A seeded chat (a prepared
        // first message from another surface) never overwrites unsent text.
        const { id, create, seed } = seededChatTarget(chats, seedText, nextChatIdRef.current);
        if (create) {
            nextChatIdRef.current++;
            setChats((cur) => [...cur, makeChat(id)]);
        }
        if (seed) setChats((cur) => cur.map((c) => (c.id === id ? { ...c, input: seed } : c)));
        setActiveId(id);
        requestAnimationFrame(() => inputRef.current?.focus());
    }, [chats]);

    const closeChatTab = useCallback((id) => {
        const remaining = chatsRef.current.filter((c) => c.id !== id && !closedChatIds.has(c.id));
        if (!remaining.length) return;
        setClosedChatIds((cur) => new Set([...cur, id]));
        if (activeIdRef.current === id) setActiveId(remaining.at(-1).id);
    }, [closedChatIds]);

    const deleteSavedChat = useCallback((id) => {
        setClosedChatIds((cur) => { const next = new Set(cur); next.delete(id); return next; });
        const chatObj = (chatsRef.current || []).find((c) => c.id === id);
        const closePlan = planChatClose({ chatObj, persistenceOn: chatPersistenceEnabled() });
        if (closePlan.mode === "everywhere") {
            deleteChatEverywhere({ tenantId: tenantIdRef.current, localId: id, serverChatId: closePlan.serverChatId }).catch(() => {});
        } else {
            deleteChat(tenantIdRef.current, id).catch(() => { /* logged in chatStorage */ });
        }
        lastSavedJsonRef.current.delete(id);
        lastSavedLargeFpRef.current.delete(id);
        lastChatRefsRef.current.delete(id);
        needsBodyLoadRef.current.delete(id);
        agentBuildingRef.current.delete(id);
        triageHostRef.current.forget(id);
        setChats((cur) => {
            if (cur.length <= 1) {
                // Last chat: replace with a fresh blank rather than
                // leaving the overlay empty. Drop the agent for the
                // old id so it doesn't outlive its chat.
                teardownChildRegistrySync(id);
                try { agentsRef.current.get(id)?.releaseGraph?.(); } catch { /* release is best-effort */ }
                agentsRef.current.delete(id);
                destroyCanvas(id);
                const fresh = makeChat(nextChatIdRef.current++);
                setActiveId(fresh.id);
                return [fresh];
            }
            const idx = cur.findIndex((c) => c.id === id);
            if (idx < 0) return cur;
            const next = cur.slice(0, idx).concat(cur.slice(idx + 1));
            teardownChildRegistrySync(id);
            try { agentsRef.current.get(id)?.releaseGraph?.(); } catch { /* release is best-effort */ }
            agentsRef.current.delete(id);
            destroyCanvas(id);
            // If we closed the active tab, jump to the neighbor that
            // took its slot (or the new last tab when closing the
            // rightmost one).
            if (activeIdRef.current === id) {
                const neighbor = next[Math.min(idx, next.length - 1)];
                if (neighbor) setActiveId(neighbor.id);
            }
            return next;
        });
    }, [teardownChildRegistrySync]);

    const forkFromTurn = useCallback((turnId) => {
        const id = activeIdRef.current;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat) return;
        const idx = chat.items.findIndex((it) => it.kind === "turn" && it.id === turnId);
        if (idx < 0) return;
        const turn = chat.items[idx];
        if (!turn.snapshot) return; // turn hasn't completed yet
        const newId = nextChatIdRef.current++;
        // Stash the Pi message snapshot for the agent-creation effect
        // to consume. Slice() each side so neither chat aliases the
        // other's transcript array.
        pendingForkSnapshotRef.current.set(newId, turn.snapshot.slice());
        cloneStore(String(id), String(newId));
        cloneCanvasStore(id, newId);
        setChats((cur) => [
            ...cur,
            {
                ...makeChat(newId),
                title: chat.title === "New chat" ? "Fork" : `${chat.title} (fork)`,
                items: chat.items.slice(0, idx + 1),
                thinkingLevel: chat.thinkingLevel,
            },
        ]);
        setActiveId(newId);
    }, []);

    // Track whether a PDF export is in-flight so the button can show
    // a busy state and we can refuse concurrent exports.
    // Id of the chat currently exporting to PDF (null = none). Per-chat
    // so the export affordance lives on each tab, not as one rail button.
    const [exportingId, setExportingId] = useState(null);

    const exportPdf = useCallback(async (chatId) => {
        const id = chatId || activeIdRef.current;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat || !chat.items?.length || exportingId) return;
        setExportingId(id);
        try {
            const [{ exportChatToPdf }, { exportNodeAsPng }] = await Promise.all([
                import("../lib/exportChatPdf.js"),
                import("../lib/exportNodePng.js"),
            ]);
            const renderBlock = async ({ kind, spec, data }) => {
                const host = document.createElement("div");
                const blockKind = kind === "kernelBlock" ? spec?.kind : null;
                const isChartBlock = blockKind === "chart" || blockKind === "sparkline";
                const fixedHeight = isChartBlock
                    ? `height:${Number.isFinite(spec?.height) ? spec.height + 60 : 300}px;display:flex;flex-direction:column;`
                    : "";
                host.style.cssText =
                    `position:fixed;left:-100000px;top:0;width:760px;${fixedHeight}background:var(--bg);color:var(--ink);padding:12px;font-family:inherit;`;
                document.body.appendChild(host);
                const root = createRoot(host);
                try {
                    root.render(
                        kind === "kernelBlock" ? (
                            // Flex column with a sized child so chart
                            // primitives' outer divs get a real height
                            // to fill, propagating to SurfaceChart's
                            // h-full w-full container.
                            <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
                                <div style={{ flex: "1 1 auto", minHeight: isChartBlock ? 200 : 0 }}>
                                    <KernelBlock spec={spec} data={data} onOpenScope={() => {}} />
                                </div>
                            </div>
                        ) : (
                            <AnalyticsResultTable result={spec} onOpenScope={() => {}} />
                        ),
                    );
                    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

                    if (isChartBlock) {
                        const canvas = host.querySelector("canvas");
                        if (canvas && canvas.width > 0 && canvas.height > 0) {
                            return canvas.toDataURL("image/png");
                        }
                    }

                    return await exportNodeAsPng(host, {
                        pixelRatio: 2,
                        backgroundColor: getComputedStyle(document.documentElement)
                            .getPropertyValue("--bg")
                            .trim() || "#0d0d0d",
                        padding: 8,
                    });
                } finally {
                    root.unmount();
                    host.remove();
                }
            };
            const title = chat.title && chat.title !== "New chat" ? chat.title : null;
            const stamp = new Date().toISOString().slice(0, 10);
            const slug = (title || "chat").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40);
            await exportChatToPdf(chat, {
                renderBlock,
                title,
                filename: `${slug}-${stamp}.pdf`,
            });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[chat] pdf export failed:", err);
            updateChat(id, (c) => ({
                ...c,
                error: `PDF export failed: ${err.message || err}`,
            }));
        } finally {
            setExportingId(null);
        }
    }, [exportingId, updateChat]);

    // A job a surface ran for the reader (lib/chatJobs.js: the board's walk
    // through) lands in the chat in front as a row of its own; the words it
    // came to are kept on the row once it is done (settleJob), for a browser
    // that does not hold what it made.
    useEffect(() => onChatJob((item) => {
        const id = item.targetChatId ?? activeIdRef.current;
        if (id == null) return;
        const { targetChatId: _target, ...row } = item;
        updateChat(id, (c) => ({ ...c, items: appendChatJob(c.items, row) }));
    }), [updateChat]);
    useEffect(() => onChatJobPatch((key, patch) => setChats((cur) => patchJobRows(cur, key, patch))), []);
    const settleJob = useCallback((itemId, patch) => {
        updateChat(activeIdRef.current, (c) => ({ ...c, items: (c.items || []).map((it) => (it?.kind === "job" && it.id === itemId ? { ...it, ...patch } : it)) }));
    }, [updateChat]);

    const toggleNode = useCallback((nodeId, kind) => {
        const id = activeIdRef.current;
        updateChat(id, (c) => {
            const current = c.toggled?.[nodeId];
            const next = current === undefined ? !DEFAULT_EXPANDED[kind] : !current;
            return { ...c, toggled: { ...c.toggled, [nodeId]: next } };
        });
    }, [updateChat]);

    const reorderChats = useCallback((from, to) => {
        setChats((cur) => {
            if (from === to || from < 0 || from >= cur.length) return cur;
            const next = cur.slice();
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();
    const openCanvas = useTraceCanvasOpen();
    const canvasUp = !!openCanvas;
    const policyInvestigationId = useTraceCanvas(openCanvas?.id || null).investigationId;

    // ⌘K: by default opens the floating overlay everywhere (the familiar
    // behaviour). Behind the `origin.feature.chat-autodock` flag, on a page
    // that hosts the dock (Explore) it instead surfaces the docked chat —
    // flip the right column to Chat + un-collapse it + focus the composer
    // (ScopedPage listens for the event and owns panelMode + the collapse).
    // The Context⇄Chat rail switch docks the chat manually regardless of
    // the flag; this only gates the ⌘K auto-dock.
    const toggle = useCallback(() => {
        if (openPageRef.current) {
            openPageRef.current();
            setTimeout(() => inputRef.current?.focus(), 60);
            return;
        }
        // When the chat is ALREADY docked and on screen, ⌘K opens a NEW
        // session in the dock — same as the dock's "+" button. Branch on
        // the ACTUAL docked state (getDockBodyHost() is non-null only
        // while the right column is in Chat mode), NOT on the autodock
        // flag: the chat can be docked manually via the rail switch with
        // the flag off, and gating on the flag is what made ⌘K dead here.
        // newChat() activates the session and focuses the docked composer.
        if (getDockAvailable() && getDockBodyHost()) {
            newChat();
            return;
        }
        // Dock available (autodock) but chat not on screen → surface it.
        if (getDockAvailable() && chatAutoDockEnabled()) {
            window.dispatchEvent(new CustomEvent("origin-chat-activate"));
            // The composer may need a render to mount when switching in
            // from Context; the open-driven autofocus covers that case,
            // this covers the already-docked case.
            setTimeout(() => inputRef.current?.focus(), 60);
            return;
        }
        // No dock host on this page → the floating overlay.
        setOpen((v) => !v);
    }, [newChat]);
    const close = useCallback(() => setOpen(false), []);
    useGlobalHotkey(toggle);

    // A first message staged by another surface (the findings review's
    // "Investigate in chat") opens the chat the way the hotkey does and lands
    // in a fresh composer. Nothing is sent until the analyst presses Send.
    const openSeeded = useCallback(() => {
        const seed = takeChatSeed();
        if (!seed) return;
        const surface = seedSurface({ dockAvailable: getDockAvailable(), dockBodyHost: Boolean(getDockBodyHost()), autoDock: chatAutoDockEnabled() });
        if (openPageRef.current) openPageRef.current();
        else if (surface === "activate") window.dispatchEvent(new CustomEvent("origin-chat-activate"));
        else if (surface === "overlay") setOpen(true);
        newChat(seed.text);
        setTimeout(() => inputRef.current?.focus(), 60);
    }, [newChat]);
    useEffect(() => {
        openSeeded();
        return subscribeChatSeed(openSeeded);
    }, [openSeeded]);

    // Dock and overlay are mutually exclusive surfaces for this one
    // chat instance. Entering the dock opens the session so open-gated
    // effects (autofocus, autoscroll) still run; leaving it closes the
    // overlay so it doesn't pop back over the page.
    useEffect(() => {
        dockedRef.current = docked;
        setOpen(docked);
    }, [docked]);

    // Chat's own settings (model, provider and key) open in a dialog over the
    // chat; `true` lets the picker choose its tab (the key, with none saved).
    // Any other view still goes to Settings.
    const [chatSettings, setChatSettings] = useState(null);
    const openSettings = useCallback((view = "chat-models") => {
        if ((settingForView(view) ?? "chat/model").startsWith("chat")) {
            setChatSettings(view === "chat-models" ? true : (settingForView(view).split("/")[1] ?? true));
            return;
        }
        setOpen(false);
        if (onOpenSettings) onOpenSettings(view);
        else setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.set("mode", "settings");
            next.set("setting", settingForView(view) ?? "chat/model");
            next.delete("adminView");
            next.delete("settingsView");
            next.delete("view");
            next.delete("menu");
            return next;
        });
    }, [onOpenSettings, setSearchParams]);

    // Open a detected scope as a dashboard tab in the MAIN Explore
    // column — used by the render_block "open scope" affordance.
    // Publishes through navigationStore so App applies it via the same
    // mode-aware, open-or-activate path the chat's navigation tool uses
    // (the old hand-written `tabs`/`active` write didn't switch into
    // Explore mode, so it no-oped from Studio/Settings — same class as
    // O-2082). The overlay-close is handled by the navigationStore
    // subscription below (docked chats stay put).
    const openScopeAsTab = useCallback((dim, ids) => {
        const cleanIds = Array.isArray(ids)
            ? ids.filter((x) => typeof x === "string" && x.length)
            : (typeof ids === "string" && ids ? [ids] : []);
        if (!DIMENSIONS[dim] || !cleanIds.length) return;
        requestNavigation({ kind: "workspace", scopePath: [{ dim, ids: cleanIds }] });
    }, []);

    // After ANY chat-driven navigation (agent tool, in-transcript chip,
    // or render_block "open scope"), a floating overlay should get out
    // of the way so the user actually sees the page it opened. A docked
    // chat stays put beside the new tab. App owns the navigation itself
    // (it subscribes too and mutates the URL); this side only owns the
    // overlay's own open state.
    useEffect(() => subscribeNavigation(() => {
        if (!dockedRef.current) setOpen(false);
    }), []);

    // A trace reading wearing a chat's knowledge graph asks for that chat back
    // by its graph key (the Lanes graph dock's "open the chat"). The page flips
    // its column to the chat on the same event; this side picks the chat.
    useEffect(() => {
        const onActivate = (e) => {
            openPageRef.current?.();
            const key = e?.detail?.graphKey;
            if (!key) return;
            const chat = chatForGraphKey(chatsRef.current, key);
            if (chat) setActiveId(chat.id);
            if (!getDockAvailable()) setOpen(true);
        };
        window.addEventListener("origin-chat-activate", onActivate);
        return () => window.removeEventListener("origin-chat-activate", onActivate);
    }, []);

    const buildAgent = useCallback(async (id) => {
        agentBuildingRef.current.add(id);
        try {
            // Clear any stale agent-build error from a prior failed
            // activation. Banner-preserving by value (see
            // clearedStaleActivationError), and run BEFORE the body
            // load anyway — hydration sets chat.error to the
            // interrupted banner, and the old blanket reset after it
            // erased the banner on every first activation.
            updateChat(id, (c) => ({ ...c, error: clearedStaleActivationError(c.error) }));
            // Lazily fetch this chat's transcript the first time it's
            // opened — chats restored from `chatMeta` carry only their
            // tab fields until now. Populates pendingHydrationRef[id]
            // and patches the restored items into React state.
            await loadChatBodyIntoState(id);
            const hydrationPayload = pendingHydrationRef.current.get(id);
            if (hydrationPayload?.canvas) {
                try { writeCanvasStore(id, hydrationPayload.canvas); }
                catch (err) { console.warn("[chat] writeCanvasStore failed:", err); }
            }
            // Build the agent and load any persisted large tool-result
            // rows in parallel. Large rows live in their own IDB store
            // (chunky payloads kept separate from the chat record so
            // the chat record stays cheap to rewrite). We need both
            // before calling loadStore so the toolResultStore has full
            // visibility before any tool can fire.
            const [agent, largeEntries] = await Promise.all([
                createChatAgent({
                    chatId: String(id),
                    ...chatToolOptions(id, { host: triageHostRef.current, chatsRef, sent: sentAddedRef.current }),
                    onHarnessEvent: harnessEventHook(id),
                    serverChatId: (chatsRef.current || []).find((c) => c.id === id)?.serverChatId || null,
            getServerChatId: () => (chatsRef.current || []).find((c) => c.id === id)?.serverChatId || null,
                        runContext: LIVE_COMMAND_RUN_CONTEXT,
                    getRunContext: () => (
                        interactiveAgentRunContextsRef.current.get(id)
                        || LIVE_COMMAND_RUN_CONTEXT
                    ),
                }),
                hydrationPayload ? loadLargeRows(tenantIdRef.current, id) : Promise.resolve([]),
            ]);
            // Chat may have been closed during the build. Skip
            // installing the agent if so — better to discard the
            // runtime than to leak an orphan in agentsRef with no
            // corresponding chat object.
            const stillExists = chatsRef.current.some((c) => c.id === id);
            if (!stillExists) return;
            // Apply the chat's reasoning budget. An explicit choice
            // (incl. "off") wins; an unset chat falls back to the
            // model's default depth (medium for top reasoning models).
            // resolveThinkingLevel drops a stale "off" on a model that
            // can't accept it (Fable) so a persisted "off" + model swap
            // doesn't 400 the next send.
            const chat = chatsRef.current.find((c) => c.id === id);
            agent.state.thinkingLevel = resolveThinkingLevel(
                agent.state.model?.provider, agent.state.model?.id, chat?.thinkingLevel);
            // Forked chat: replay the parent's transcript into
            // this agent so the next prompt has the full history
            // as LLM context. Mutating state.messages is safe
            // here — the agent hasn't started a run yet.
            const snap = pendingForkSnapshotRef.current.get(id);
            if (snap) {
                setTranscript(agent, snap);
                pendingForkSnapshotRef.current.delete(id);
            }
            const hydration = pendingHydrationRef.current.get(id);
            if (hydration) {
                if (Array.isArray(hydration.agentMessages) && hydration.agentMessages.length) {
                    setTranscript(agent, hydration.agentMessages);
                }
                // Restore the chat's toolResultStore namespace —
                // small entries inline in the hydration payload,
                // large entries from IDB. Namespace key matches
                // piChat.js's getNamespaceKey: String(chat.id).
                try {
                    loadStore(String(id), {
                        small: hydration.smallToolResults || {},
                        large: largeEntries || [],
                    });
                } catch (err) {
                    console.warn("[chat] loadStore failed for chat", id, err);
                }
                // Seed the large-row fingerprint so the first
                // post-hydrate flush doesn't rewrite unchanged
                // data.
                const fp = (largeEntries || [])
                    .map(({ id: tid, entry }) => `${tid}:${entry?.recordedAt || 0}`)
                    .sort()
                    .join(",");
                lastSavedLargeFpRef.current.set(id, fp);
                pendingHydrationRef.current.delete(id);
            }
            agentsRef.current.set(id, agent);
            setAgentReadyEpoch((n) => n + 1);
            wireChildRegistrySync(id, agent);
        } catch (err) {
            const stillExists = chatsRef.current.some((c) => c.id === id);
            if (stillExists) {
                updateChat(id, (c) => ({ ...c, error: err.message || String(err) }));
            }
        } finally {
            agentBuildingRef.current.delete(id);
        }
    }, [updateChat, wireChildRegistrySync, loadChatBodyIntoState]);

    // Build agents lazily, per chat. The active chat warms its agent
    // on open / activation so it's ready by the time the user finishes
    // typing. All agents are cleared on close so stale agents don't
    // outlive a settings change (matches the original single-chat
    // behavior). createChatAgent is async because tenantId comes from
    // /v1/me (cached after first hit).
    useEffect(() => {
        if (!open) return;
        // Wait for the hydrate effect to populate `chats` from
        // IndexedDB before building any agents. Without this, the
        // default makeChat(1) chat would briefly build a runtime
        // against id=1 only to have it replaced when the persisted
        // chats land.
        if (!hydrated) return;
        const id = activeId;
        // Agent already exists or a build is in flight for this id —
        // just focus the input. Without the agentBuildingRef gate, a
        // rapid X → Y → X switch would fire a second createChatAgent
        // for X while the first is still pending, leaking the loser's
        // runtime + child-registry subscriber.
        if (agentsRef.current.has(id) || agentBuildingRef.current.has(id)) {
            const raf = requestAnimationFrame(() => inputRef.current?.focus());
            return () => cancelAnimationFrame(raf);
        }
        // Claim the build synchronously (before any await below) so a
        // rapid X → Y → X switch can't fire two concurrent builds for X.
        // Note: we deliberately do NOT abandon an in-flight build when
        // the user switches away — the build always runs to completion
        // and installs the agent (guarded only by the chat-still-exists
        // check, as before). Bailing early here would strand the chat
        // agent-less: the re-activation would early-return on this same
        // agentBuildingRef guard, and the effect wouldn't re-fire.
        buildAgent(id);
        const raf = requestAnimationFrame(() => inputRef.current?.focus());
        return () => {
            cancelAnimationFrame(raf);
        };
    }, [open, activeId, hydrated, buildAgent]);

    // Esc closes the dialog — except when one of the inline slash picker is open, in which case Esc should
    // close just that popover. Refs let the window listener read the
    // latest state without resubscribing on every toggle.
    const slashOpenRef = useRef(false);
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                if (slashOpenRef.current) return;
                e.preventDefault();
                close();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, close]);

    // Auto-scroll transcript on new content — but only when the user
    // is already pinned to the bottom. The decision is made by
    // comparing the current scrollTop against the *previous* render's
    // scrollHeight: if the gap from the old bottom is small, the user
    // was stuck and the new content should slide into view; otherwise
    // they've scrolled up to read and we leave them alone.
    //
    // Using prev-vs-current geometry (rather than a scroll listener
    // ref) avoids a race during fast streaming where the scroll
    // event from a manual wheel-up fires *after* React's commit and
    // the auto-scroll has already slammed the viewport back to the
    // bottom. Tab switches are detected by activeId change and
    // always pin to the new chat's bottom.
    //
    // Only fires when scrollHeight actually grew (new content arrived).
    // Without that gate, *any* re-render (busy flag, hover state, sibling
    // state) where the user happens to be parked near the bottom yanks
    // their scrollTop back to the floor — a sticky cage even when nothing
    // is streaming.
    const prevScrollHeightRef = useRef(0);
    const lastActiveIdRef = useRef(activeId);
    const transcriptOsRef = useRef(null);
    // Force the OverlayScrollbars handle to re-sync against the viewport's
    // current geometry. Used after a programmatic scrollTop change so the
    // scrollbar bar doesn't lag the actual scroll position.
    const syncTranscriptScrollbar = () => {
        transcriptOsRef.current?.update(true);
    };
    useLayoutEffect(() => {
        const viewport = getOverlayViewport(transcriptHostRef.current);
        if (!viewport) return;
        const switchedTab = lastActiveIdRef.current !== activeId;
        lastActiveIdRef.current = activeId;
        if (switchedTab) {
            viewport.scrollTop = viewport.scrollHeight;
            prevScrollHeightRef.current = viewport.scrollHeight;
            syncTranscriptScrollbar();
            return;
        }
        const prevHeight = prevScrollHeightRef.current;
        const currHeight = viewport.scrollHeight;
        if (currHeight === prevHeight) return;
        const wasAtBottom =
            prevHeight === 0
            || prevHeight - viewport.scrollTop - viewport.clientHeight < 40;
        if (wasAtBottom) viewport.scrollTop = currHeight;
        prevScrollHeightRef.current = currHeight;
        syncTranscriptScrollbar();
    });

    // Drive a single agent invocation (prompt or continue) — owns the
    // subscribe lifecycle and busy/error transitions so submit and
    // retry share the exact same plumbing.
    //
    // Stall watchdog: any agent event resets a timer. If no event
    // arrives for STALL_THRESHOLD_MS, the chat's `stalledSince`
    // timestamp is set so the UI can surface a "no events in Xs —
    // retry?" affordance. The watchdog NEVER auto-aborts the run —
    // it only informs. The user still has to click Stop and retry
    // themselves. Common cause is a provider whose upstream stalled
    // mid-answer; the keepalive comment frames keep the SSE connection
    // alive but no real events flow. The stream runtime (piStreamFn.js)
    // abandons and retries such a call on its own after STREAM_IDLE_MS,
    // so this indicator is what the person sees when the retries are
    // stalling too.
    const runAgentCall = useCallback(async (
        id,
        agent,
        action,
        { onFinalize = null, analytics: turnProps = null } = {},
    ) => {
        let cleared = false;
        const turn = startChatTurn({
            chatId: id,
            trigger: turnProps?.trigger,
            stops: turnStopsRef.current,
            send: (properties) => analytics.capture(EVENT_NAME.CHAT_TURN, properties),
        });
        const watchdog = createStallWatchdog({
            thresholdMs: STALL_THRESHOLD_MS,
            onStall: (quietSince) => updateChat(id, (c) => (
                c.stalledSince ? c : { ...c, stalledSince: quietSince }
            )),
        });
        const clearStallState = () => {
            updateChat(id, (c) => (c.stalledSince ? { ...c, stalledSince: null } : c));
        };
        watchdog.arm();
        let off = null;
        let runStartTurns = 0;
        // Idempotent teardown. The normal path runs it from `finally`,
        // but a STALLED run is the problem case: when pi's abort can't
        // unstick a hung upstream/tool fetch, `action()` never settles,
        // so `finally` never fires and the UI stays wedged "busy" —
        // making every STOP button look dead. Registering finalize lets
        // the STOP handlers force the run closed (see abort /
        // stopAndRetry). Unsubscribing first means a late-settling
        // zombie request can't mutate the chat afterward.
        const finalize = () => {
            if (cleared) return;
            cleared = true;
            watchdog.stop();
            try { off?.(); } catch { /* noop */ }
            // Only clear our own registration — a newer run for this
            // chat may have already overwritten it.
            if (activeRunsRef.current.get(id)?.finalize === finalize) {
                activeRunsRef.current.delete(id);
            }
            try { onFinalize?.(); } catch { /* cleanup cannot block teardown */ }
            updateChat(id, (c) => (c.busy || c.stalledSince ? { ...c, busy: false, stalledSince: null } : c));
            // Here rather than after `action()`, so a stopped run that hung,
            // whose `finally` never fires, is still counted once.
            try { turn.end({ docked: dockedRef.current }); } catch { /* analytics cannot block teardown */ }
        };
        off = agent.subscribe((event) => {
            // Logged so devtools can tell us *why* a run feels stalled
            // (no events firing → upstream LLM/network; error events →
            // tool/validation failures).
            // eslint-disable-next-line no-console
            console.debug("[chat]", event.type, event);
            // Every real event from pi-agent-core resets the
            // watchdog and clears any prior stall flag. Keepalive
            // comments from the upstream provider don't surface as
            // agent events, so they correctly DON'T reset the timer
            // — that's the entire point of running this watchdog
            // alongside pi-agent-core's stream rather than at the
            // SSE layer.
            watchdog.arm();
            clearStallState();
            updateChat(id, (c) => {
                if (event.type === "agent_start") {
                    runStartTurns = c.items.filter((it) => it.kind === "turn").length;
                }
                let items = applyAgentEvent(c.items, event, runStartTurns);
                // Snapshot the full Pi message array on each turn
                // boundary. Stored on the turn node so "Fork from
                // here" can clone the conversation at that exact
                // point without re-running anything.
                if (event.type === "turn_end") {
                    const snapshot = transcriptOf(agent);
                    items = updateLastTurn(items, (t) => ({ ...t, snapshot }));
                }
                return { ...c, items };
            });
        });
        activeRunsRef.current.set(id, { agent, finalize });
        try {
            await action();
        } catch (err) {
            turn.failed(err);
            // Abort surfaces as an AbortError — that's user-driven, not
            // a failure, so don't sticky-pin it as an error banner.
            if (err?.name !== "AbortError") {
                updateChat(id, (c) => ({ ...c, error: err.message || String(err) }));
            }
        } finally {
            finalize();
        }
    }, [updateChat, analytics]);

    const runInteractiveAgentCall = useCallback(async (
        id,
        agent,
        triggerText,
        action,
        { trigger = null } = {},
    ) => {
        const context = withInteractiveConsent(
            LIVE_COMMAND_RUN_CONTEXT,
            { broker: runConsentBroker, triggerText },
        );
        interactiveAgentRunContextsRef.current.set(id, context);
        const clearContext = () => {
            if (interactiveAgentRunContextsRef.current.get(id) === context) {
                interactiveAgentRunContextsRef.current.delete(id);
            }
        };
        try {
            const turnTrigger = chatTurnTrigger(triggerText, trigger);
            await runAgentCall(id, agent, action, { onFinalize: clearContext, analytics: { trigger: turnTrigger } });
        } finally {
            clearContext();
        }
    }, [runAgentCall, runConsentBroker]);

    const kickOffAudit = useCallback((targetChatId) => {
        const id = activeIdRef.current;
        const agent = agentsRef.current.get(id);
        if (!agent) {
            updateChat(id, (c) => ({
                ...c,
                error: "Agent unavailable. Check AI Governance.",
            }));
            return;
        }
        const pendingId = `audit:${Date.now()}`;
        const targetLabel = String(targetChatId || id);
        updateChat(id, (c) => ({
            ...c,
            input: "",
            error: null,
            items: [
                ...c.items,
                { kind: "user", text: targetLabel === String(id) ? "/audit" : `/audit ${targetLabel}` },
                {
                    kind: "audit",
                    id: pendingId,
                    targetChatId: targetLabel,
                    status: "running",
                    slot: { status: "running", activity: "starting…", tools: [], preview: "", messages: [] },
                    abort: null,
                    error: null,
                    result: null,
                },
            ],
        }));

        // Cross-chat audit reads transcripts of arbitrary chats through
        // the registry (listChatSummaries / getChatById). Load every
        // chat's body first — lazy bodies included — so the auditor never
        // reads an empty placeholder transcript for a chat the user
        // hasn't opened this session. The placeholder item above already
        // gave immediate feedback; the agent just starts a beat later.
        ensureAllBodiesLoaded().then(() => {
            const handle = startChatAudit({
                targetChatId: targetLabel,
                parentTools: agent.state?.tools || [],
                thinkingLevel: agent.state?.thinkingLevel,
                onState: (slot) => {
                    updateChat(id, (c) => ({
                        ...c,
                        items: c.items.map((it) =>
                            it.kind === "audit" && it.id === pendingId
                                ? { ...it, slot }
                                : it,
                        ),
                    }));
                },
            });

            updateChat(id, (c) => ({
                ...c,
                items: c.items.map((it) =>
                    it.kind === "audit" && it.id === pendingId
                        ? { ...it, abort: handle.abort }
                        : it,
                ),
            }));

            handle.promise.then((res) => {
                updateChat(id, (c) => ({
                    ...c,
                    items: c.items.map((it) => {
                        if (it.kind !== "audit" || it.id !== pendingId) return it;
                        if (res.ok) {
                            return { ...it, status: "done", abort: null, result: res.result, error: null };
                        }
                        return { ...it, status: "error", abort: null, error: res.error || "audit failed" };
                    }),
                }));
            });
        });
    }, [updateChat, ensureAllBodiesLoaded]);

    // Latest-ref to runPromptInNewChat (defined below) so submit can route
    // scout-tab follow-ups to a fresh chat without a forward-reference or a
    // dependency cycle on the callback.
    const runPromptInNewChatRef = useRef(null);

    // Give a server-restored chat the conversation the model last saw, once,
    // immediately before its first send.
    //
    // Deferred to send rather than done at open for two reasons: opening a chat
    // to read it should not touch the conversation store at all, and the read is
    // only meaningful once - after this the live `agent.state.messages` is the
    // truth and re-reading would clobber the turns since appended.
    //
    // Never throws and never blocks the send: `loadContinuationContext` falls
    // back to a transcript-only history when capture has not landed, so the
    // worst case is a model with weaker memory of its own earlier turns, not a
    // chat that refuses to continue.
    const ensureContinuationContext = useCallback(async (id, chat, agent) => {
        if (!claimContinuationRebuild(needsContinuationRef.current, id)) return;
        const result = await installContinuationContext({
            agent,
            serverChatId: chat?.serverChatId,
            items: chat?.items || [],
            // Matches piChat's getNamespaceKey, so the seeded markers land in
            // the store this chat's tools actually read.
            namespaceKey: String(id),
            deps: { getChatContinuation },
        });
        if (continuationIsDegraded(result)) {
            console.info("[chat] continued with reduced context", {
                chat: id,
                source: result.source,
                truncated: result.truncated,
                tailAligned: result.tailAligned,
            });
        }
    }, []);

    const submit = useCallback(async (modeOrOpts = "default") => {
        // Back-compat: legacy callers pass the mode string directly;
        // newer callers pass `{ mode, explicitPrompt }`. The explicit
        // prompt path is for the slash picker's Enter handler, which
        // can't rely on chatsRef having the just-typed completion
        // (state ref updates after render).
        const opts = typeof modeOrOpts === "string" ? { mode: modeOrOpts } : (modeOrOpts || {});
        const mode = opts.mode || "default";
        // A message sent from outside the composer (a reply typed on a claim's
        // card over Lanes) leaves the composer's own draft and staged images
        // where they were, and is prose for the model, never a command. It
        // names the chat it goes to, which need not be the one on screen, and
        // carries its reply tag into the transcript so the card can follow it.
        const keepDraft = opts.keepDraft === true;
        const id = opts.chatId ?? activeIdRef.current;
        const tag = opts.graphReply ? { graphReply: opts.graphReply } : null;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat) return;
        let prompt = (opts.explicitPrompt != null ? opts.explicitPrompt : chat.input).trim();
        // Work staged here to be steered starts on the person's word, steered
        // by the composer's lines; what goes out is the prompt its surface
        // makes of them. Only once the chat can send it: work started from a
        // chat still loading would have no chat to report to.
        if (chat.steering && !chat.busy && !keepDraft) {
            if (!agentsRef.current.get(id)) return;
            prompt = triageHostRef.current.begin({ chatId: id }, prompt) || prompt;
        }
        const staged = keepDraft ? [] : (chat.staged || []);
        // What the reader added from the workspace goes with the words the
        // composer sends, as the staged images do; a reply sent from
        // elsewhere brings its own (`opts.added`: the trace a note it answers
        // is on) and leaves the tray as it was.
        const fromTray = !keepDraft;
        const added = fromTray ? chatTray(`console:${id}`) : (Array.isArray(opts.added) ? opts.added : []);
        // Empty prompt is allowed if at least one image is staged —
        // "look at this" with no caption is a valid use case. So is one with
        // things added from the workspace: it asks about them.
        if (!prompt && staged.length === 0 && added.length === 0) return;
        const withAdded = addedToSend(prompt, added);
        prompt = withAdded.words;
        // What it adds counts toward what the chat is about from now on.
        const keepSent = (sent) => sentAddedRef.current.set(id, [...(sentAddedRef.current.get(id) || []), ...addedOf(sent)]);
        // A scout (tenant-profile) tab is a result view — its transcript
        // isn't rendered, so a message typed here would silently vanish.
        // Route any follow-up to a fresh chat (the same destination a
        // tailored card opens), keeping the profile tab pristine.
        if (chat.kind === "scout" && prompt) {
            updateChat(id, (c) => ({ ...c, input: "" }));
            runPromptInNewChatRef.current?.(prompt, { skipScope: chat.skipScope });
            return;
        }
        const agent = agentsRef.current.get(id);
        if (!agent) {
            updateChat(id, (c) => ({
                ...c,
                error: "Agent unavailable. Check AI Governance.",
            }));
            return;
        }

        // Slash commands — built-ins run locally without an LLM
        // round-trip; user-saved prompts expand into an LLM turn;
        // Both flow through findSlashCommand → {command, args}.
        const slash = !chat.busy && !keepDraft ? findSlashCommand(prompt) : null;
        if (slash) {
            // User-defined slash command — expand body + args into a
            // normal LLM turn. Falls through to the chat-submit path
            // below by overwriting `prompt`.
            if (slash.command.userPrompt) {
                const body = slash.command.userPrompt.body;
                prompt = slash.args ? `${body}\n\n${slash.args}` : body;
            } else {
                const newItems = [];
                const append = (item) => {
                    newItems.push({ id: `system:${Date.now()}:${newItems.length}`, ...item });
                };
                let pendingResult = null;
                try {
                    pendingResult = slash.command.run({
                        agent,
                        append,
                        args: slash.args,
                        chat,
                        getAllChats: () => chatsRef.current,
                        // Load every chat's transcript (lazy bodies
                        // included) so cross-chat exports aren't blank
                        // for chats the user never opened this session.
                        collectChats: () => collectChatsForExport(),
                        // Append a system line to THIS chat after the
                        // run returns — for handlers whose result is only
                        // known asynchronously (e.g. /export-audits must
                        // load lazy bodies before it can count audits).
                        // `append` can't be used post-return: its items
                        // are spread into the chat synchronously below.
                        notify: (item) => updateChat(id, (c) => ({
                            ...c,
                            items: [...c.items, { id: `system:${Date.now()}:${c.items.length}`, ...item }],
                        })),
                    });
                } catch (err) {
                    updateChat(id, (c) => ({ ...c, error: err.message || String(err) }));
                    return;
                }

                if (pendingResult?.pending?.kind === "audit") {
                    const { targetChatId } = pendingResult.pending;
                    updateChat(id, (c) => ({
                        ...c,
                        input: "",
                        error: null,
                        items: [...c.items, ...newItems],
                    }));
                    kickOffAudit(targetChatId);
                    return;
                }

                updateChat(id, (c) => ({
                    ...c,
                    input: "",
                    error: null,
                    items: [...c.items, ...newItems],
                }));
                return;
            }
        }

        // Steer (default) or follow-up: a run is already in flight.
        //   steer    → injected at the next turn boundary (interrupts)
        //   followup → injected only after the agent would have stopped
        // Either way we render the message inline with a badge so the
        // user can see it's pending pickup. Don't kick off a new run.
        if (chat.busy) {
            const queueMode = mode === "followup" ? "post" : "mid";
            try {
                // Pi's steer/followUp take a single AgentMessage;
                // build a multi-content user message so staged
                // images come along for the ride.
                const said = withAdded.queued;
                const content = staged.length
                    ? [
                          { type: "text", text: said },
                          ...staged.map((s) => ({ type: "image", data: s.data, mimeType: s.mimeType })),
                      ]
                    : said;
                const msg = { role: "user", content, timestamp: Date.now() };
                if (queueMode === "post") {
                    agent.followUp(msg);
                } else {
                    agent.steer(msg);
                }
            } catch (err) {
                updateChat(id, (c) => ({ ...c, error: err.message || String(err) }));
                return;
            }
            if (added.length) keepSent(fromTray ? takeChatTray(`console:${id}`) : added);
            updateChat(id, (c) => ({
                ...c,
                updatedAt: Date.now(),
                ...composerAfterSend(c, keepDraft),
                items: [
                    ...c.items,
                    { kind: "user", text: prompt, queued: queueMode, images: staged, ...tag, ...(added.length ? { added: addedOf(added) } : {}) },
                ],
            }));
            return;
        }

        const isFirstPrompt = chat.items.length === 0;
        // Resolve whether to send scope this turn, and (on the
        // first prompt only) lock the chat into "include" / "exclude"
        // so the chip — and future injections — behave consistently
        // for the rest of the conversation.
        const dropScope = chat.scopeMode === "exclude"
            || (chat.scopeMode === null && chat.skipScope);
        const nextScopeMode = chat.scopeMode === null
            ? (dropScope ? "exclude" : "include")
            : chat.scopeMode;
        if (added.length) keepSent(fromTray ? takeChatTray(`console:${id}`) : added);
        // The walkthroughs the reader ran from the board's bar in this chat
        // that the chat has not been told, told once with this message.
        const walks = untoldWalks(chat.items);
        const told = new Set(walks.ids);
        updateChat(id, (c) => ({
            ...c,
            ...composerAfterSend(c, keepDraft),
            items: [
                ...(told.size ? c.items.map((it) => (it?.kind === "job" && told.has(it.id) ? { ...it, told: true } : it)) : c.items),
                { kind: "user", text: prompt, images: staged, ...tag, ...(added.length ? { added: addedOf(added) } : {}) },
            ],
            busy: true,
            error: null,
            pendingContext: null,
            title: isFirstPrompt && prompt ? titleFromPrompt(prompt) : c.title,
            scopeMode: nextScopeMode,
            // Last-activity stamp for the sidebar's relative time. Without
            // it, a reused blank draft keeps the updatedAt it was seeded
            // with at restore — its old savedAt — so a chat you just typed
            // into can show hours-old ("19h ago") until the next reload.
            updatedAt: Date.now(),
        }));

        // Inject a fresh dashboard-context notification ahead of every
        // user turn. piChat.js's convertToLlm wraps these in a
        // <dashboard_context> block so the model treats them as
        // ambient, not as something the user typed. The notification
        // is invisible in our transcript tree (we only render `user`
        // and `turn` kinds at the top level).
        const rawCtx = buildDashboardContext(searchParams);
        const ctxForLlm = dropScope ? { ...rawCtx, scope: [] } : rawCtx;
        const ctxText = stringifyDashboardContext(ctxForLlm);
        // What the conversation is about and how to read it, as its tools
        // will be (lib/chatSubject.js): `chat` is the pre-append snapshot,
        // and what this message adds is already kept as sent.
        const subjectText = describeSubject(chatSubject(subjectItems(chat.items, sentAddedRef.current.get(id)), { board: openBoardTraces() }), { walkHeld: openBoardWalkHeld() });
        const images = staged.map((s) => ({ type: "image", data: s.data, mimeType: s.mimeType }));
        const userContent = images.length
            ? [{ type: "text", text: prompt }, ...images]
            : prompt;
        // What another surface staged for this chat (the case an
        // investigation opened on) rides ahead of the question once; `chat`
        // is the pre-append snapshot, so it still holds it here.
        const pending = chat.pendingContext?.text
            ? { role: "notification", text: String(chat.pendingContext.text), timestamp: Date.now() }
            : null;
        // What the reader added from the workspace, ahead of their words as
        // what they are about.
        const about = withAdded.about ? { role: "notification", text: withAdded.about, timestamp: Date.now() } : null;
        const promptInput = ctxText || subjectText || walks.text || pending || about
            ? [
                  ...(ctxText ? [{ role: "notification", text: ctxText, timestamp: Date.now() }] : []),
                  ...(subjectText ? [{ role: "notification", text: subjectText, timestamp: Date.now() }] : []),
                  ...(walks.text ? [{ role: "notification", text: walks.text, timestamp: Date.now() }] : []),
                  ...(pending ? [pending] : []),
                  ...(about ? [about] : []),
                  { role: "user", content: userContent, timestamp: Date.now() },
              ]
            : images.length
                ? { role: "user", content: userContent, timestamp: Date.now() }
                : prompt;
        // `chat` is the pre-append snapshot, so the reconstruction sees the
        // transcript WITHOUT the prompt being sent - which is exactly what the
        // model's prior context is.
        await ensureContinuationContext(id, chat, agent);
        await runInteractiveAgentCall(
            id,
            agent,
            prompt,
            () => agent.prompt(promptInput),
        );
    }, [updateChat, runInteractiveAgentCall, searchParams, kickOffAudit, caps, runConsentBroker, ensureContinuationContext]);

    // A reply typed on a claim's card over the Lanes view goes to the chat whose
    // graph made the claim, as the reader's own message in that chat, and its
    // progress goes back to the card (graphReplies), so the answer is read on
    // the trace. Nothing is brought forward: a chat without an agent yet (not
    // opened since a reload) builds one in the background, and a chat that is
    // running finishes that run first, so the turns after the reply are its
    // answer. One reply per chat at a time, in the order they were typed. The
    // composer's own draft is never touched; a reply whose chat cannot start
    // in time is left in that chat's composer rather than lost.
    const pendingRepliesRef = useRef([]);
    const [replyTick, setReplyTick] = useState(0);
    useEffect(() => {
        const onReply = (e) => {
            const detail = e?.detail;
            if (!detail?.text || detail.graphKey == null) return;
            const id = nextReplyId();
            const reply = {
                id,
                text: detail.text,
                words: detail.words || detail.text,
                claimIds: Array.isArray(detail.claimIds) ? detail.claimIds.map(String) : [],
                at: Date.now(),
                ...(Array.isArray(detail.added) && detail.added.length ? { added: detail.added } : {}),
            };
            detail.replyId = id;
            const chat = chatForGraphKey(chatsRef.current, detail.graphKey);
            const shown = { id, text: reply.words, claimIds: reply.claimIds, at: reply.at };
            if (!chat) {
                detail.result = "missing";
                graphReplies.upsert(detail.graphKey, { ...shown, state: "missing" });
                return;
            }
            detail.result = "queued";
            pendingRepliesRef.current.push({ ...reply, chatId: chat.id, graphKey: String(detail.graphKey), buildTried: false });
            graphReplies.upsert(detail.graphKey, { ...shown, state: "waiting", activity: null });
            setReplyTick((n) => n + 1);
        };
        window.addEventListener("origin-chat-reply", onReply);
        return () => window.removeEventListener("origin-chat-reply", onReply);
    }, []);
    useEffect(() => {
        if (!pendingRepliesRef.current.length) return undefined;
        const step = () => {
            const { keep, actions } = planReplyQueue(pendingRepliesRef.current, {
                chatOf: (id) => chatsRef.current.find((c) => c.id === id) || null,
                agentReady: (id) => agentsRef.current.has(id),
                building: (id) => agentBuildingRef.current.has(id),
            });
            pendingRepliesRef.current = keep;
            for (const a of actions) {
                const r = a.reply;
                if (a.type === "missing") graphReplies.upsert(r.graphKey, { id: r.id, state: "missing" });
                else if (a.type === "leave") {
                    updateChat(r.chatId, (c) => ({ ...c, input: c.input ? `${c.input}\n\n${r.text}` : r.text }));
                    graphReplies.upsert(r.graphKey, { id: r.id, state: "left", error: a.error });
                } else if (a.type === "build") buildAgent(r.chatId);
                else if (a.type === "send") submit(replySubmitOptions(a.chat, r));
                else graphReplies.upsert(r.graphKey, { id: r.id, state: "waiting", activity: a.activity });
            }
            return keep.length === 0;
        };
        if (step()) return undefined;
        const timer = setInterval(() => { if (step()) clearInterval(timer); }, 400);
        return () => clearInterval(timer);
    }, [replyTick, submit, buildAgent, updateChat]);
    // A card reading a chat's graph shows the replies already in that chat's
    // transcript, so a chat restored without its body (not opened since a
    // reload) loads it as soon as a card watches, not only once the reader
    // sends something. Loading the body builds no agent.
    const [watchTick, setWatchTick] = useState(0);
    useEffect(() => graphReplies.onWatch(() => setWatchTick((n) => n + 1)), []);
    const chatCount = chats.length;
    useEffect(() => {
        if (!hydrated) return;
        for (const id of watchedBodyLoads(graphReplies.watchedKeys(), chatsRef.current, needsBodyLoadRef.current)) {
            loadChatBodyIntoState(id).catch(() => {});
        }
    }, [watchTick, hydrated, chatCount, loadChatBodyIntoState]);
    // What each reply in a transcript has come to, published for its card. A
    // chat the page has not changed since the last pass is skipped.
    const repliesSeenRef = useRef(new WeakSet());
    useEffect(() => {
        for (const chat of chats) {
            if (repliesSeenRef.current.has(chat)) continue;
            repliesSeenRef.current.add(chat);
            const key = graphChatIdFor(chat, chat.id);
            for (const it of chat.items || []) {
                const tag = it?.kind === "user" ? it.graphReply : null;
                if (!tag?.id) continue;
                const progress = replyProgress(chat, tag.id);
                if (progress) graphReplies.upsert(key, { id: tag.id, text: tag.words || it.text, claimIds: tag.claimIds || [], at: tag.at ?? null, ...progress });
            }
        }
    }, [chats]);

    // Resume from the current transcript after an error — `continue()`
    // re-runs the last user/toolResult turn without adding a new
    // message. Useful for transient LLM failures and tool-arg validation
    // mismatches the agent can self-correct on retry.
    const retry = useCallback(async () => {
        const id = activeIdRef.current;
        const agent = agentsRef.current.get(id);
        if (!agent) return;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat || chat.busy) return;
        updateChat(id, (c) => ({ ...c, busy: true, error: null }));
        const triggerText = [...(chat.items || [])]
            .reverse()
            .find((item) => item?.kind === "user")?.text || "Retry the previous request";
        await runInteractiveAgentCall(
            id,
            agent,
            triggerText,
            () => agent.continue(),
        );
    }, [updateChat, runInteractiveAgentCall]);

    // Replace a user turn with a new prompt and rerun from that
    // point. Truncates the visible transcript and rewinds
    // agent.state.messages to the snapshot captured at the previous
    // turn boundary (or empty for the first user turn), so the LLM
    // sees the new prompt with the same history as the original.
    const editAndRerun = useCallback(async (userIdx, newText) => {
        const text = newText.trim();
        if (!text) return;
        const id = activeIdRef.current;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat || chat.busy) return;
        if (chat.items[userIdx]?.kind !== "user") return;
        const agent = agentsRef.current.get(id);
        if (!agent) {
            updateChat(id, (c) => ({
                ...c,
                error: "Agent unavailable. Check AI Governance.",
            }));
            return;
        }
        // Find the most recent turn snapshot before this user item —
        // that's the LLM state we want to rewind to. If none, start
        // from an empty transcript.
        setTranscript(agent, snapshotBeforeUserIdx(chat.items, userIdx));
        updateChat(id, (c) => ({
            ...c,
            items: [...c.items.slice(0, userIdx), { kind: "user", text }],
            busy: true,
            error: null,
        }));
        const rawCtx = buildDashboardContext(searchParams);
        const chatNow = chatsRef.current.find((c) => c.id === id);
        // editAndRerun replays an existing user turn, so scopeMode
        // is already locked. Fall back to skipScope only if we
        // somehow get called pre-lock (e.g. snapshot replay tests).
        const dropScope = chatNow?.scopeMode === "exclude"
            || (chatNow?.scopeMode === null && chatNow?.skipScope);
        const ctxForLlm = dropScope ? { ...rawCtx, scope: [] } : rawCtx;
        const ctxText = stringifyDashboardContext(ctxForLlm);
        const promptInput = ctxText
            ? [
                  { role: "notification", text: ctxText, timestamp: Date.now() },
                  { role: "user", content: text, timestamp: Date.now() },
              ]
            : text;
        // Same precondition reset as submit() — editAndRerun rewinds
        // the agent's message history and starts a fresh analysis.
        await runInteractiveAgentCall(
            id,
            agent,
            text,
            () => agent.prompt(promptInput),
        );
    }, [updateChat, runInteractiveAgentCall, searchParams]);

    const rewindToUser = useCallback((userIdx) => {
        const id = activeIdRef.current;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat || chat.busy) return;
        const item = chat.items[userIdx];
        if (item?.kind !== "user") return;
        const agent = agentsRef.current.get(id);
        if (agent) setTranscript(agent, snapshotBeforeUserIdx(chat.items, userIdx));
        const text = typeof item.text === "string" ? item.text : "";
        updateChat(id, (c) => ({
            ...c,
            items: c.items.slice(0, userIdx),
            input: text,
            error: null,
        }));
        requestAnimationFrame(() => inputRef.current?.focus());
    }, [updateChat]);

    // Fork to a new tab from just before the user item at `userIdx`,
    // leaving the original thread (and all its downstream turns) intact.
    // Mirrors forkFromTurn's snapshot/clone plumbing, but anchored at a
    // user message and rewound to BEFORE it so the new tab is a clean
    // retry surface. Non-destructive sibling of rewindToUser.
    const forkFromUser = useCallback((userIdx) => {
        const id = activeIdRef.current;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat) return;
        const item = chat.items[userIdx];
        if (item?.kind !== "user") return;
        const newId = nextChatIdRef.current++;
        pendingForkSnapshotRef.current.set(newId, snapshotBeforeUserIdx(chat.items, userIdx));
        cloneStore(String(id), String(newId));
        cloneCanvasStore(id, newId);
        const text = typeof item.text === "string" ? item.text : "";
        setChats((cur) => [
            ...cur,
            {
                ...makeChat(newId),
                title: chat.title === "New chat" ? "Fork" : `${chat.title} (fork)`,
                items: chat.items.slice(0, userIdx),
                input: text,
                thinkingLevel: chat.thinkingLevel,
            },
        ]);
        setActiveId(newId);
        requestAnimationFrame(() => inputRef.current?.focus());
    }, []);

    // Rewind to (and keeping) an agent turn: drop everything after it
    // and restore agent.state.messages to that turn's end-of-turn
    // snapshot, so the conversation reads exactly as it did right after
    // the agent responded — a clean point to type a different follow-up.
    // The turn-level analog of rewindToUser; non-destructive sibling is
    // forkFromTurn. No re-run.
    const rewindToTurn = useCallback((turnId) => {
        const id = activeIdRef.current;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat || chat.busy) return;
        const idx = chat.items.findIndex((it) => it.kind === "turn" && it.id === turnId);
        if (idx < 0) return;
        const turn = chat.items[idx];
        if (!turn.snapshot) return; // turn hasn't completed yet
        const agent = agentsRef.current.get(id);
        if (agent) setTranscript(agent, turn.snapshot);
        updateChat(id, (c) => ({
            ...c,
            items: c.items.slice(0, idx + 1),
            error: null,
        }));
        requestAnimationFrame(() => inputRef.current?.focus());
    }, [updateChat]);

    // Replay an agent turn: re-run the user prompt that drove it to get
    // a fresh response (the turn and everything after it are discarded
    // and regenerated). Delegates to editAndRerun with the unchanged
    // prompt text — that already rewinds to the pre-prompt snapshot,
    // truncates, and re-prompts. The driving prompt is the nearest
    // non-queued user item before the turn (queued steers belong to the
    // same turn, so skip them).
    const replayTurn = useCallback((turnId) => {
        const id = activeIdRef.current;
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat || chat.busy) return;
        const idx = chat.items.findIndex((it) => it.kind === "turn" && it.id === turnId);
        if (idx < 0) return;
        let userIdx = -1;
        for (let i = idx - 1; i >= 0; i--) {
            const it = chat.items[i];
            if (it?.kind === "user" && !it.queued) { userIdx = i; break; }
        }
        if (userIdx < 0) return; // no prompt to replay (e.g. an opening agent message)
        const text = typeof chat.items[userIdx].text === "string" ? chat.items[userIdx].text : "";
        if (!text.trim()) return;
        editAndRerun(userIdx, text);
    }, [editAndRerun]);

    // Aborts the in-flight run. Pi normally resolves the awaited
    // prompt() with an AbortError, which runAgentCall swallows silently.
    // But when the run is STALLED on a hung upstream/tool fetch, pi's
    // abort can't unstick the awaited promise — so it never settles and
    // runAgentCall's finally never fires, leaving the chat stuck "busy"
    // and STOP looking dead. So after signalling abort we ALSO force the
    // run's teardown directly: unsubscribe + clear busy. The abandoned
    // request can't corrupt the chat — its event subscription is gone,
    // and any restart rewinds agent.state.messages to a clean snapshot.
    const abort = useCallback(() => {
        const id = activeIdRef.current;
        turnStopsRef.current.stop(id);
        try { agentsRef.current.get(id)?.abort(); } catch { /* noop */ }
        activeRunsRef.current.get(id)?.finalize?.();
    }, []);

    // Stop the current run AND replay the prompt that started it —
    // the composite action behind the stall banner's button.
    //
    // First attempt used `retry()` (which wraps pi-agent-core's
    // `agent.continue()`) but that's the wrong tool here:
    // `continue()` resumes from the existing message history, and
    // when an abort fires mid-stream the last assistant message has
    // an incomplete tool_call (the args were still streaming). The
    // LLM either rejects the malformed state or returns nothing.
    //
    // The correct shape is editAndRerun's rewind: find the last user
    // message, restore the agent's message history to the snapshot
    // BEFORE that user turn, truncate chat items, re-prompt with the
    // same text. That gives the LLM a clean state to start over from.
    // editAndRerun already implements this — we just locate the
    // anchor and delegate.
    const stopAndRetry = useCallback(async () => {
        const id = activeIdRef.current;
        // Force the run closed (same path as `abort`) so this works even
        // on a stalled run pi's abort can't unstick — otherwise busy
        // never clears and editAndRerun bails on its `chat.busy` guard.
        turnStopsRef.current.stop(id);
        try { agentsRef.current.get(id)?.abort(); } catch { /* noop */ }
        activeRunsRef.current.get(id)?.finalize?.();
        // finalize clears busy via updateChat; wait for that to land in
        // chatsRef before re-prompting. Bounded so a stuck cleanup never
        // wedges the button.
        const deadline = Date.now() + 3000;
        while (Date.now() < deadline) {
            if (!chatsRef.current.find((c) => c.id === id)?.busy) break;
            await new Promise((r) => setTimeout(r, 30));
        }
        const chat = chatsRef.current.find((c) => c.id === id);
        if (!chat) return;
        let userIdx = -1;
        for (let i = chat.items.length - 1; i >= 0; i--) {
            if (chat.items[i]?.kind === "user" && !chat.items[i]?.queued) { userIdx = i; break; }
        }
        if (userIdx < 0) return;
        const text = typeof chat.items[userIdx]?.text === "string" ? chat.items[userIdx].text : "";
        if (!text.trim()) return;
        await editAndRerun(userIdx, text);
    }, [editAndRerun]);

    // Bound every image at the door (prepareImage: resize, re-encode, refuse).
    //
    // Staging an oversized image is not a recoverable mistake: it lands in
    // `agent.state.messages` and rides every later request, so the provider
    // rejects the chat from then on and only a rewind clears it. Refusing here
    // costs the user one paste; admitting it costs them the conversation.
    const stageImageFiles = useCallback(async (files) => {
        const list = [...files].filter((f) => f && f.type?.startsWith("image/"));
        if (!list.length) return;
        const id = activeIdRef.current;
        const staged = [];
        const refused = [];
        for (const file of list) {
            try {
                staged.push(await prepareImage(file));
            } catch (err) {
                refused.push(err?.message || "image could not be read");
            }
        }
        updateChat(id, (c) => ({
            ...c,
            staged: staged.length ? [...c.staged, ...staged] : c.staged,
            // One bad file in a multi-paste must not discard the good ones.
            error: refused.length ? refused.join(" ") : c.error,
        }));
    }, [updateChat]);

    const removeStagedImage = useCallback((imgId) => {
        const id = activeIdRef.current;
        updateChat(id, (c) => ({
            ...c,
            staged: c.staged.filter((s) => s.id !== imgId),
        }));
    }, [updateChat]);

    const onInputPaste = useCallback((e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        const files = [];
        for (const it of items) {
            if (it.kind === "file" && it.type?.startsWith("image/")) {
                const f = it.getAsFile();
                if (f) files.push(f);
            }
        }
        if (files.length) {
            e.preventDefault();
            stageImageFiles(files);
        }
    }, [stageImageFiles]);

    const onDialogDrop = useCallback((e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        stageImageFiles(e.dataTransfer.files);
    }, [stageImageFiles]);

    const setThinkingLevel = useCallback((level) => {
        const id = activeIdRef.current;
        updateChat(id, (c) => ({ ...c, thinkingLevel: level }));
        const agent = agentsRef.current.get(id);
        if (agent) agent.state.thinkingLevel = level;
    }, [updateChat]);

    // Load the active model summary for the footer pill. Refreshed
    // by Settings changes after the model or provider is saved. The
    // `configured` flag drives the first-run takeover view: when
    // there's no API key, the dialog body is replaced with the
    // inline setup panel instead of the empty transcript.
    const refreshCredsSummary = useCallback(() => {
        getChatCreds()
            .then((creds) => {
                setCredsSummary({
                    provider: creds.provider || "",
                    model: creds.model || "",
                    // `configured` drives the first-run takeover view.
                    // After O-893 it's "is there a stored key on the
                    // backend" rather than "is there a key in this
                    // browser" — but the consumer-side semantics are
                    // identical. A managed-default tenant has no
                    // stored key yet chat is usable on Origin's key, so
                    // `hasApiKey || managedDefault` is configured —
                    // see isChatConfigured for the canonical contract.
                    configured: isChatConfigured(creds),
                    // Config now known — release the composer's load gate.
                    loading: false,
                });
            })
            // Even on a failed fetch, drop the load gate: the agent
            // build will surface the real error (getChatCreds throws
            // there too) and the agent-ready gate keeps the composer
            // disabled, so we don't want to also hang it on `loading`.
            .catch(() => {
                setCredsSummary((prev) => ({ ...prev, loading: false }));
            });
    }, []);
    useEffect(() => { if (engaged) refreshCredsSummary(); }, [engaged, refreshCredsSummary]);

    // First-ever open in this browser lands the user in the chat. localStorage
    // (not sessionStorage) so a shared deep link opened in a fresh tab doesn't
    // pop the overlay over the linked page; clearChatData re-arms it.
    useEffect(() => {
        if (openPageRef.current) return;
        try {
            const mode = searchParams.get("mode");
            if (mode === "triage" || ((!mode || mode === "explore") && searchParams.get("view") === "scope.reviews")) return;
            if (localStorage.getItem("chatAutoOpened") === "1") return;
            localStorage.setItem("chatAutoOpened", "1");
        } catch {}
        setOpen(true);
    }, []);

    // Rebuild a cached chat agent after a settings change.
    // Preserves the in-memory transcript so the new agent picks up
    // where the old one left off; the prior LLM context (compaction
    // state, queued steers) belongs to the agent loop so we don't
    // try to migrate it.
    // Rows the harness adds after a run (a recipe saved at run end) go
    // through the same reducer as the agent's own tool events, for whichever
    // chat the agent was created for.
    const harnessEventHook = useCallback((chatKey) => (event) => {
        if (event?.type === "harness_continue") {
            runHarnessContinue({
                event,
                getChat: () => chatsRef.current.find((c) => c.id === chatKey),
                getAgent: () => agentsRef.current.get(chatKey),
                updateChat: (fn) => updateChat(chatKey, fn),
                runTurn: (agent, messages, reason) => runInteractiveAgentCall(chatKey, agent, reason, () => agent.prompt(messages), { trigger: CHAT_TRIGGER.CONTINUATION }),
            });
            return;
        }
        updateChat(chatKey, (c) => applyHarnessNoteToChat(c, event, applyAgentEvent));
    }, [updateChat, runInteractiveAgentCall]);
    const reloadAgentForActive = useCallback(async (id = activeIdRef.current) => {
        if (agentBuildingRef.current.has(id) || chatsRef.current.find((chat) => chat.id === id)?.busy) return;
        agentBuildingRef.current.add(id);
        const prev = agentsRef.current.get(id);
        const messages = prev?.state?.messages ? transcriptOf(prev) : null;
        teardownChildRegistrySync(id);
        try { agentsRef.current.get(id)?.releaseGraph?.(); } catch { /* release is best-effort */ }
        agentsRef.current.delete(id);
        // Agent gone until the rebuild below installs the replacement —
        // disable the composer for that window.
        setAgentReadyEpoch((n) => n + 1);
        updateChat(id, (c) => ({ ...c, error: null }));
        // Sub-agent runtimes cache the resolved model + headers, so a
        // creds change (primary OR task model swap, key rotation)
        // would otherwise keep firing against the stale runtime until
        // a page refresh. Drop both caches so the next sub-agent run
        // rebuilds from the new creds.
        try {
            const { resetSubAgentRuntimes } = await import("../lib/subAgent.js");
            resetSubAgentRuntimes();
        } catch { /* noop */ }
        try {
            const agent = await createChatAgent({
                chatId: String(id),
                ...chatToolOptions(id, { host: triageHostRef.current, chatsRef, sent: sentAddedRef.current }),
                onHarnessEvent: harnessEventHook(id),
                serverChatId: (chatsRef.current || []).find((c) => c.id === id)?.serverChatId || null,
                getServerChatId: () => (chatsRef.current || []).find((c) => c.id === id)?.serverChatId || null,
                runContext: LIVE_COMMAND_RUN_CONTEXT,
                getRunContext: () => (
                    interactiveAgentRunContextsRef.current.get(id)
                    || LIVE_COMMAND_RUN_CONTEXT
                ),
            });
            const chat = chatsRef.current.find((c) => c.id === id);
            agent.state.thinkingLevel = resolveThinkingLevel(
                agent.state.model?.provider, agent.state.model?.id, chat?.thinkingLevel);
            if (messages) setTranscript(agent, messages);
            agentsRef.current.set(id, agent);
            setAgentReadyEpoch((n) => n + 1);
            wireChildRegistrySync(id, agent);
        } catch (err) {
            updateChat(id, (c) => ({ ...c, error: err.message || String(err) }));
        } finally {
            agentBuildingRef.current.delete(id);
            setAgentReadyEpoch((n) => n + 1);
        }
    }, [updateChat, wireChildRegistrySync, teardownChildRegistrySync]);

    // Build a chat's agent imperatively, OUTSIDE the activation effect,
    // so a chat can run without being the foreground tab. Mirrors the
    // build effect's essentials for a fresh chat (no hydration). Claims
    // agentBuildingRef synchronously so a concurrent activation early-
    // returns instead of double-building; if a build is already in
    // flight (e.g. the activation effect won the race), waits for it.
    const ensureAgentFor = useCallback(async (id) => {
        if (agentsRef.current.has(id)) return agentsRef.current.get(id);
        if (agentBuildingRef.current.has(id)) {
            for (let i = 0; i < 240 && !agentsRef.current.has(id); i++) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 25));
            }
            return agentsRef.current.get(id) || null;
        }
        agentBuildingRef.current.add(id);
        try {
            const agent = await createChatAgent({
                chatId: String(id),
                ...chatToolOptions(id, { host: triageHostRef.current, chatsRef, sent: sentAddedRef.current }),
                onHarnessEvent: harnessEventHook(id),
                serverChatId: (chatsRef.current || []).find((c) => c.id === id)?.serverChatId || null,
                getServerChatId: () => (chatsRef.current || []).find((c) => c.id === id)?.serverChatId || null,
                runContext: LIVE_COMMAND_RUN_CONTEXT,
                getRunContext: () => (
                    interactiveAgentRunContextsRef.current.get(id)
                    || LIVE_COMMAND_RUN_CONTEXT
                ),
            });
            if (!chatsRef.current.some((c) => c.id === id)) return null;
            const chat = chatsRef.current.find((c) => c.id === id);
            agent.state.thinkingLevel = resolveThinkingLevel(
                agent.state.model?.provider, agent.state.model?.id, chat?.thinkingLevel);
            agentsRef.current.set(id, agent);
            setAgentReadyEpoch((n) => n + 1);
            wireChildRegistrySync(id, agent);
            return agent;
        } catch (err) {
            updateChat(id, (c) => ({ ...c, error: err.message || String(err) }));
            return null;
        } finally {
            agentBuildingRef.current.delete(id);
        }
    }, [updateChat, wireChildRegistrySync]);

    // What another surface runs a chat with: a chat restored from storage,
    // its transcript not yet read, is built the way opening it builds it, so
    // it answers with its conversation and shows it when it comes on screen.
    // ensureAgentFor alone would build it bare and leave it restoring.
    const ensureLoadedAgentFor = useCallback(async (id) => {
        if (needsBodyLoadRef.current.has(id) && !agentsRef.current.has(id) && !agentBuildingRef.current.has(id)) {
            await buildAgent(id);
            return agentsRef.current.get(id) || null;
        }
        return ensureAgentFor(id);
    }, [buildAgent, ensureAgentFor]);

    // "Tailor these to your data" — run the tenant-profiling playbook.
    // The trigger only renders in the empty-chat state, so the active
    // chat is normally empty: run the playbook IN PLACE (convert this
    // tab to a "scout" view) rather than spawning a separate tab, which
    // orphaned the empty "New chat" the user was already on and was
    // inconsistent with every other starter (they run in the current
    // tab). Defensive fallback: if somehow invoked with a non-empty
    // active chat, open a dedicated tab so we never clobber a real
    // conversation. The scout tab persists and renders as a profile
    // view, not a transcript.
    const startTenantScout = useCallback(async () => {
        const activeId = activeIdRef.current;
        const current = chatsRef.current.find((c) => c.id === activeId);
        const reuse = current && (current.items?.length ?? 0) === 0 && !current.busy;
        const id = reuse ? activeId : nextChatIdRef.current++;
        const userItem = { kind: "user", text: "Profile this tenant's AI usage and tailor the starter suggestions." };
        // Mark the run busy + scout up front (one update) so the view
        // switches straight to the profiling progress panel — no flash of
        // the "finished without a tailored set" empty branch in the gap
        // before busy is set.
        if (reuse) {
            updateChat(id, (c) => ({ ...c, kind: "scout", title: SCOUT_TITLE, items: [...c.items, userItem], busy: true, error: null }));
        } else {
            setChats((cur) => [...cur, { ...makeChat(id), kind: "scout", title: SCOUT_TITLE, items: [userItem], busy: true }]);
            setActiveId(id);
        }
        const agent = await ensureAgentFor(id);
        if (!agent) {
            // Roll back the busy state so the trigger isn't stuck disabled.
            updateChat(id, (c) => ({ ...c, busy: false }));
            return;
        }
        const playbook = buildStarterPlaybook();
        await runInteractiveAgentCall(
            id,
            agent,
            userItem.text,
            () => agent.prompt(playbook),
            { trigger: CHAT_TRIGGER.STARTER },
        );
    }, [ensureAgentFor, runInteractiveAgentCall, updateChat]);

    // Run a single tailored question in a fresh normal chat (the profile
    // view's clickable cards). Activates the new chat; the activation
    // effect early-returns because ensureAgentFor claims the build
    // synchronously before React commits the activeId change.
    const runPromptInNewChat = useCallback(async (body, opts = {}) => {
        if (!body) return;
        const id = nextChatIdRef.current++;
        // Seed the spawned chat's scope toggle from the originating scout —
        // the scout's chip is the control for whether the follow-ups it
        // spawns carry scope. A fresh chat is always on its first prompt, so
        // skipScope alone decides include vs exclude.
        const seedSkip = !!opts.skipScope;
        setChats((cur) => [...cur, { ...makeChat(id), skipScope: seedSkip }]);
        setActiveId(id);
        const agent = await ensureAgentFor(id);
        if (!agent) return;
        // Mirror submit()'s first-prompt scope contract: lock scopeMode and
        // inject the <dashboard_context> notification ahead of the prompt.
        // Previously this path sent a bare prompt — the agent never received
        // the scope the chip claimed it was sending, and scopeMode stayed
        // null while items grew, leaving the chip's × toggle suppressed on a
        // chat that was, in fact, a started conversation (O-2263).
        const nextScopeMode = seedSkip ? "exclude" : "include";
        const rawCtx = buildDashboardContext(searchParams);
        const ctxForLlm = seedSkip ? { ...rawCtx, scope: [] } : rawCtx;
        const ctxText = stringifyDashboardContext(ctxForLlm);
        const promptInput = ctxText
            ? [
                  { role: "notification", text: ctxText, timestamp: Date.now() },
                  { role: "user", content: body, timestamp: Date.now() },
              ]
            : body;
        updateChat(id, (c) => ({
            ...c,
            items: [...c.items, { kind: "user", text: body }],
            busy: true,
            error: null,
            title: titleFromPrompt(body),
            scopeMode: nextScopeMode,
        }));
        await runInteractiveAgentCall(
            id,
            agent,
            body,
            () => agent.prompt(promptInput),
        );
    }, [ensureAgentFor, runInteractiveAgentCall, updateChat, searchParams]);
    runPromptInNewChatRef.current = runPromptInNewChat;

    // The host's closures read the console's latest callbacks through this
    // ref; the offer to the lens is registered once, for as long as the
    // console is mounted.
    triageHostDepsRef.current = {
        updateChat,
        ensureAgentFor: ensureLoadedAgentFor,
        surface: () => transcriptSurface({ dockHost: getDockHostPage(), dockOpen: getDockAvailable(), dockBodyHost: Boolean(getDockBodyHost()) }),
        // After the surface has settled, the way a staged first message lands.
        focusComposer: () => setTimeout(() => inputRef.current?.focus(), 60),
        sendPrompt: stagedPromptSender(activeIdRef, submit),
        // A turn nobody typed carries no consent of its own: it runs under
        // the live context alone, so a live action in it fails closed.
        runTurn: (id, agent, _label, messages) => runAgentCall(id, agent, () => agent.prompt(messages), { analytics: { trigger: CHAT_TRIGGER.CONTINUATION } }),
        reloadActive: reloadAgentForActive,
    };
    useEffect(() => registerTriageChatHost(triageHostRef.current), []);

    // Settings can change while a chat runs in the background. Preserve
    // that run, then refresh cached agents with their transcripts intact.
    useEffect(() => {
        const changed = () => {
            refreshCredsSummary();
            if (chatsRef.current.some((chat) => chat.id === activeIdRef.current)) settingsDirtyAgentsRef.current.add(activeIdRef.current);
            for (const id of agentsRef.current.keys()) settingsDirtyAgentsRef.current.add(id);
            for (const id of agentBuildingRef.current) settingsDirtyAgentsRef.current.add(id);
            setSettingsEpoch((n) => n + 1);
        };
        return onChatSettingsChanged(changed);
    }, [refreshCredsSummary]);
    useEffect(() => {
        if (chats.some((chat) => chat.busy) || agentBuildingRef.current.size) return;
        const pending = [...settingsDirtyAgentsRef.current];
        settingsDirtyAgentsRef.current.clear();
        if (!pending.length) return;
        void (async () => {
            for (const id of pending) {
                if (!chatsRef.current.some((chat) => chat.id === id)) continue;
                if (chatsRef.current.some((chat) => chat.busy) || agentBuildingRef.current.has(id)) {
                    settingsDirtyAgentsRef.current.add(id);
                    continue;
                }
                await reloadAgentForActive(id);
            }
        })();
    }, [chats, settingsEpoch, agentReadyEpoch, reloadAgentForActive]);

    const items = activeChat?.items || [];
    // The chat's latest answer, with the graph its claims are in, for the
    // workspace showing that graph to list them in the answer's order.
    const latestAnswer = useMemo(() => latestAnswerText(items), [items]);
    const answerGraph = agentsRef.current.get(activeChatId)?.graph || null;
    useEffect(() => { publishChatAnswer(answerGraph, latestAnswer); }, [answerGraph, latestAnswer]);
    useEffect(() => () => publishChatAnswer(null, ""), []);

    // Session uuids in prose become clickable on exact match against ids this
    // chat saw come back from analytics.
    const linkifyProse = useSessionLinkifier({
        chatId: activeChatId,
        items,
        openScope: openScopeAsTab,
    });
    const input = activeChat?.input || "";
    const busy = !!activeChat?.busy;
    // A card in the transcript (a question's choices) answers as the person's
    // next message in this chat.
    const answeredQuestions = useMemo(() => answeredQuestionIds(items), [items]);
    const chatReply = useMemo(() => ({
        reply: (text) => submit({ explicitPrompt: text }), busy, answered: (id) => answeredQuestions.has(id),
    }), [submit, busy, answeredQuestions]);
    const error = activeChat?.error || null;
    // Chat persistence: a small, calm "this chat isn't backing up" line when the
    // sync layer has permanently parked this chat (oversize / no write access) —
    // otherwise the promise silently fails for exactly the heaviest chats.
    const syncBlockedReason = activeChat?.syncBlocked ? activeChat.syncBlockedReason : null;
    // The live agent backing the active chat, if its build has landed.
    // agentsRef is a ref, so depend on agentReadyEpoch (bumped on every
    // install/teardown) to re-resolve when readiness flips.
    const activeAgent = useMemo(
        () => (activeChat ? agentsRef.current.get(activeChat.id) || null : null),
        [activeChat, agentReadyEpoch],
    );
    const agentReady = !!activeAgent;
    // The composer can't dispatch a NEW prompt until the tenant LLM
    // config has loaded AND this chat's agent has finished building —
    // sending before then is exactly what threw "Agent not ready".
    // Steering an in-flight run reuses the already-built agent, so the
    // input stays live while busy. Gate logic is the pure
    // `computeComposerGate` helper (unit-tested in composerGate.test.js).
    const { blocked: composerBlocked, inputDisabled: composerDisabled } =
        computeComposerGate({ loading: credsSummary.loading, agentReady, busy, harnessTurn: activeChat?.harnessTurn || null });
    // The mount-time autofocus RAF fires while the textarea is still
    // disabled (agent building), so it's a no-op — pull focus in when
    // the composer un-gates so the freshly-enabled input is ready to
    // type into. Skipped while busy so we don't yank focus mid-run.
    const composerWasDisabledRef = useRef(composerDisabled);
    useEffect(() => {
        const wasDisabled = composerWasDisabledRef.current;
        composerWasDisabledRef.current = composerDisabled;
        if (open && !busy && wasDisabled && !composerDisabled) {
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [open, busy, composerDisabled]);
    // A chat restored from the lightweight tab metadata whose transcript
    // is still being read from IndexedDB on first open. Its `items` is
    // empty only because the body hasn't loaded — show a brief restoring
    // state instead of the "empty chat" onboarding view. See O-1746.
    const bodyLoading = activeChat?.bodyLoaded === false;
    const transcriptEmpty = items.length === 0 && !bodyLoading;
    // Beside a workspace, the band of the jobs the chat runs there.
    const workspaceAsks = canvasUp && activeChat?.kind !== "scout";
    // A workspace started on a question (the blank pane's Ask) has it asked
    // of its chat once that chat is beside it, empty and able to send: the
    // reader asked it there, so no second Send. The chat is the workspace's
    // from then on, so the board follows what it finds.
    const openWorkspace = useTraceCanvasOpen();
    const openWorkspaceId = openWorkspace && !openWorkspace.preview ? openWorkspace.id : null;
    const openRecord = useTraceCanvas(openWorkspaceId);
    const questionAsked = useRef(null);
    useEffect(() => {
        if (!pageVisible || !openWorkspaceId || !openRecord.question || openRecord.chatAsked || questionAsked.current === openWorkspaceId) return;
        if (!activeChat || activeChat.kind === "scout" || !transcriptEmpty || busy || composerBlocked || !credsSummary.configured) return;
        questionAsked.current = openWorkspaceId;
        setTraceCanvasChat(openWorkspaceId, String(activeChat.id));
        submit({ explicitPrompt: openRecord.question });
    }, [pageVisible, openWorkspaceId, openRecord.question, openRecord.chatAsked, activeChat, transcriptEmpty, busy, composerBlocked, credsSummary.configured, submit]);

    // Slash-command picker. Matches are derived purely from the
    // input — the menu opens whenever the input is a `/foo` token
    // with no spaces. `slashIndex` is the highlighted row; we clamp
    // it to the current match count so deleting characters can't
    // leave us pointing past the end.
    const slashMatches = useMemo(() => matchSlashCommands(input), [input]);
    const slashOpen = !!slashMatches && slashMatches.length > 0 && !busy;
    useEffect(() => { slashOpenRef.current = slashOpen; }, [slashOpen]);
    const [slashIndex, setSlashIndex] = useState(0);
    useEffect(() => {
        if (!slashMatches || slashMatches.length === 0) {
            if (slashIndex !== 0) setSlashIndex(0);
            return;
        }
        if (slashIndex >= slashMatches.length) setSlashIndex(0);
    }, [slashMatches, slashIndex]);

    const completeSlash = useCallback((name) => {
        const id = activeIdRef.current;
        updateChat(id, (c) => ({ ...c, input: name }));
    }, [updateChat]);

    const onKeyDown = useCallback(
        (e) => {
            // Slash picker keyboard handling — only active while the
            // menu is open. Enter completes-and-submits the
            // highlighted command so partial typing (`/cont`) runs
            // /context without a second keypress.
            if (slashOpen && slashMatches) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSlashIndex((i) => (i + 1) % slashMatches.length);
                    return;
                }
                if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length);
                    return;
                }
                if (e.key === "Tab") {
                    e.preventDefault();
                    completeSlash(slashMatches[slashIndex]?.name || "");
                    return;
                }
                if (e.key === "Escape") {
                    // React's synthetic stopPropagation only stops the
                    // React tree — the native event still bubbles to
                    // the window-level Esc-to-close listener. Stop the
                    // native event so dismissing the picker doesn't
                    // also close the chat overlay.
                    e.preventDefault();
                    e.nativeEvent.stopImmediatePropagation();
                    const id = activeIdRef.current;
                    updateChat(id, (c) => ({ ...c, input: "" }));
                    return;
                }
                if (e.key === "Enter") {
                    e.preventDefault();
                    const picked = slashMatches[slashIndex];
                    if (!picked) return;
                    submit({ explicitPrompt: picked.name });
                    return;
                }
            }
            if (e.key !== "Enter") return;
            // Shift+Enter is always a newline now that the input is
            // multi-line. Let the textarea handle it natively.
            if (e.shiftKey) return;
            e.preventDefault();
            // Cmd/Ctrl+Enter while busy: queue for after the run
            // finishes (followUp). Plain Enter while busy: interrupt
            // at the next turn boundary (steer). Not busy: just submit.
            const queue = busy && (e.metaKey || e.ctrlKey);
            submit(queue ? "followup" : "default");
        },
        [slashOpen, slashMatches, slashIndex, completeSlash, updateChat, submit, busy],
    );

    // UI compaction: when the transcript grows long, fold the older
    // prefix behind a banner. The offset is added back to per-item
    // indices passed to TreeItem so edit/rerun still truncates at
    // the right position in the underlying items array.
    const compactedCount =
        items.length > UI_COMPACT_TRIGGER ? items.length - UI_COMPACT_KEEP_TAIL : 0;
    const compactionExpanded = isNodeExpanded(
        "compaction",
        "compaction",
        activeChat?.toggled,
    );
    const visibleStart = compactedCount > 0 && !compactionExpanded ? compactedCount : 0;
    const visibleItems = items.slice(visibleStart);
    const runs = useMemo(() => workRuns(items), [items]);

    const openChatCount = chats.filter((chat) => !closedChatIds.has(chat.id)).length;
    const tabStripTabs = chats.map((c) => ({
        id: `chat:${c.id}`,
        chatId: c.id,
        busy: !!c.busy,
        label: c.title,
        active: c.id === activeId,
        updatedAt: c.updatedAt,
        hasItems: !!(c.hasItems || c.items?.length),
        title: c.title,
        dot: c.busy ? "bg-[color:var(--green)]" : undefined,
        onSelect: () => setActiveId(c.id),
        closed: closedChatIds.has(c.id),
        onClose: openChatCount > 1 && !closedChatIds.has(c.id) ? () => closeChatTab(c.id) : undefined,
        onDelete: () => deleteSavedChat(c.id),
        // "Export to PDF" icon — only on the ACTIVE tab (and only when it
        // has something to export). Keeps inactive tabs clean; replaces
        // the wide "↓ pdf" rail button.
        onExport: (c.id === activeId && c.items?.length) ? () => exportPdf(c.id) : undefined,
        exporting: exportingId === c.id,
    }));

    // "+" affordance styled to match FocusTabStrip's new-tab button.
    const newChatAction = (
        <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
            <button
                type="button"
                onClick={newChat}
                title="New chat"
                aria-label="New chat"
                className="inline-flex w-[28px] shrink-0 items-center justify-center px-[8px] text-[14px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--muted)] transition-colors cursor-pointer"
            >
                +
            </button>
            {docked ? null : (
            <button
                type="button"
                onClick={toggleExpanded}
                title={expanded ? "Restore" : "Expand"}
                aria-label={expanded ? "Restore chat console" : "Expand chat console"}
                className="inline-flex w-[28px] shrink-0 items-center justify-center border-l border-[color:var(--line)] px-[8px] text-[color:var(--muted-soft)] hover:text-[color:var(--muted)] transition-colors cursor-pointer"
            >
                <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className="h-[12px] w-[12px]"
                >
                    {/* Four corner arrows. Round caps/joins to match
                        the rest of the dashboard. Diagonal shafts are
                        4 units long with their inner ends at
                        (6,6)/(10,6)/(6,10)/(10,10) — a 4-unit empty
                        square in the middle keeps the four arrows
                        visually separated rather than clustering at
                        center. L-bracket arms stay at 3 units. */}
                    {expanded ? (
                        <>
                            <path d="M3 6 L6 6 L6 3 M6 6 L2 2" />
                            <path d="M13 6 L10 6 L10 3 M10 6 L14 2" />
                            <path d="M3 10 L6 10 L6 13 M6 10 L2 14" />
                            <path d="M13 10 L10 10 L10 13 M10 10 L14 14" />
                        </>
                    ) : (
                        <>
                            <path d="M6 6 L2 2 M5 2 L2 2 L2 5" />
                            <path d="M10 6 L14 2 M11 2 L14 2 L14 5" />
                            <path d="M6 10 L2 14 M2 11 L2 14 L5 14" />
                            <path d="M10 10 L14 14 M14 11 L14 14 L11 14" />
                        </>
                    )}
                </svg>
            </button>
            )}
        </div>
    );

    // The settings shortcut shows the active model in its tooltip. We drop the provider label from the
    // visible pill (it's redundant for openrouter-prefixed ids and
    // short enough not to matter for native ones) and tuck it into
    // the tooltip; that buys real estate for the model id without
    // making the pill dominate the footer.
    const modelPillProvider = useMemo(() => {
        if (credsSummary.provider) {
            return findCuratedProvider(credsSummary.provider)?.label || credsSummary.provider;
        }
        return "";
    }, [credsSummary]);

    const modelPillLabel = useMemo(() => {
        if (!credsSummary.model) return "Configure model…";
        return credsSummary.model;
    }, [credsSummary]);

    

    // Thinking-level control — a fixed 34px icon button (the depth meter)
    // matching the settings gear, with the level menu on the same
    // dropdown machinery. Replaces the old wide "THINK OFF ▾" label.
    // Resolved thinking depth shown on the pill. Precedence:
    //   1. the chat's explicit choice (incl. "off") — the user picked it;
    //   2. the live agent's resolved level — the EXACT value the next
    //      request will carry (set at build/reload from the model's
    //      default), so the pill can't drift from what's actually sent;
    //   3. a model-derived default for the brief window before the agent
    //      exists, computed from the loaded tenant config.
    // Before (2) the pill read credsSummary alone, which loads on a
    // different clock than the agent — so the depth meter rendered "off"
    // until config arrived and then jumped, reading as a phantom reset
    // even though the agent already held the right level.
    // Fable rejects a disabled-thinking request and 400s the turn, so the
    // active model can't be handed "off". The default already steers clear
    // (defaultThinkingLevelFor), but the pill let a user pick it explicitly
    // — so disable that option (with a reason) for mandatory-thinking models
    // rather than letting the next send fail. Keyed off the model the agent
    // will actually use, falling back to the configured one pre-build.
    const { offDisabled } = thinkingPillModel(activeAgent, credsSummary);
    // A persisted explicit level wins (precedence 1) — but never a stale
    // "off" on a model that now mandates thinking (e.g. the chat held "off"
    // before its model was switched to Fable). Honouring it would show the
    // selected-but-disabled "off" and 400 the next send, so drop it and fall
    // through to the agent's resolved level (which resolveThinkingLevel
    // already coerced off "off") / the model default. Precedence 2 (the live
    // agent's level) stays intact for the creds-load race.
    const persistedLevel = (activeChat?.thinkingLevel === "off" && offDisabled)
        ? null
        : activeChat?.thinkingLevel;
    const thinkingLevel = persistedLevel
        ?? activeAgent?.state?.thinkingLevel
        ?? defaultThinkingLevelFor(credsSummary.provider, credsSummary.model);
    const thinkingPill = <ThinkingPill level={thinkingLevel} offDisabled={offDisabled} onChange={setThinkingLevel} framed={framed} />;

    // Publish the chat's tab model so the right-column rail (ScopedPage)
    // can render the session tabs next to the mode switch while docked.
    // Gated on a lightweight signature so streaming re-renders don't
    // spam the store (and re-render the rail) every frame.
    // Per-chat item presence (`:1`/`:0`) + the exporting id are in the sig
    // so the per-tab export icon appears/updates as any chat gains content
    // or starts/finishes exporting — not just the active one. updatedAt is in
    // the sig so the sidebar's "last active" relTime re-publishes when a chat
    // is touched without otherwise changing the strip.
    const chatBarSig = docked
        ? `${chats.map((c) => `${c.id}:${c.title || ""}:${c.busy ? 1 : 0}:${(c.items?.length || c.hasItems) ? 1 : 0}:${c.updatedAt || 0}`).join("|")}#${activeId}#${exportingId || ""}#${hydrated ? 1 : 0}#${[...closedChatIds].join(",")}`
        : "";
    useEffect(() => {
        if (!docked) { setChatBar(null); return; }
        setChatBar({
            tabs: tabStripTabs,
            // Whether the chats kept from before are in and landed on: until
            // then the tabs are not the reader's, and whichever is in front
            // is about to be replaced.
            hydrated,
            onReorder: chats.length > 1 ? reorderChats : null,
            onNew: newChat,
        });
        return () => setChatBar(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docked, chatBarSig]);

    if (!pageVisible || (onOpenPage && !docked) || (!open && !docked)) return null;

    // Docked fills its host column; overlay uses its centered, resizable
    // sizing. dialogEl is rendered into one of two wrappers below.
    const dialogInlineStyle = docked
        ? undefined
        : (!expanded && size
            ? { width: size.width, height: size.height, maxHeight: "none" }
            : undefined);

    const dialogSizeClass = docked
        ? "h-full w-full"
        : `border border-[color:var(--line-strong)] shadow-lg ${size || expanded || flipInProgress ? "" : "transition-[width] duration-300 ease-out"} ${expanded ? "w-screen h-screen max-h-none" : (size ? "" : "w-[min(720px,92vw)] h-[72vh]")}`;

    const dialogEl = (
            <div
                ref={dialogRef}
                onClick={(e) => e.stopPropagation()}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={onDialogDrop}
                style={dialogInlineStyle}
                className={`relative grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto_auto] cursor-default bg-[color:var(--bg)] ${!docked && 'px-[12px]'} ${dialogSizeClass}`}
            >
                {docked || !credsSummary.configured ? (
                    // Reserve the grid's first (auto) row even when the tab
                    // strip is suppressed (docked: tabs live on the column
                    // rail). A bare `null` is NOT a grid item, so the body
                    // would slide up into this row and the `1fr` track would
                    // collapse the input/footer — keep an empty placeholder.
                    <div aria-hidden="true" />
                ) : (
                    <TabStrip
                        tabs={tabStripTabs.filter((tab) => !tab.closed)}
                        className="pt-[11px]"
                        actions={newChatAction}
                        onReorder={chats.length > 1 ? reorderChats : null}
                    />
                )}

                {tenantLoadError ? (
                    <div role="alert" className="grid min-h-0 place-content-center gap-[10px] px-[20px] text-center">
                        <span className="text-[12px] leading-[1.5] text-[color:var(--muted)]">{tenantLoadError}</span>
                        <Button
                            onClick={() => setTenantLoadAttempt((attempt) => attempt + 1)}
                            className="mx-auto"
                        >
                            Retry tenant verification
                        </Button>
                    </div>
                ) : !credsSummary.configured ? (
                    <div className="grid min-h-0 place-content-center gap-[10px] px-[20px] text-center">
                        <h2 className="m-0 text-[16px] text-[color:var(--ink)]">Set up chat</h2>
                        <p className="m-0 max-w-[380px] text-[12px] leading-[1.5] text-[color:var(--muted)]">Choose a model and connect a provider to start chatting.</p>
                        <Button variant="primary" onClick={() => openSettings()} className="mx-auto">Open chat settings</Button>
                    </div>
                ) : (
                <div
                    className="relative grid min-h-0 min-w-0 grid-cols-1"
                >
                <div className="relative min-w-0 min-h-0">
                <ScrollArea
                    hostRef={transcriptHostRef}
                    instanceRef={transcriptOsRef}
                    options={TRANSCRIPT_OS_OPTIONS}
                    className="min-h-0 h-full"
                    // Docked in a column, the column's gaps are the transcript's
                    // inset, as they are the composer's below it.
                    contentClassName={`py-[14px] text-[13px] leading-[1.6] text-[color:var(--ink)] [overflow-wrap:anywhere]${(transcriptEmpty || bodyLoading || activeChat?.kind === "scout") ? ` flex min-h-full items-center${framed ? " px-[16px]" : ""}` : framed ? " px-[10px]" : docked ? "" : " px-[20px]"}`}
                >
                    {bodyLoading ? (
                        // Lazily restoring this chat's transcript from
                        // IndexedDB (it was opened for the first time this
                        // session). Brief — one small record read. Matches
                        // the agent "Working" three-dot idiom below.
                        <div className="mx-auto flex w-full max-w-[640px] items-center justify-center gap-[8px] text-[12px] text-[color:var(--muted-soft)]">
                            <span className="inline-flex items-center gap-[3px]">
                                <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:0ms]" />
                                <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:160ms]" />
                                <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:320ms]" />
                            </span>
                            <span>Restoring conversation…</span>
                        </div>
                    ) : activeChat?.kind === "scout" ? (
                        <TenantProfileView
                            chat={activeChat}
                            onPickCard={runPromptInNewChat}
                        />
                    ) : transcriptEmpty && canvasUp ? (
                        // Beside a workspace the chat opens on what it can do
                        // there: the traces on the board are what it is
                        // about, so its starters ask about them.
                        <WorkspaceChatStart
                            onPick={(body) => submit({ explicitPrompt: body })}
                            disabled={busy || composerBlocked || !credsSummary.configured}
                        />
                    ) : transcriptEmpty ? (
                        // Empty state: an intro block (same eyebrow/h2/p shape as
                        // the model-picker intro) over a card grid of starter
                        // queries on the dotted diagram sheet — mirroring the
                        // Activity Surface so the docked column reads as a
                        // designed panel rather than a bare text blob. No
                        // horizontal padding of its own: the sheet aligns flush
                        // with the input footer's edges (the column gap is the
                        // inset), and a framed pane gives it the pane's gutters.
                        <div className="mx-auto flex w-full max-w-[640px] min-w-0 flex-col gap-[14px]">
                            <div className="flex flex-col gap-[6px]">
                                <span className="text-[12px] text-[color:var(--muted-soft)]">
                                    Analytics agent
                                </span>
                                <h2 className="m-0 text-[16px] leading-[1.25] text-[color:var(--ink)]">
                                    Ask the analytics graph
                                </h2>
                                <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--muted)]">
                                    The agent can query the tenant data you can access and render tables and charts in the chat.
                                </p>
                            </div>
                            {/* Default: always show the curated static pack so a
                                profiling run never silently clobbers it. The
                                tailored set lives in the store (set_starter_cards)
                                and renders in its own "Tenant profile" tab from
                                that run's transcript regardless — it only TAKES
                                OVER this empty-state grid when the per-user
                                `origin.feature.tailored-starters` flag is on. */}
                            <StarterCards
                                queries={tailoredStartersEnabled() && tailoredStarters?.cards?.length ? tailoredStarters.cards : SAMPLE_QUERIES}
                                onPick={(body) => submit({ explicitPrompt: body })}
                                disabled={busy || composerBlocked || !credsSummary.configured}
                            />
                            {/* The trigger runs the profiling playbook IN this empty
                                chat (startTenantScout converts it to a scout view); the
                                progress + tailored result live in this same tab. Disabled
                                while one is running. */}
                            <button
                                type="button"
                                onClick={startTenantScout}
                                disabled={runningScout || !credsSummary.configured}
                                className="group mx-auto flex items-center gap-[5px] bg-transparent px-[4px] py-[2px] text-[11px] leading-[1.3] text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--ink)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <span aria-hidden="true" className={runningScout ? "text-[color:var(--muted-soft)] animate-pulse" : "text-[color:var(--green)]"}>✦</span>
                                <span className="border-b border-transparent group-hover:border-[color:var(--line-strong)]">
                                    {runningScout ? "Profiling your tenant…" : "Profile your AI usage"}
                                </span>
                            </button>
                        </div>
                    ) : (
                        // Keyed per chat so switching tabs REMOUNTS the
                        // transcript instead of reconciling one chat's
                        // item list against another's — positional keys
                        // below make cross-chat fiber reuse render
                        // duplicated/dropped rows.
                        // In a framed pane the transcript fills it, its prose
                        // too: a dock under a board is wide and short, and a
                        // column centered in it left most of it empty.
                        <ChatReplyContext.Provider value={chatReply}>
                        <div key={activeChat?.id ?? "none"} className={framed ? "flex flex-col gap-[18px]" : "mx-auto flex max-w-[900px] flex-col gap-[18px]"}>
                            {compactedCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => toggleNode("compaction", "compaction")}
                                    className="flex w-full items-center gap-[6px] bg-transparent text-left text-[12px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer"
                                >
                                    <Chevron expanded={compactionExpanded} />
                                    <span className="shrink-0 whitespace-nowrap">
                                        {compactionExpanded ? "Hide" : "Show"} {compactedCount} earlier
                                        {compactedCount === 1 ? " item" : " items"}
                                    </span>
                                    <span className="text-[color:var(--muted-soft)]">
                                        · compacted
                                    </span>
                                </button>
                            ) : null}
                            {visibleItems.map((it, i) => {
                                const fullIdx = i + visibleStart;
                                // A run of work turns reads as its first member's
                                // line; open, its turns follow under it.
                                // A run of work turns is one block at its first
                                // member: its line, and opened, its turns in one
                                // panel with a hairline between them.
                                const run = runs.get(fullIdx);
                                const runOpen = run ? isNodeExpanded("turnWork", run.id, activeChat?.toggled) : false;
                                const head = !!run && fullIdx === run.first;
                                const treeItem = (idx, node, bare = false) => (
                                        <TreeItem
                                            index={idx}
                                            node={node}
                                            bare={bare}
                                            chatBusy={busy}
                                            toggled={activeChat?.toggled || NO_TOGGLES}
                                            onToggle={toggleNode}
                                            onOpenScope={openScopeAsTab}
                                            linkifyProse={linkifyProse}
                                            onFork={forkFromTurn}
                                            onRewindTurn={rewindToTurn}
                                            onReplayTurn={replayTurn}
                                            onEditUser={editAndRerun}
                                            onRewindUser={rewindToUser}
                                            onForkUser={forkFromUser}

                                            onSettleJob={settleJob}
                                        />
                                );
                                return (
                                    <div
                                        key={it.id || `top:${fullIdx}`}
                                        className={run && !head ? "hidden" : undefined}
                                    >
                                        {head ? (
                                            <div className={runOpen ? "rounded-[2px] bg-[color:var(--panel)] px-[12px] py-[8px]" : undefined} data-work-run-block="">
                                                <WorkRunLine run={run} open={runOpen} onToggle={() => toggleNode(run.id, "turnWork")} />
                                                {runOpen ? (
                                                    <div className="mt-[8px] divide-y divide-[color:var(--line-soft)]">
                                                        {run.members.map((k) => (
                                                            <div key={items[k]?.id || `run:${k}`} className="py-[8px] first:pt-0 last:pb-0">
                                                                {treeItem(k, items[k], true)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : run.outputs.length ? (
                                                    // Folded, the work goes; what it made for the
                                                    // reader (the chart they asked for) stays.
                                                    <div className="mt-[10px] flex flex-col gap-[10px]" data-work-run-outputs="">
                                                        {foldedOutputs(run.outputs).map((o) => (o.pins ? (
                                                            <ChatPinsCard key={`pins:${o.id}`} pins={o.pins} heading={pinsHeading(o.pins.length)} />
                                                        ) : (
                                                            <ToolNode
                                                                key={o.node.id}
                                                                node={o.node}
                                                                toggled={activeChat?.toggled || NO_TOGGLES}
                                                                onToggle={toggleNode}
                                                                onOpenScope={openScopeAsTab}
                                                                linkifyProse={linkifyProse}

                                                            />
                                                        )))}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : run ? null : treeItem(fullIdx, it)}
                                    </div>
                                );
                            })}
                            {busy ? (
                                <WorkingIndicator
                                    stalledSince={activeChat?.stalledSince || null}
                                    onStop={abort}
                                    onStopAndRetry={stopAndRetry}
                                />
                            ) : null}
                        </div>
                        </ChatReplyContext.Provider>
                    )}
                </ScrollArea>
                </div>
                </div>
                )}

                {credsSummary.configured ? (
                // Footer: input field flush to the rule's left edge and the
                // gear flush to the right edge (the column gap supplies the
                // inset — no extra horizontal padding); run controls + the
                // model/settings gear sit on the right.
                <div className={framed ? "border-t border-[color:var(--line-soft)] px-[12px]" : "border-t border-[color:var(--line)] pb-[12px]"}>
                    {(() => {
                        const rawCtx = buildDashboardContext(searchParams);
                        const hasScope = rawCtx.scope.length > 0;
                        const mode = activeChat?.scopeMode ?? null;
                        // Chip is irrelevant when there's nothing to
                        // show or when the chat has explicitly locked
                        // scope out — both render nothing. The × toggle is
                        // offered pre-first-prompt, and on a scout: a scout
                        // (tenant-profile) tab is a result/scratch surface,
                        // not a committed conversation — every follow-up
                        // typed there spawns a fresh chat that inherits this
                        // skipScope, so its chip stays toggle-live regardless
                        // of the run's items.
                        if (mode === "exclude") return null;
                        if (!hasScope) return null;
                        const isScout = activeChat?.kind === "scout";
                        const conversationStarted = (activeChat?.items?.length ?? 0) > 0;
                        const preFirstPrompt = isScout || (mode === null && !conversationStarted);
                        return (
                            <div className="pt-[8px]">
                                <AutoContextChip
                                    ctx={rawCtx}
                                    skipped={preFirstPrompt && !!activeChat?.skipScope}
                                    onToggleSkip={preFirstPrompt
                                        ? () => updateChat(activeId, (c) => ({ ...c, skipScope: !c.skipScope }))
                                        : null}
                                />
                            </div>
                        );
                    })()}
                    <ChatSteering
                        steering={activeChat?.steering || null}
                        input={input}
                        busy={busy}
                        ready={!composerBlocked}
                        onToggle={(steerId) => triageHostRef.current.toggleSteer({ chatId: activeId }, steerId)}
                        onStart={() => submit()}
                    />
                    <ChatStarterQuestions
                        pendingContext={activeChat?.pendingContext || null}
                        busy={busy}
                        onPick={(q) => {
                            updateChat(activeId, (c) => ({ ...c, input: q }));
                            requestAnimationFrame(() => inputRef.current?.focus());
                        }}
                    />
                    {activeChatId != null ? <ChatTray id={`console:${activeChatId}`} className="mt-[8px]" /> : null}
                    {activeChat?.staged?.length ? (
                        <div className="mb-[8px] mt-[8px] flex flex-wrap gap-[6px]">
                            {activeChat.staged.map((img) => (
                                <div
                                    key={img.id}
                                    className="relative inline-flex items-center gap-[6px] rounded-[7px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[3px] pr-[6px]"
                                >
                                    <img
                                        src={img.previewUrl}
                                        alt="staged"
                                        className="h-[32px] w-[32px] rounded-[5px] object-cover"
                                    />
                                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">
                                        {img.mimeType.replace("image/", "")}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeStagedImage(img.id)}
                                        title="Remove"
                                        aria-label="Remove image"
                                        className="cursor-pointer text-[12px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--red)]"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : null}
                    {error ? (
                        <div className="pt-[6px] text-[10px] leading-[1.35] text-[color:var(--red)]">{error}</div>
                    ) : null}
                    {syncBlockedReason ? (
                        <div role="status" className="pt-[6px] text-[10px] leading-[1.35] text-[color:var(--muted)]">{syncBlockedReason}</div>
                    ) : null}
                    {/* Beside a workspace, the jobs the chat runs there, a
                        click each, in a band of their own over the input. */}
                    {workspaceAsks ? (
                        <WorkspaceAsks
                            onPick={(body) => submit({ explicitPrompt: body })}
                            disabled={busy || composerBlocked}
                            framed={framed}
                        />
                    ) : null}
                    {/* Framed, the input row is the band itself, out to the
                        frame's edges past the notes' inset, under its own rule
                        when notes or the workspace's asks stand above it. */}
                    <div className={framed
                        ? `-mx-[12px] flex items-stretch [&:not(:first-child)]:border-t [&:not(:first-child)]:border-[color:var(--line-soft)] ${workspaceAsks ? "" : "[&:not(:first-child)]:mt-[8px]"}`
                        : "flex items-end gap-[6px] pt-[8px]"}
                    >
                        <div className="relative min-w-0 flex-1">
                            {slashOpen ? (
                                <SlashMenu
                                    matches={slashMatches}
                                    selectedIndex={slashIndex}
                                    onHover={setSlashIndex}
                                    onPick={(cmd) => {
                                        // Mouse pick: complete to the command
                                        // name and refocus — Enter runs.
                                        completeSlash(cmd.name);
                                        requestAnimationFrame(() => inputRef.current?.focus());
                                    }}
                                />
                            ) : null}
                            <ChatInput
                                ref={inputRef}
                                band={framed}
                                value={input}
                                onChange={(next) => updateChat(activeId, (c) => ({ ...c, input: next }))}
                                onKeyDown={onKeyDown}
                                onPaste={onInputPaste}
                                disabled={composerDisabled}
                                placeholder={composerPlaceholder({ harnessTurn: activeChat?.harnessTurn || null, busy, loading: credsSummary.loading, agentReady, steering: Boolean(activeChat?.steering), staged: activeChat?.staged?.length || 0, controls: !!policyInvestigationId })}
                            />
                        </div>
                        {error && !busy ? (
                            <button
                                type="button"
                                onClick={retry}
                                title="Retry"
                                aria-label="Retry"
                                className={`${framed ? COMPOSER_CELL : `${COMPOSER_BOX} hover:border-[color:var(--line-strong)]`} text-[14px] leading-none text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] cursor-pointer`}
                            >
                                ↻
                            </button>
                        ) : null}
                        {busy ? <StopRunButton onStop={abort} framed={framed} /> : null}
                        <InvestigationGraphPill chatId={graphChatKeyId} chatTitle={activeChat?.title || ""} cell={framed} />
                        {thinkingPill}
                        {/* Open the shared model and provider settings. */}
                        <ModelGear label={modelPillProvider ? `${modelPillProvider} · ${modelPillLabel}` : modelPillLabel} onOpen={() => openSettings()} framed={framed} />
                    </div>
                </div>
                ) : null}

                {!expanded && !docked ? (
                    <ResizeAffordances
                        beginResize={beginResize}
                        hoveredHandle={hoveredHandle}
                        activeHandle={activeHandle}
                        onHoverChange={setHoveredHandle}
                    />
                ) : null}
            </div>
    );

    // Over everything, outside the chat's own layout; its clicks stop here so
    // they don't reach the chat overlay's click-out.
    const chatSettingsEl = chatSettings ? createPortal(
        <div onClick={(event) => event.stopPropagation()}>
            <ChatSettingsDialog
                open
                tab={chatSettings === true ? null : chatSettings}
                onClose={() => setChatSettings(null)}
                onSaved={refreshCredsSummary}
            />
        </div>,
        document.body,
    ) : null;

    if (docked) {
        return (
            <>
                {createPortal(dialogEl, dockHost)}
                {chatSettingsEl}
            </>
        );
    }

    // Overlay: centered dialog over a dimming scrim; click-out closes.
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Chat console"
            onClick={close}
            className="fixed inset-0 z-[10000] grid place-items-center justify-items-center bg-black/50 backdrop-saturate-[0.5] backdrop-contrast-[0.75] backdrop-brightness-[0.75] cursor-pointer"
        >
            {dialogEl}
            {chatSettingsEl}
        </div>
    );
}

// Surfaces the dashboard state the chat agent auto-receives ahead
// of every prompt — the current time range and the active scope
// path. Without this, "explain this drop in 1M traffic" looks
// magical; with it, the user can see exactly what context the
// agent is being handed and trust (or correct) the framing.
// The × button drops the scope from auto-injection for this chat
// (range still goes) — useful when the user wants to ask a
// dashboard-wide question without navigating back to the
// overview first.
function AutoContextChip({ ctx, skipped, onToggleSkip }) {
    const hasScope = ctx?.scope?.length > 0;
    const range = ctx?.range || "1M";
    const canToggle = typeof onToggleSkip === "function";
    const tooltip = skipped
        ? "Scope skipped for the first prompt. Click ↺ to include it again."
        : canToggle
            ? "Your first prompt includes the current dashboard scope and time range. References such as 'this' use that context. Click × for an unscoped question. You cannot change this choice after sending."
            : "Every prompt includes the dashboard scope and time range used to start this chat. Navigate the dashboard to change the scope.";
    return (
        <div
            title={tooltip}
            className={`mb-[8px] flex items-center gap-[8px] rounded-[7px] border border-[color:var(--line)] px-[8px] py-[4px] text-[11.5px] ${skipped ? "border-dashed bg-transparent text-[color:var(--muted-soft)]" : "bg-[color:var(--control-bg)] text-[color:var(--muted-soft)]"}`}
        >
            <span className="shrink-0">Scope</span>
            <span aria-hidden="true" className="h-[10px] w-px shrink-0 bg-[color:var(--line)]" />
            <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]">
                {skipped ? (
                    hasScope ? (
                        <span className="text-[color:var(--muted-soft)] line-through">
                            {ctx.scope.map((s) => `${s.dimLabel}=${s.label}`).join(" / ")}
                        </span>
                    ) : (
                        <span>overview · no filters</span>
                    )
                ) : hasScope ? (
                    <>
                        {ctx.scope.map((s, i) => (
                            <span key={`${s.dim}:${s.id}:${i}`}>
                                {i > 0 ? <span className="text-[color:var(--muted-soft)]"> / </span> : null}
                                <span className="text-[color:var(--muted-soft)]">{s.dimLabel}=</span>
                                <span className="text-[color:var(--ink)]">{s.label}</span>
                            </span>
                        ))}
                    </>
                ) : (
                    <span>overview · no filters</span>
                )}
            </span>
            <span className="shrink-0 text-[color:var(--muted)]">{range}</span>
            {hasScope && canToggle ? (
                <button
                    type="button"
                    onClick={onToggleSkip}
                    title={skipped ? "Include scope when you send the first prompt" : "Drop scope from agent context for this chat"}
                    aria-label={skipped ? "Re-include scope" : "Drop scope"}
                    className="shrink-0 cursor-pointer text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] leading-none"
                >
                    {skipped ? "↺" : "×"}
                </button>
            ) : null}
        </div>
    );
}

// Resize affordances for the chat dialog. Eight hit zones (4 edges
// + 4 corners) straddle the dialog border, and four edge-indicator
// strips sit directly on the border itself. The strips light up
// when the corresponding edge is hovered OR when a corner adjacent
// to it is hovered/dragged — so hovering a corner reads as "both
// of these edges will move," and dragging keeps the highlight on
// while the cursor wanders. Edge handles are full corner-to-corner
// so the highlight isn't broken by the corner handles; corner
// handles sit on top with a higher z-index and win at the overlap.
function ResizeAffordances({ beginResize, hoveredHandle, activeHandle, onHoverChange }) {
    const lit = (edge) => {
        const cur = activeHandle || hoveredHandle;
        if (!cur) return false;
        if (cur === edge) return true;
        if (edge === "n") return cur === "nw" || cur === "ne";
        if (edge === "s") return cur === "sw" || cur === "se";
        if (edge === "e") return cur === "ne" || cur === "se";
        if (edge === "w") return cur === "nw" || cur === "sw";
        return false;
    };

    const stripCls = (on) =>
        `pointer-events-none absolute bg-[color:var(--ink)] transition-opacity duration-100 ${on ? "opacity-100" : "opacity-0"}`;

    const handleProps = (dir) => ({
        onMouseDown: beginResize(dir),
        onMouseEnter: () => onHoverChange(dir),
        onMouseLeave: () => onHoverChange((cur) => (cur === dir ? null : cur)),
    });

    return (
        <>
            {/* Edge indicator strips — 2px thick, half outside / half
                inside the dialog border so they read as "thickening
                the border" rather than a separate inset line. */}
            <div className={`${stripCls(lit("n"))} top-[-1px] left-0 right-0 h-[2px] z-[3]`} />
            <div className={`${stripCls(lit("s"))} bottom-[-1px] left-0 right-0 h-[2px] z-[3]`} />
            <div className={`${stripCls(lit("w"))} top-0 bottom-0 left-[-1px] w-[2px] z-[3]`} />
            <div className={`${stripCls(lit("e"))} top-0 bottom-0 right-[-1px] w-[2px] z-[3]`} />

            {/* Edge hit zones — full length, ~10px thick, straddle
                the border so the user can grab from either side. */}
            <div {...handleProps("n")} className="absolute top-[-6px] left-0 right-0 h-[12px] cursor-ns-resize z-[1]" />
            <div {...handleProps("s")} className="absolute bottom-[-6px] left-0 right-0 h-[12px] cursor-ns-resize z-[1]" />
            <div {...handleProps("e")} className="absolute right-[-6px] top-0 bottom-0 w-[12px] cursor-ew-resize z-[1]" />
            <div {...handleProps("w")} className="absolute left-[-6px] top-0 bottom-0 w-[12px] cursor-ew-resize z-[1]" />

            {/* Corner hit zones — 24×24, on top of the edge zones so
                they win at the overlap. The visual feedback comes
                from the two edge strips lighting up together. */}
            <div {...handleProps("nw")} className="absolute top-[-10px] left-[-10px] w-[24px] h-[24px] cursor-nwse-resize z-[2]" />
            <div {...handleProps("ne")} className="absolute top-[-10px] right-[-10px] w-[24px] h-[24px] cursor-nesw-resize z-[2]" />
            <div {...handleProps("sw")} className="absolute bottom-[-10px] left-[-10px] w-[24px] h-[24px] cursor-nesw-resize z-[2]" />
            <div {...handleProps("se")} className="absolute bottom-[-10px] right-[-10px] w-[24px] h-[24px] cursor-nwse-resize z-[2]" />
        </>
    );
}

// The composer's action buttons: boxes beside the input, or, in a framed
// pane, cells of the composer's band divided by hairlines.
export const COMPOSER_BOX = "grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[7px] border border-[color:var(--line)] bg-[color:var(--control-bg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--green)]";
export const COMPOSER_CELL = "grid w-[38px] shrink-0 self-stretch place-items-center border-0 border-l border-[color:var(--line-soft)] bg-transparent";

// The composer's thinking depth: the depth meter, its levels on the same
// dropdown machinery as the rest; "off" not offered to a model that must
// think (it would 400 the turn).
export function ThinkingPill({ level, offDisabled = false, onChange, framed = false }) {
    return (
        <HoverTooltip label={`Thinking depth: ${level}`} placement="top">
            <span className={`inline-flex shrink-0${framed ? " self-stretch" : ""}`}>
                <Select
                    value={level}
                    onChange={onChange}
                    options={THINKING_LEVELS.map((lvl) => ({
                        value: lvl,
                        label: lvl,
                        ...(lvl === "off" && offDisabled
                            ? { disabled: true, title: "This model requires a thinking level" }
                            : {}),
                    }))}
                    align="right"
                    menuWidth={132}
                    ariaLabel="Thinking depth"
                    triggerContent={<ThinkingDepthIcon level={level} />}
                    className={`${framed ? `${COMPOSER_CELL} h-full` : COMPOSER_BOX} text-[color:var(--muted)] hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-[14px] [&_svg]:w-[14px]`}
                />
            </span>
        </HoverTooltip>
    );
}

// The composer's stop, while a run is under way.
export function StopRunButton({ onStop, framed = false }) {
    return (
        <button
            type="button"
            onClick={onStop}
            title="Stop run"
            aria-label="Stop run"
            className={`${framed ? COMPOSER_CELL : `${COMPOSER_BOX} hover:border-[color:var(--red)]`} text-[12px] leading-none text-[color:var(--muted)] transition-colors hover:text-[color:var(--red)] cursor-pointer`}
        >
            ■
        </button>
    );
}

// The composer's shortcut into the shared Settings tab.
export function ModelGear({ label, onOpen, framed = false }) {
    return (
        <HoverTooltip label={label} placement="top">
            <button
                type="button"
                onClick={onOpen}
                aria-label="Open chat settings"
                className={`${framed ? COMPOSER_CELL : COMPOSER_BOX} text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] cursor-pointer [&_svg]:block [&_svg]:h-[14px] [&_svg]:w-[14px]`}
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
            </button>
        </HoverTooltip>
    );
}

// Slash-command picker, on the dashboard's menu surface (rounded,
// its rows raised where the pointer or the arrow keys are). Anchored
// just above the input via the
// parent's `relative` wrapper; `bottom-full` keeps it pinned to the
// input's top edge regardless of how many staged-image chips push
// the input down.
// Multi-line chat input with a syntax-highlighting overlay.
//
// We can't style `<textarea>` content per-token, so we layer two
// elements: an `aria-hidden` div that renders the value as styled
// spans (currently: a leading /slash-command in green), and a
// transparent-text textarea on top that owns editing/selection/caret.
// Both elements share identical typography, padding, and wrapping
// rules so each wrapped line lands on the same baseline in both
// layers; the textarea's scroll position is mirrored to the overlay.
//
// The textarea auto-grows with content up to `maxHeight`; past that
// it scrolls internally. `placeholder` still works because Tailwind's
// `placeholder:` variant overrides the transparent text color for the
// `::placeholder` pseudo only.
//
// The ref exposes a `focus()` method so the existing focus-on-mount
// and post-submit refocus call sites keep working unchanged.
export const ChatInput = forwardRef(function ChatInput(
    { value, onChange, onKeyDown, onPaste, placeholder, disabled = false, maxHeight = 200, band = false, ariaLabel = undefined },
    ref,
) {
    const textareaRef = useRef(null);
    const overlayRef = useRef(null);

    useImperativeHandle(ref, () => ({
        focus: () => textareaRef.current?.focus(),
        blur: () => textareaRef.current?.blur(),
    }));

    // Resize-to-fit. Reset to `auto` first so the textarea's
    // scrollHeight reflects the natural content height even when the
    // user is deleting lines (otherwise scrollHeight stays pinned to
    // the previously-taller layout). Text only added, and still fitting
    // the height the box has (or the box already at its most), needs no
    // reset: each collapse lays the page out again, which a long
    // transcript made slow on every key. `force`: the width or the
    // placeholder changed, which can shrink the fit without a deletion.
    const typedLength = useRef(0);
    const fitHeight = useCallback((force = false) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const grew = ta.value.length >= typedLength.current;
        typedLength.current = ta.value.length;
        if (!force && grew && ta.style.height) {
            if (parseFloat(ta.style.height) >= maxHeight) return;
            if (ta.scrollHeight <= ta.clientHeight) return;
        }
        ta.style.height = "auto";
        const next = Math.min(ta.scrollHeight, maxHeight);
        ta.style.height = `${next}px`;
    }, [maxHeight]);

    useLayoutEffect(() => {
        fitHeight();
    }, [fitHeight, value]);
    // While empty, the wrapped placeholder counts toward scrollHeight, so a
    // placeholder change can change the fit as much as a value change.
    useLayoutEffect(() => {
        fitHeight(true);
    }, [fitHeight, placeholder]);

    // The fit also depends on the wrap width, which changes without the
    // value changing: the dock column animates and resizes, and run buttons
    // join the row. A height measured at a narrower width otherwise stays
    // until the next keystroke. The refit waits a frame because resizing the
    // observed textarea inside its own callback raises ResizeObserver's loop
    // error.
    useEffect(() => {
        const ta = textareaRef.current;
        if (!ta || typeof ResizeObserver === "undefined") return undefined;
        let width = -1;
        let frame = 0;
        const ro = new ResizeObserver((entries) => {
            const next = entries[entries.length - 1].contentRect.width;
            if (next === width) return;
            width = next;
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => fitHeight(true));
        });
        ro.observe(ta);
        return () => {
            ro.disconnect();
            cancelAnimationFrame(frame);
        };
    }, [fitHeight]);

    const onScroll = useCallback(() => {
        const ta = textareaRef.current;
        const ov = overlayRef.current;
        if (!ta || !ov) return;
        ov.scrollTop = ta.scrollTop;
    }, []);

    return (
        // In a composer band (a framed pane) the band's rules are the input's
        // edges: it draws none of its own.
        <div className={`relative w-full bg-[color:var(--chat-input-bg)] ${band ? "h-full" : "rounded-[7px] border border-[color:var(--line)] transition-colors hover:border-[color:var(--line-strong)] focus-within:border-[color:var(--green)]"}${disabled ? " opacity-60" : ""}`}>
            {/* Highlight overlay — sits behind the (transparent) textarea
                text. `whitespace-pre-wrap` + `break-words` matches the
                textarea's wrapping; identical padding and typography
                keeps each wrapped line aligned with the textarea's
                corresponding line. */}
            <div
                ref={overlayRef}
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-[10px] py-[8px] text-[13px] leading-[16px] text-[color:var(--ink)]"
            >
                {renderChatHighlight(value)}
                {/* Trailing-newline guard: when `value` ends with `\n`,
                    the textarea reserves an extra blank line, but a
                    bare \n at the end of a text node may not contribute
                    height under pre-wrap. Append a zero-width char so
                    the overlay's height matches the textarea exactly. */}
                {value.endsWith("\n") ? "​" : null}
            </div>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onScroll={onScroll}
                placeholder={placeholder}
                aria-label={ariaLabel}
                rows={1}
                spellCheck={false}
                disabled={disabled}
                className="relative block w-full resize-none overflow-y-auto border-0 bg-transparent px-[10px] py-[8px] text-[13px] leading-[16px] text-transparent caret-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] disabled:cursor-not-allowed"
            />
        </div>
    );
});

// Render `value` with a leading `/slash-command` token highlighted.
// We only highlight when the whole input is currently a slash token
// (no whitespace yet) — that mirrors `matchSlashCommands` and keeps
// the highlight from sticking around once the user starts typing
// arguments. Once the user types a space, the input is treated as
// freeform prose again.
function renderChatHighlight(value) {
    if (!value) return null;
    if (value[0] === "/" && !/\s/.test(value)) {
        return <span className="text-[color:var(--green)]">{value}</span>;
    }
    return value;
}

// Top-of-viewport breathing room. The picker is anchored to the
// bottom edge (bottom-full); its top can grow up to MARGIN below the
// window's top.
const SLASH_MENU_VIEWPORT_MARGIN = 16;

function SlashMenu({ matches, selectedIndex, onHover, onPick }) {
    const hostRef = useRef(null);
    const footerRef = useRef(null);
    const listHostRef = useRef(null);
    const [maxListHeight, setMaxListHeight] = useState(null);

    // Window-boundary awareness. The picker's bottom edge is pinned
    // just above the input; its top edge can run off-screen when the
    // list grows past the available height. Measure the host's
    // viewport rect plus the footer height and clamp the list to
    // whatever vertical space is left above the input, minus a small
    // margin from the window top.
    useLayoutEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;
        const recompute = () => {
            const rect = host.getBoundingClientRect();
            const footerHeight = footerRef.current?.offsetHeight || 0;
            // rect.bottom is the menu's anchored bottom edge. The list
            // can grow until its top edge reaches MARGIN below the
            // window top, leaving room for the footer below it.
            const available = rect.bottom - SLASH_MENU_VIEWPORT_MARGIN - footerHeight;
            setMaxListHeight(Math.max(80, available));
        };
        recompute();
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(recompute) : null;
        ro?.observe(host);
        window.addEventListener("resize", recompute);
        return () => {
            ro?.disconnect();
            window.removeEventListener("resize", recompute);
        };
    }, [matches.length]);

    // Keep the highlighted row visible as the user navigates with the
    // arrow keys past the scroll boundary.
    useEffect(() => {
        const host = listHostRef.current;
        if (!host) return;
        const viewport = host.querySelector("[data-overlayscrollbars-viewport]") || host;
        const btn = viewport.querySelector(`[data-slash-index="${selectedIndex}"]`);
        if (btn && typeof btn.scrollIntoView === "function") {
            btn.scrollIntoView({ block: "nearest" });
        }
    }, [selectedIndex, matches.length]);

    return (
        <div
            ref={hostRef}
            className={`absolute bottom-full left-0 right-0 mb-[4px] z-[1] flex flex-col overflow-hidden ${MENU}`}
        >
            <ScrollArea
                hostRef={listHostRef}
                maxHeight={maxListHeight != null ? `${maxListHeight}px` : undefined}
                contentClassName="grid gap-[1px] p-[5px]"
            >
                {matches.map((cmd, i) => {
                    const active = i === selectedIndex;
                    return (
                        <button
                            key={cmd.name}
                            type="button"
                            data-slash-index={i}
                            onMouseDown={(e) => {
                                // Use mousedown so the click fires before
                                // the input's blur handler can swallow it.
                                e.preventDefault();
                                onPick(cmd);
                            }}
                            onMouseEnter={() => onHover(i)}
                            className={`flex w-full items-start gap-[10px] rounded-[6px] px-[8px] py-[6px] text-left text-[12px] leading-[1.4] cursor-pointer ${active ? MENU_ITEM_ON : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
                        >
                            <span className="font-mono text-[color:var(--ink)]">{cmd.name}</span>
                            <span className="grid min-w-0 gap-[2px] text-[11px] text-[color:var(--muted-soft)]">
                                <span>{cmd.description}</span>

                            </span>
                        </button>
                    );
                })}
            </ScrollArea>
            <div
                ref={footerRef}
                className="border-t border-[color:var(--line)] px-[10px] py-[5px] text-[11px] text-[color:var(--muted-soft)]"
            >
                ↑↓ navigate · Tab complete · ⏎ run · Esc cancel
            </div>
        </div>
    );
}

// "Working…" indicator for the in-flight agent response. Uses the
// same three-dot pulsing pattern as GridStatus so the dashboard's
// busy-states stay visually consistent.
export function WorkingIndicator({ stalledSince, onStop, onStopAndRetry }) {
    const [tick, setTick] = useState(0);
    useEffect(() => {
        if (!stalledSince) return undefined;
        // Refresh the elapsed counter once per second while stalled so
        // the user sees the duration grow. Cleared when the stall
        // resolves or the indicator unmounts.
        const t = setInterval(() => setTick((v) => v + 1), 1000);
        return () => clearInterval(t);
    }, [stalledSince]);
    if (stalledSince) {
        const secs = Math.max(0, Math.floor((Date.now() - stalledSince) / 1000));
        return (
            <div className="flex flex-wrap items-center gap-[8px] text-[12px] text-[color:var(--muted-soft)]">
                <span className="inline-flex items-center gap-[4px]" aria-hidden="true">
                    <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:0ms]" />
                    <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:160ms]" />
                    <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:320ms]" />
                </span>
                <span>
                    No events for {secs}s
                </span>
                <span className="text-[color:var(--muted-soft)]">
                    The upstream service may be stalled.
                </span>
                <div className="flex items-center gap-[6px]">
                    {onStopAndRetry ? (
                        <Button variant="primary" size="sm" onClick={onStopAndRetry}>
                            Stop &amp; retry
                        </Button>
                    ) : null}
                    {onStop ? (
                        <Button size="sm" onClick={onStop}>
                            Stop
                        </Button>
                    ) : null}
                </div>
            </div>
        );
    }
    // Suppress useless ref to `tick` — the state exists only to drive
    // the elapsed counter above. Linters don't like an unused binding,
    // so consume it harmlessly here.
    void tick;
    return (
        <div className="flex items-center gap-[8px] text-[12px] text-[color:var(--muted-soft)]">
            <span className="inline-flex items-center gap-[4px]" aria-hidden="true">
                <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:0ms]" />
                <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:160ms]" />
                <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:320ms]" />
            </span>
            <span className="animate-pulse">Working</span>
        </div>
    );
}

// ── Tree renderer ──────────────────────────────────────────────────
//
// Items at the top level alternate user prompts and turn nodes. A turn
// contains assistant/thinking/tool children; tools may carry details
// the user can drill into. The tree exposes the same structure Pi
// emits — turn boundaries are first-class, every tool call is a
// discrete branch, and thinking deltas surface as their own block
// rather than being hidden.

function Chevron({ expanded }) {
    return (
        <svg
            viewBox="0 0 10 10"
            className={[
                "h-[9px] w-[9px] shrink-0 text-[color:var(--muted-soft)] transition-transform duration-150",
                expanded ? "rotate-90" : "",
            ].join(" ")}
            aria-hidden="true"
        >
            <path d="M3.5 2L6.5 5L3.5 8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function StatusDot({ status }) {
    const color =
        status === "error"
            ? "var(--red)"
            : status === "done"
                ? "var(--green)"
                : "var(--muted)";
    // "preparing" (LLM still typing args), "queued" (awaiting tool
    // execution), and "running" all pulse — they're in-progress states
    // that haven't reached a terminal result yet.
    const pulse =
        status === "preparing" || status === "queued" || status === "running"
            ? "animate-pulse"
            : "";
    return (
        <span
            aria-hidden="true"
            className={`h-[7px] w-[7px] shrink-0 rounded-full ${pulse}`}
            style={{ backgroundColor: color }}
        />
    );
}

// Container that draws the vertical guide line for indented children.
// A turn's steps, held in a panel rather than indented under its line.
function Branch({ children }) {
    return (
        <div className="mt-[6px] flex flex-col gap-[8px] rounded-[2px] bg-[color:var(--panel)] px-[12px] py-[8px]">
            {children}
        </div>
    );
}

// Memoized: while an answer streams, only the turn it is writing changes, so
// every earlier item (the same node, the same handlers) is left as drawn
// rather than drawn again, markdown and all, on every token.
export const TreeItem = memo(function TreeItem({ index, node, toggled, onToggle, onOpenScope, linkifyProse, onFork, onRewindTurn, onReplayTurn, onEditUser, onRewindUser, onForkUser, onSettleJob, bare = false, chatBusy = false }) {
    if (node.kind === "user") return <UserNode node={node} index={index} onEdit={onEditUser} onRewind={onRewindUser} onFork={onForkUser} />;
    if (node.kind === "job" && node.job === "controlProposalEntry") return <ControlProposalEntry cardKey={node.key} choice={node.controlChoice} chatBusy={chatBusy} />;
    if (node.kind === "job" && node.job === "controlProposal") return <ControlProposalCard cardKey={node.key} chatBusy={chatBusy} />;
    if (node.kind === "job" && node.job === "policyChat") return <PolicyChatCard cardKey={node.key} savedSession={node.policySession} savedView={node.policyView} chatBusy={chatBusy} />;
    if (node.kind === "job") return <JobNode node={node} onSettle={onSettleJob} toggled={toggled} onToggle={onToggle} onOpenScope={onOpenScope} linkifyProse={linkifyProse}   />;
    if (node.kind === "turn") {
        return (
            <TurnNode
                node={node}
                toggled={toggled}
                onToggle={onToggle}
                onOpenScope={onOpenScope}
                linkifyProse={linkifyProse}
                onFork={onFork}
                onRewind={onRewindTurn}
                onReplay={onReplayTurn}

                bare={bare}
            />
        );
    }
    if (node.kind === "system") return <SystemNode node={node} />;

    if (node.kind === "audit") return <AuditNode node={node} />;
    // Fallback for stray top-level assistant items (e.g. error blurbs
    // emitted before any turn started — shouldn't happen, but render
    // safely.)
    if (node.kind === "assistant") return <AssistantNode node={node} linkifyProse={linkifyProse} />;
    return null;
});

// A walkthrough the board ran for the reader in this chat (lib/chatJobs.js),
// drawn as the chat's own walk_through call is: what it reads while it runs,
// the story's card once done. The words it came to are kept on the row
// (`onSettle`), for a browser that does not hold the walkthrough.
export function JobNode({ node, onSettle = null, ...rest }) {
    const walk = useWalkthrough(node.key);
    const held = !!(walk.account || walk.notes.length);
    const running = walk.status === "running";
    const moments = held ? walk.notes.map((n) => String(n.text || "").slice(0, 300)) : node.moments || [];
    useEffect(() => {
        if (walk.status !== "done" || !held || node.settled) return;
        onSettle?.(node.id, { settled: true, account: walk.account || "", moments });
    }, [walk.status, held, node.settled, node.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const kept = held || !!node.settled;
    const failed = !running && !kept;
    const tool = {
        kind: "tool",
        id: node.id,
        name: "walk_through",
        status: running ? "running" : failed ? "error" : "done",
        summary: running ? ""
            : failed ? (walk.status === "error" ? `The walkthrough failed: ${walk.error || "it did not finish"}` : "Stopped before it finished")
                : "Walked through",
        details: running ? { activity: walk.now || "Reading the trace's record" }
            : kept ? { kind: "walkthrough", key: node.key, account: held ? walk.account : node.account || "", moments }
                : null,
    };
    return <ToolNode node={tool} {...rest} />;
}

function ChatRuleHeader({ as: Tag = "div", className = "", onClick, disabled, leading = null, primary, trailing = null, detail = null, actions = null }) {
    const tagProps = Tag === "button" ? { type: "button", onClick, disabled } : {};
    return (
        <div className="sticky top-0 z-[2] -mx-[16px] w-[calc(100%+32px)] bg-[color:var(--bg)] px-[16px] py-[4px]">
            <Tag
                {...tagProps}
                className={`flex w-full items-center gap-[8px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] ${className}`}
            >
                <span className="h-px w-[10px] shrink-0 bg-[color:var(--line)]" aria-hidden="true" />
                {leading}
                <span className="min-w-0 shrink truncate">{primary}</span>
                {trailing != null ? (
                    <span className="shrink-0 normal-case tracking-normal">{trailing}</span>
                ) : null}
                <span className="h-px flex-1 bg-[color:var(--line)]" aria-hidden="true" />
                {actions}
            </Tag>
            {detail != null && detail !== "" ? (
                <div className="mt-[2px] pl-[18px] text-[10px] normal-case leading-[1.4] tracking-normal text-[color:var(--muted-soft)] [overflow-wrap:anywhere]">
                    {detail}
                </div>
            ) : null}
        </div>
    );
}

export function SystemNode({ node }) {
    const [expanded, setExpanded] = useState(!!node.expanded);
    const hasSnapshot = Array.isArray(node.snapshot) && node.snapshot.length > 0;
    const hasCost = !!node.cost;
    const hasLessons = Array.isArray(node.lessons) && node.lessons.length > 0;
    const hasRecipes = Array.isArray(node.recipes) && node.recipes.length > 0;
    const canExpand = hasSnapshot || hasCost || hasLessons || hasRecipes;

    return (
        <div className="my-[2px] flex flex-col gap-[6px]">
            <ChatRuleHeader
                as="button"
                onClick={canExpand ? () => setExpanded((v) => !v) : undefined}
                disabled={!canExpand}
                className={canExpand ? "hover:text-[color:var(--ink)] cursor-pointer" : ""}
                leading={canExpand ? <Chevron expanded={expanded} /> : null}
                primary={node.text}
                detail={node.detail || null}
                trailing={hasSnapshot
                    ? <>· {node.snapshot.length} {node.snapshot.length === 1 ? "message" : "messages"}</>
                    : hasLessons
                        ? <>· {node.lessons.length} {node.lessons.length === 1 ? "lesson" : "lessons"}</>
                        : hasRecipes
                            ? <>· {node.recipes.length} {node.recipes.length === 1 ? "recipe" : "recipes"}</>
                            : null}
            />
            {expanded && hasSnapshot ? <SnapshotView messages={node.snapshot} /> : null}
            {expanded && hasCost ? <CostView totals={node.cost} /> : null}
            {expanded && hasLessons ? <LessonsView lessons={node.lessons} /> : null}
            {expanded && hasRecipes ? <RecipesView recipes={node.recipes} /> : null}
        </div>
    );
}

// The recipes the harness can run, in the lessons block's language: name,
// source, runs and id on a mono header line, then the trigger, the steps
// and the parameters wrapping underneath.
function RecipesView({ recipes }) {
    return (
        <div className="flex flex-col gap-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[10px] text-[11px] leading-[1.45] text-[color:var(--ink)]" data-recipes-view>
            {recipes.map((r) => (
                <div key={r.id} className="flex flex-col gap-[2px]">
                    <div className={`flex min-w-0 items-baseline gap-[10px] font-mono text-[10px] text-[color:var(--muted-soft)]${r.status === "archived" ? " opacity-60" : ""}`}>
                        <span className="min-w-0 truncate text-[color:var(--ink)]">{r.name}</span>
                        <span className="shrink-0">{r.source}</span>
                        {r.confidence ? <span className="shrink-0 tabular-nums">{r.confidence}</span> : null}
                        <span className="shrink-0 tabular-nums">{r.uses ? `${r.uses} run${r.uses === 1 ? "" : "s"} \u00b7 ${r.successes} with supported claims` : "not run yet"}</span>
                        {r.orgUses ? <span className="shrink-0 tabular-nums" data-recipe-org-record>{`${r.orgUses} run${r.orgUses === 1 ? "" : "s"} across the org \u00b7 ${r.orgSuccesses} with supported claims${r.orgBrowsers > 1 ? ` \u00b7 ${r.orgBrowsers} browsers` : ""}`}</span> : null}
                        {Number.isFinite(r.savedAvg) ? <span className="shrink-0 tabular-nums">{r.savedAvg >= 0 ? `saves ${r.savedAvg} call${r.savedAvg === 1 ? "" : "s"}` : `costs ${-r.savedAvg} call${r.savedAvg === -1 ? "" : "s"}`}</span> : null}
                        <span className="min-w-0 truncate">{r.id}</span>
                        {r.tags ? <span className="min-w-0 truncate">{r.tags}</span> : null}
                    </div>
                    <div className="whitespace-pre-wrap break-words">when {r.when}</div>
                    {r.org ? <div className="whitespace-pre-wrap break-words text-[color:var(--muted)]" data-recipe-org>{orgProvenanceLine(r.org)}</div> : null}
                    {r.paraphrases?.length ? <div className="whitespace-pre-wrap break-words text-[color:var(--muted)]">also: {r.paraphrases.join(" \u00b7 ")}</div> : null}
                    <div className="whitespace-pre-wrap break-words text-[color:var(--muted)]">{r.steps}</div>
                    {r.params || r.exampleTerm ? (
                        <div className="whitespace-pre-wrap break-words text-[color:var(--muted)]">
                            {r.params ? `params: ${r.params}` : null}
                            {r.params && r.exampleTerm ? " \u00b7 " : null}
                            {r.exampleTerm ? `example term: ${r.exampleTerm}` : null}
                        </div>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

// The harness memory's lessons, one block per entry: id, effective
// confidence, scope and tags on a mono header line, the rule wrapping
// underneath, and the shape change that fixed it when one was recorded.
function LessonsView({ lessons }) {
    return (
        <div className="flex flex-col gap-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[10px] text-[11px] leading-[1.45] text-[color:var(--ink)]">
            {lessons.map((l) => (
                <div key={l.id} className="flex flex-col gap-[2px]">
                    <div className="flex min-w-0 items-baseline gap-[10px] font-mono text-[10px] text-[color:var(--muted-soft)]">
                        <span className="shrink-0">{l.id}</span>
                        <span className="shrink-0 tabular-nums">{l.confidence}</span>
                        <span className="min-w-0 truncate">{l.scope}{l.errorClass ? ` / ${l.errorClass}` : ""}</span>
                        {l.tags ? <span className="min-w-0 truncate">{l.tags}</span> : null}
                    </div>
                    <div className="whitespace-pre-wrap break-words">{l.rule}</div>
                    {l.fix ? (
                        <div className="whitespace-pre-wrap break-words text-[color:var(--muted)]">what worked: {l.fix}</div>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

function AuditNode({ node }) {
    const status = node.status || "running";
    const dotColor =
        status === "done"
            ? "var(--green)"
            : status === "error"
                ? "var(--red, #c0392b)"
                : "var(--ink)";
    const slot = node.slot || null;
    const findings = Array.isArray(node.result?.findings) ? node.result.findings : null;
    const summaryText = typeof node.result?.summary === "string" ? node.result.summary : null;
    const statusLabel =
        status === "running" ? (slot?.activity || "running…")
        : status === "stopped" ? "stopped"
        : status === "error" ? `error · ${node.error || "audit failed"}`
        : findings ? `${findings.length} finding${findings.length === 1 ? "" : "s"}`
        : "done";

    return (
        <div className="my-[2px] flex flex-col gap-[6px]">
            <ChatRuleHeader
                leading={
                    <span
                        className={`inline-block h-[7px] w-[7px] shrink-0 rounded-full ${status === "running" ? "animate-pulse" : ""}`}
                        style={{ background: dotColor }}
                        aria-hidden="true"
                    />
                }
                primary={<>auditing · <span className="font-mono normal-case tracking-normal text-[color:var(--ink)]">chat {node.targetChatId}</span></>}
                detail={statusLabel}
                actions={status === "running" && typeof node.abort === "function"
                    ? <SubAgentStopButton onClick={node.abort} />
                    : null}
            />
            {(status === "running" || status === "stopped") && slot ? (
                <AuditProgress slot={slot} />
            ) : null}
            {status === "done" && findings ? (
                <AuditFindings findings={findings} summary={summaryText} node={node} />
            ) : null}
            {status === "error" && node.error ? (
                <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
                    {node.error}
                </div>
            ) : null}
        </div>
    );
}

function AuditProgress({ slot }) {
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    const messages = Array.isArray(slot?.messages) ? slot.messages : [];
    return (
        <div className="space-y-[6px]">
            <SubAgentSlot slot={slot} />
            {messages.length ? (
                <div className="flex justify-end">
                    <SubAgentTranscriptToggle
                        open={transcriptOpen}
                        onToggle={() => setTranscriptOpen((v) => !v)}
                        count={messages.length}
                    />
                </div>
            ) : null}
            {transcriptOpen && messages.length ? (
                <SubAgentTranscript messages={messages} />
            ) : null}
        </div>
    );
}

function AuditFindings({ findings, summary, node }) {
    const downloadOne = useCallback(async (format) => {
        const [{ downloadAudits }, { getChatById }] = await Promise.all([
            import("../lib/exportAudits.js"),
            import("../lib/chatRegistry.js"),
        ]);
        // Wrap this single audit in a chat-shaped container so
        // downloadAudits doesn't need a per-audit code path.
        // referencePool is the actual target chat (looked up via
        // the registry) so the referenced-transcripts section in
        // the export resolves cited tool_call_ids properly.
        const wrapper = {
            id: node?.targetChatId || "?",
            title: node?.targetChatId ? `audit of chat ${node.targetChatId}` : "audit",
            items: [node],
        };
        const target = node?.targetChatId != null ? getChatById(node.targetChatId) : null;
        const referencePool = target ? [target] : [];
        downloadAudits([wrapper], { format, referencePool });
    }, [node]);
    if (!findings.length) {
        return (
            <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
                Audit complete. No findings.
                {summary ? <div className="mt-[4px] text-[color:var(--muted-soft)]">{summary}</div> : null}
            </div>
        );
    }
    return (
        <div className="space-y-[6px]">
            <div className="flex items-center justify-end gap-[8px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                <button
                    type="button"
                    onClick={() => downloadOne("md")}
                    className="hover:text-[color:var(--ink)] uppercase"
                    title="Download these findings as Markdown"
                >
                    ↓ md
                </button>
                <button
                    type="button"
                    onClick={() => downloadOne("json")}
                    className="hover:text-[color:var(--ink)] uppercase"
                    title="Download these findings as JSON"
                >
                    ↓ json
                </button>
            </div>
            {summary ? (
                <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                    {summary}
                </div>
            ) : null}
            <div className="space-y-[4px]">
                {findings.map((f, i) => <AuditFindingRow key={i} finding={f} />)}
            </div>
        </div>
    );
}

function AuditFindingRow({ finding }) {
    const [open, setOpen] = useState(false);
    const sev = finding?.severity || "observation";
    const sevColor =
        sev === "bug" ? "var(--red, #c0392b)"
        : sev === "improvement" ? "var(--green)"
        : "var(--muted)";
    const evidence = Array.isArray(finding?.evidence_tool_call_ids) ? finding.evidence_tool_call_ids : [];
    const issueId = typeof finding?.linear_issue_id === "string" ? finding.linear_issue_id : null;
    return (
        <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)]">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-[8px] px-[10px] py-[6px] text-left text-[11px] text-[color:var(--ink)]"
            >
                <span
                    className="inline-block h-[6px] w-[6px] shrink-0 rounded-full"
                    style={{ background: sevColor }}
                    aria-hidden="true"
                />
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">{sev}</span>
                {finding?.category ? (
                    <span className="font-mono text-[10px] text-[color:var(--muted-soft)]">· {finding.category}</span>
                ) : null}
                <span className="truncate font-medium">{finding?.title || "(no title)"}</span>
                {issueId ? (
                    <span className="ml-auto font-mono text-[10px] text-[color:var(--muted)]">{issueId}</span>
                ) : null}
            </button>
            {open ? (
                <div className="space-y-[4px] border-t border-[color:var(--line)] px-[10px] py-[8px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
                    {finding?.body ? <div className="whitespace-pre-wrap">{finding.body}</div> : null}
                    {evidence.length ? (
                        <div className="text-[10px] text-[color:var(--muted-soft)]">
                            evidence: {evidence.map((id) => <span key={id} className="font-mono">{id} </span>)}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function saveStatusLabel(node) {
    if (node.status === "done") return `saved · invoke with /${node.command}`;
    if (node.status === "error") return `error · ${node.error || "save failed"}`;
    return node.slot?.activity || "running…";
}

const judgeCache = new Map();
onAuthIdentityChange(() => judgeCache.clear());

// Inline card for activity navigation (open_in_workspace /
// open_studio_item). Two shapes share it, keyed on details.kind:
//   • navSuggestion — the agent OFFERED to navigate (it was volunteering,
//     or the chat is a floating overlay). Render a one-click button that
//     fires the intent; App applies it and a floating overlay closes.
//   • navApplied — the agent already navigated (docked + the user asked).
//     Render a static confirmation with a re-open button in case the
//     user has since moved away.
// Firing goes through navigationStore.requestNavigation directly — the
// same channel the tool used — so we don't thread another handler down
// the transcript tree.
// Shared progress read for a scout run — used both by the in-console
// progress panel (empty state) and the profile tab. Returns the steps
// seen so far (summarized per tool — see lib/scoutSteps) + a single live
// "what it's doing now" line in the spirit of the sub-agent progress
// line: prefer a tool that's currently running, else the latest
// assistant line / thinking.
function scoutProgress(chat) {
    const steps = [];
    let latest = "";
    for (const it of (chat?.items || [])) {
        if (it?.kind !== "turn") continue;
        for (const ch of (it?.children || [])) {
            if (ch?.kind === "tool" && ch?.name) steps.push(summarizeScoutStep(ch));
            else if (ch?.kind === "assistant" && typeof ch?.text === "string" && ch.text.trim()) latest = ch.text.trim();
        }
    }
    let liveAction = "Starting…";
    const turns = (chat?.items || []).filter((it) => it?.kind === "turn");
    const lastKids = turns.length ? (turns[turns.length - 1].children || []) : [];
    const runningTool = [...lastKids].reverse().find(
        (c) => c?.kind === "tool" && (c.status === "running" || c.status === "preparing" || c.status === "queued"));
    const lastChild = lastKids[lastKids.length - 1];
    if (runningTool) liveAction = `${runningTool.name} · running…`;
    else if (lastChild?.kind === "tool" && lastChild?.name) liveAction = `${lastChild.name} · done`;
    else if (lastChild?.kind === "assistant" && typeof lastChild?.text === "string" && lastChild.text.trim()) liveAction = lastChild.text.trim();
    else if (lastChild?.kind === "thinking") liveAction = "Thinking…";
    else if (latest) liveAction = latest;
    if (liveAction.length > 120) liveAction = `${liveAction.slice(0, 117)}…`;
    return { steps, liveAction };
}

function scoutResult(chat) {
    let cardsOut = null;
    for (const it of (chat?.items || [])) {
        if (it?.kind !== "turn") continue;
        for (const ch of (it?.children || [])) {
            if (ch?.kind !== "tool") continue;
            let a = ch.args;
            if (typeof a === "string") { try { a = JSON.parse(a); } catch { a = null; } }
            if (!a) continue;
            if (ch.name === "set_starter_cards" && Array.isArray(a.cards)) {
                const cards = a.cards
                    .filter((c) => c && typeof c.title === "string" && typeof c.body === "string" && c.title.trim() && c.body.trim())
                    .map((c) => ({ title: c.title.trim(), body: c.body.trim() }));
                if (cards.length) cardsOut = { profile: typeof a.profile === "string" ? a.profile.trim() : "", cards };
            }
        }
    }
    if (!cardsOut) return null;
    return {
        profile: cardsOut?.profile || "",
        cards: cardsOut?.cards?.length ? cardsOut.cards : null,
    };
}

// Custom render for a kind:"scout" chat — the "Tailor these to your
// data" profiling run. Instead of a raw transcript it shows a tidy
// progress panel while the run is in flight, then the org profile + the
// tailored cards it produced (clickable to run in a fresh chat), plus a
// collapsible step list for "how this was computed". The run still
// persists as a normal chat (items / agentMessages), so it's saved and
// exportable — only the presentation differs.

export function TenantProfileView({ chat, onPickCard }) {
    const busy = !!chat?.busy;
    const { steps, liveAction } = scoutProgress(chat);
    // Source profile + cards from THIS run's own transcript (its
    // set_starter_cards call), not the global store — so a rehydrated,
    // interrupted, or repeat run shows what IT produced, not the latest
    // tenant-wide set. No result → the honest "didn't finish" copy below.
    const result = scoutResult(chat);
    const profile = result?.profile || "";
    const cards = result?.cards || null;

    // While the run is in flight: a contained progress panel (the same
    // treatment the starter sheet used) — eyebrow + a live one-line read
    // of the current step + a short caption. The tailored profile + cards
    // land in this same tab when it finishes.
    if (busy) {
        return (
            <div className="mx-auto flex w-full max-w-[640px] min-w-0 flex-col gap-[14px]">
                <div className="flex flex-col gap-[6px]">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        Tenant profile
                    </span>
                    <h2 className="m-0 text-[16px] leading-[1.25] text-[color:var(--ink)]">
                        Profiling your tenant…
                    </h2>
                </div>
                <div className="flex flex-col gap-[10px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] p-[12px]">
                    <div className="flex items-center gap-[7px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[7px] text-[11px] leading-[1.4]">
                        <span className="inline-block h-[6px] w-[6px] shrink-0 rounded-full bg-[color:var(--green)] animate-pulse" />
                        <span className="min-w-0 flex-1 truncate font-mono text-[color:var(--muted)]">{liveAction}</span>
                        <span className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">{steps.length} {steps.length === 1 ? "step" : "steps"}</span>
                    </div>
                    <p className="m-0 text-[11px] leading-[1.5] text-[color:var(--muted-soft)]">
                        Sampling topics and endpoints to create starter queries.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto flex w-full max-w-[640px] min-w-0 flex-col gap-[14px]">
            <div className="flex flex-col gap-[6px]">
                <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                    Tenant profile
                </span>
                <h2 className="m-0 text-[16px] leading-[1.25] text-[color:var(--ink)]">
                    What your org does with AI
                </h2>
                {profile ? (
                    <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--muted)]">{profile}</p>
                ) : (
                    <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--muted)]">
                        This run ended without a tailored set. Review the steps below or run it again.
                    </p>
                )}
            </div>

            {cards?.length ? (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-[6px]">
                    {cards.map((q) => (
                        <button
                            key={q.title}
                            type="button"
                            onClick={() => onPickCard?.(q.body, { skipScope: chat?.skipScope })}
                            className="group flex flex-col gap-[4px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[8px] text-left transition-colors hover:border-[color:var(--line-strong)] cursor-pointer"
                        >
                            <span className="flex items-baseline justify-between gap-[6px] text-[11px] leading-[1.2] text-[color:var(--ink)]">
                                {q.title}
                                <span className="text-[color:var(--muted-soft)] transition-colors group-hover:text-[color:var(--green)]">↗</span>
                            </span>
                            <span className="text-[11px] leading-[1.4] text-[color:var(--muted-soft)] transition-colors group-hover:text-[color:var(--muted)]">
                                {q.body}
                            </span>
                        </button>
                    ))}
                </div>
            ) : null}

            {steps.length ? (
                <details className="text-[11px] leading-[1.4] text-[color:var(--muted-soft)]">
                    <summary className="cursor-pointer text-[10px] uppercase tracking-[0.14em] hover:text-[color:var(--ink)]">
                        {steps.length} {steps.length === 1 ? "step" : "steps"} in this run
                    </summary>
                    {/* Grouped reasoning trace: each block is the run's own
                        narration (assistant text, else clipped thinking) with
                        the tool calls it explains attached beneath it on a
                        tinted strip — the WHY contained with its queries. */}
                    <div className="mt-[6px] flex flex-col gap-[6px]">
                        {buildScoutTrace(chat).map((g, gi) => (
                            <div key={gi} className="border border-[color:var(--line)] bg-[color:var(--bg)]">
                                {g.note ? (
                                    <p className="m-0 px-[10px] py-[7px] text-[10px] leading-[1.55] text-[color:var(--muted)] [overflow-wrap:anywhere]">
                                        {g.note.text}
                                    </p>
                                ) : null}
                                {g.steps.map((s, si) => (
                                    <div
                                        key={`${s.name}:${si}`}
                                        className={[
                                            "flex items-baseline gap-[8px] bg-[color:var(--control-bg)] px-[10px] py-[5px] text-[10px] leading-[1.5]",
                                            g.note || si > 0 ? "border-t border-[color:var(--line)]" : "",
                                        ].join(" ")}
                                    >
                                        <span className="shrink-0 font-mono text-[color:var(--muted)]">{s.name}</span>
                                        {s.summary ? (
                                            <span className="min-w-0 flex-1 [overflow-wrap:anywhere] text-[color:var(--muted-soft)]">{s.summary}</span>
                                        ) : null}
                                        {s.error ? (
                                            <span className="ml-auto shrink-0 font-mono text-[color:var(--red,#c0392b)]">failed</span>
                                        ) : s.rows != null ? (
                                            <span className="ml-auto shrink-0 font-mono text-[color:var(--muted-soft)]">{s.rows} {s.rows === 1 ? "row" : "rows"}</span>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </details>
            ) : null}
        </div>
    );
}

function NavCard({ details }) {
    const intent = details?.intent || null;
    if (!intent || intent.kind === "canvas" || intent.kind === "studio") return null;
    const label = details?.label || "view";
    const dest = intent.kind === "trace" || intent.scopePath?.[0]?.dim !== "endpoints" || intent.scopePath.length !== 1 || (intent.scopePath[0].ids || []).length !== 1 ? "Investigations" : "Fleet";
    const applied = details?.kind === "navApplied";
    return (
        <div className="mt-[4px] flex items-center gap-[10px] border border-[color:var(--line)] bg-[color:var(--green-soft)] px-[10px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--ink)]">
            <span className="min-w-0 flex-1 truncate">
                {applied ? (
                    <>
                        <span className="text-[color:var(--green)]" aria-hidden="true">↗ </span>
                        Opened <span className="text-[color:var(--ink)]">{label}</span> in {dest}
                    </>
                ) : (
                    <>Open <span className="text-[color:var(--ink)]">{label}</span> in {dest}</>
                )}
            </span>
            <button
                type="button"
                onClick={() => requestNavigation(intent)}
                className="shrink-0 cursor-pointer border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[2px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--ink)] hover:border-[color:var(--green)]"
            >
                {applied ? "reopen" : `open in ${dest.toLowerCase()} →`}
            </button>
        </div>
    );
}

// Post-action shell. Keeps the boxed framing of the active card so
// the suggestion doesn't visually disappear from the transcript when
// the user clicks through — they get a calm row that mirrors the
// header they just saw, but with the buttons swapped out for a
// status line. Two tones today (muted for dismissed, green for
// kicked-off), wired by name rather than colour string so the call
// sites stay declarative.

function CostView({ totals }) {
    const rows = [
        ["Input", totals.input, totals.cost.input],
        ["Output", totals.output, totals.cost.output],
        ["Cache read", totals.cacheRead, totals.cost.cacheRead],
        ["Cache write", totals.cacheWrite, totals.cost.cacheWrite],
    ];
    return (
        <div className="flex flex-col gap-[4px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[10px] font-mono text-[11px] leading-[1.45] text-[color:var(--ink)]">
            {rows.map(([label, tokens, cost]) => (
                <div key={label} className="flex items-baseline justify-between gap-[12px]">
                    <span className="text-[color:var(--muted-soft)]">{label}</span>
                    <span className="text-[color:var(--muted-soft)] tabular-nums">
                        {tokens.toLocaleString()} tok
                    </span>
                    <span className="tabular-nums">{formatUsd(cost)}</span>
                </div>
            ))}
            <div className="mt-[4px] flex items-baseline justify-between gap-[12px] border-t border-[color:var(--line)] pt-[4px]">
                <span>Total</span>
                <span className="text-[color:var(--muted-soft)] tabular-nums">
                    {totals.totalTokens.toLocaleString()} tok
                </span>
                <span className="tabular-nums">{formatUsd(totals.cost.total)}</span>
            </div>
        </div>
    );
}

function SnapshotView({ messages }) {
    return (
        <div className="flex flex-col gap-[6px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[10px] text-[11px] leading-[1.45] text-[color:var(--ink)]">
            {messages.map((m, i) => (
                <SnapshotMessage key={i} message={m} />
            ))}
        </div>
    );
}

function SnapshotMessage({ message }) {
    const role = message.role;
    const label =
        role === "toolResult"
            ? `toolResult · ${message.toolName || "tool"}${message.isError ? " · error" : ""}`
            : role === "notification"
                ? "notification"
                : role;
    return (
        <div className="flex flex-col gap-[3px]">
            <div className="text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                {label}
            </div>
            <pre className="m-0 whitespace-pre-wrap break-words font-mono text-[11px] leading-[1.4] text-[color:var(--ink)] [overflow-wrap:anywhere]">
                {snapshotMessageBody(message)}
            </pre>
        </div>
    );
}

function snapshotMessageBody(m) {
    if (m.role === "notification") return m.text || "";
    if (typeof m.content === "string") return m.content;
    if (!Array.isArray(m.content)) return "";
    const parts = [];
    for (const c of m.content) {
        if (!c) continue;
        if (c.type === "text" && c.text) parts.push(c.text);
        else if (c.type === "thinking" && c.text) parts.push(`[thinking] ${c.text}`);
        else if (c.type === "toolCall") {
            // pi-ai state.messages store args under `arguments`; staged/SDK
            // shapes use `input`/`args`. Read all three so the snapshot
            // pane shows real tool-call args, not `{}`.
            const args = c.arguments ?? c.input ?? c.args ?? {};
            parts.push(`[toolCall] ${c.name}(${JSON.stringify(args)})`);
        } else if (c.type === "image") {
            parts.push(`[image · ${c.mimeType || "unknown"}]`);
        }
    }
    return parts.join("\n");
}

function UserNode({ node, index, onEdit, onRewind, onFork }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(node.text);
    // Edits only make sense on top-level user prompts that drove a
    // turn — steered/follow-up messages get queued separately, and
    // editing them mid-run has no clean semantics. Also need an edit
    // callback + index to know where to truncate.
    const hasIndex = !node.queued && typeof index === "number";
    const canEdit = hasIndex && onEdit;
    // Rewind (truncate downstream in place) + fork (branch to a new tab)
    // share the same anchor as edit — a top-level prompt with a known
    // index — and give a clean way to retry from this point without the
    // downstream slash-command / save-attempt turns coming along.
    const canRewind = hasIndex && onRewind;
    const canFork = hasIndex && onFork;

    const commit = () => {
        setEditing(false);
        if (draft.trim() && draft.trim() !== node.text) {
            onEdit(index, draft);
        }
    };
    const cancel = () => {
        setDraft(node.text);
        setEditing(false);
    };

    if (editing) {
        return (
            <div>
                <div className="mb-[4px] text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">
                    Editing
                </div>
                <textarea
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            commit();
                        } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancel();
                        }
                    }}
                    rows={Math.max(2, draft.split("\n").length)}
                    className={`${FIELD} block w-full resize-y py-[6px] leading-[1.3]`}
                />
                <div className="mt-[6px] flex items-center gap-[6px] text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">
                    <Button variant="primary" size="sm" onClick={commit}>
                        Rerun
                    </Button>
                    <Button variant="ghost" size="sm" onClick={cancel}>
                        Cancel
                    </Button>
                    <span className="text-[color:var(--muted-soft)]">
                        ⏎ rerun · esc cancel · rewinds everything after
                    </span>
                </div>
            </div>
        );
    }

    // The reader's words sit on the panel, so a turn is told apart by its
    // ground rather than a label over it.
    return (
        <div className="group relative rounded-[2px] bg-[color:var(--panel)] px-[12px] py-[9px]">
            <div className="flex items-center gap-[6px] text-[11px] leading-[1.2] text-[color:var(--muted-soft)] empty:hidden">
                {node.queued ? (
                    <span className="mb-[4px] rounded-[5px] border border-[color:var(--line)] px-[5px] py-[1px] text-[10px] text-[color:var(--muted)]">
                        {node.queued === "post" ? "Queued after this run" : "Queued mid-run"}
                    </span>
                ) : null}
                {canEdit || canRewind || canFork ? (
                    <div className="absolute right-[10px] top-[8px] flex items-center gap-[10px] bg-[color:var(--panel)] pl-[8px] opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        {canEdit ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setDraft(node.text);
                                    setEditing(true);
                                }}
                                title="Edit and rerun from here"
                                aria-label="Edit and rerun"
                                className="cursor-pointer text-[11px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                            >
                                Edit
                            </button>
                        ) : null}
                        {canRewind ? (
                            <button
                                type="button"
                                onClick={() => onRewind(index)}
                                title="Discard later messages and return this text to the composer"
                                aria-label="Rewind to here"
                                className="cursor-pointer text-[11px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                            >
                                Rewind
                            </button>
                        ) : null}
                        {canFork ? (
                            <button
                                type="button"
                                onClick={() => onFork(index)}
                                title="Start a new chat from here and keep this chat unchanged"
                                aria-label="Fork from here"
                                className="cursor-pointer text-[11px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                            >
                                Fork
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </div>
            {Array.isArray(node.images) && node.images.length ? (
                <div className="mb-[4px] flex flex-wrap gap-[6px]">
                    {node.images.map((img) => (
                        <img
                            key={img.id}
                            src={img.previewUrl}
                            alt="attachment"
                            className="max-h-[120px] max-w-[180px] border border-[color:var(--line)] object-cover"
                        />
                    ))}
                </div>
            ) : null}
            {node.text ? (
                <div className="whitespace-pre-wrap [overflow-wrap:anywhere]">{node.text}</div>
            ) : null}
            <AddedList items={node.added} />
        </div>
    );
}

// `bare`: one turn of a run, inside the run's panel: its steps alone, the
// run's line standing for its header.
function TurnNode({ node, toggled, onToggle, onOpenScope, linkifyProse, onFork, onRewind, onReplay, bare = false }) {
    // The run's answer reads open with no header; a finished turn that only
    // did work folds to its line; a running one shows its work as it goes.
    const quiet = node.status === "done" && !!node.final;
    const kind = node.status === "done" && !node.final ? "turnWork" : "turn";
    const expanded = isNodeExpanded(kind, node.id, toggled);
    const visibleChildren = visibleTurnChildren(node);
    const childCount = visibleChildren.length;
    const toolCount = visibleChildren.filter((c) => c.kind === "tool").length;
    const hidden = isTurnHidden(node);
    // The line a folded turn reads as: what it used, in words.
    const toolNames = [...new Set(visibleChildren.filter((c) => c.kind === "tool").map((c) => toolWord(c.name)))];
    const usedText = toolNames.length > 3 ? `${toolNames.slice(0, 3).join(", ")} +${toolNames.length - 3}` : toolNames.join(", ");
    const summary =
        node.status === "running"
            ? toolCount > 0 ? `Working \u00b7 ${usedText}` : "Working\u2026"
            : childCount === 0
                ? "No reply"
                : toolCount > 0
                    ? usedText
                    : "Thinking";
    // "stop" (normal completion) and "toolUse" (turn ended to run
    // tools) are expected; anything else is worth surfacing — length
    // caps, errors, aborts.
    const notableStop =
        node.stopReason && node.stopReason !== "stop" && node.stopReason !== "toolUse"
            ? node.stopReason
            : null;
    const canFork = node.status === "done" && !!node.snapshot && !!onFork;
    const canRewind = node.status === "done" && !!node.snapshot && !!onRewind;
    const canReplay = node.status === "done" && !!onReplay;
    const canExport = node.status === "done" && childCount > 0;
    const turnRef = useRef(null);
    const [exporting, setExporting] = useState(false);
    const onExport = useCallback(async () => {
        if (!turnRef.current || exporting) return;
        setExporting(true);
        try {
            await triggerExportPng(turnRef.current, `turn-${node.id || "export"}.png`);
        } catch (err) {
            console.warn("[chat] turn export failed:", err);
        } finally {
            setExporting(false);
        }
    }, [exporting, node.id]);
    if (hidden) return null;
    const action = "shrink-0 cursor-pointer bg-transparent text-[11px] leading-[1.2] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]";
    // A turn's own actions show where the pointer is, like a message's.
    const actions = canExport || canReplay || canRewind || canFork ? (
        <>
            {canExport ? (
                <button
                    type="button"
                    onClick={onExport}
                    disabled={exporting}
                    title="Export this turn as PNG"
                    aria-label="Export this turn as PNG"
                    data-export-ignore="true"
                    className={`${action} disabled:opacity-50 disabled:cursor-wait`}
                >
                    {exporting ? "\u2026" : "Export"}
                </button>
            ) : null}
            {canReplay ? (
                <button
                    type="button"
                    onClick={() => onReplay(node.id)}
                    title="Run this prompt again and discard this turn and later messages"
                    aria-label="Replay this turn"
                    data-export-ignore="true"
                    className={action}
                >
                    Replay
                </button>
            ) : null}
            {canRewind ? (
                <button
                    type="button"
                    onClick={() => onRewind(node.id)}
                    title="Keep this turn, discard later messages, and continue from here"
                    aria-label="Rewind to this turn"
                    data-export-ignore="true"
                    className={action}
                >
                    Rewind
                </button>
            ) : null}
            {canFork ? (
                <button
                    type="button"
                    onClick={() => onFork(node.id)}
                    title="Fork a new chat from this turn"
                    aria-label="Fork from this turn"
                    data-export-ignore="true"
                    className={action}
                >
                    Fork
                </button>
            ) : null}
        </>
    ) : null;
    const children = visibleChildren.map((child, i) => (
        <ChildNode
            key={child.id || `c:${i}`}
            node={child}
            toggled={toggled}
            onToggle={onToggle}
            onOpenScope={onOpenScope}
            linkifyProse={linkifyProse}

        />
    ));
    if (bare) return visibleChildren.length ? <div ref={turnRef} className="flex flex-col gap-[8px]">{children}</div> : null;
    return (
        <div ref={turnRef} className="group/turn relative">
            {quiet ? (
                actions ? (
                    <div className="absolute right-0 -top-[17px] z-[2] flex items-center gap-[12px] opacity-0 transition-opacity group-hover/turn:opacity-100 focus-within:opacity-100">
                        {actions}
                    </div>
                ) : null
            ) : (
                /* Sticky header so the collapse toggle stays in reach while
                   the user scrolls through a long expanded turn. Negative
                   horizontal margins + matching padding make the background
                   full-width when pinned so it covers content beneath it. */
                <div className="sticky top-0 z-[2] -mx-[16px] flex items-center gap-[6px] bg-[color:var(--bg)] px-[16px] py-[3px]">
                    <button
                        type="button"
                        onClick={() => onToggle(node.id, kind)}
                        className="flex min-w-0 items-center gap-[6px] bg-transparent text-left text-[12px] leading-[1.3] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer"
                    >
                        <Chevron expanded={expanded} />
                        {node.status === "done" ? null : <StatusDot status={node.status} />}
                        <span className="truncate">
                            {summary}
                            {notableStop ? (
                                <span className="ml-[6px] text-[color:var(--red)]">stopped: {notableStop}</span>
                            ) : null}
                        </span>
                    </button>
                    {actions ? (
                        <div className="ml-auto flex shrink-0 items-center gap-[12px] opacity-0 transition-opacity group-hover/turn:opacity-100 focus-within:opacity-100">
                            {actions}
                        </div>
                    ) : null}
                </div>
            )}
            {visibleChildren.length > 0 && (quiet || expanded) ? (
                quiet ? <div className="flex flex-col gap-[10px]">{children}</div> : <Branch>{children}</Branch>
            ) : null}
        </div>
    );
}

function ChildNode({ node, toggled, onToggle, onOpenScope, linkifyProse }) {
    if (node.kind === "assistant") return <AssistantNode node={node} linkifyProse={linkifyProse} />;
    if (node.kind === "thinking") {
        return <ThinkingNode node={node} toggled={toggled} onToggle={onToggle} />;
    }
    if (node.kind === "tool") {
        return (
            <ToolNode
                node={node}
                toggled={toggled}
                onToggle={onToggle}
                onOpenScope={onOpenScope}
                linkifyProse={linkifyProse}

            />
        );
    }
    return null;
}

function AssistantNode({ node, linkifyProse }) {
    // Its claim refs tell it on the workspace in front, where that board can
    // show the claims they name.
    const shown = useBoardClaims();
    const linkify = useMemo(() => makeClaimRefLinkifier(node.text, shown, linkifyProse), [node.text, shown, linkifyProse]);
    // Many turns produce no visible prose — the model emits only a
    // tool call. Skip the empty AGENT block entirely; the turn header
    // already signals "agent activity here". When the assistant
    // *does* speak, render the markdown without a label since it's
    // unambiguous in context.
    if (!node.text) return null;
    // Reading spacing for the answer: paragraphs and lists apart, and a
    // command wraps in the column rather than running off its edge. It runs
    // as wide as the prompt above it, so the two line up.
    return (
        <div className="[&_p]:my-[7px] [&_ol]:my-[7px] [&_ul]:my-[7px] [&_li]:my-[3px] [&_pre]:whitespace-pre-wrap [&_pre]:break-words" data-chat-measure="">
            <Markdown text={node.text} linkifyText={linkify} />
        </div>
    );
}

function ThinkingNode({ node, toggled, onToggle }) {
    // Thinking blocks reuse the turn id space via index — there's at
    // most one active thinking node per turn but the id is unstable
    // (no toolCallId). Key on the first 32 chars so toggles survive
    // across re-renders within the same turn.
    const id = `thinking:${node.text.slice(0, 32)}`;
    const expanded = isNodeExpanded("thinking", id, toggled);
    // Folded, the line says what the model was thinking about: its first
    // line, without the markdown marks around it.
    const preview = (String(node.text || "").split("\n").map((l) => l.trim()).find(Boolean) || "")
        .replace(/^#+\s*/, "")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/\*(.+?)\*/g, "$1");
    return (
        <div>
            <button
                type="button"
                onClick={() => onToggle(id, "thinking")}
                className="flex w-full min-w-0 items-center gap-[6px] bg-transparent text-left text-[12px] leading-[1.3] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer"
            >
                <Chevron expanded={expanded} />
                <span className="shrink-0 whitespace-nowrap">Thinking</span>
                {!expanded && preview ? <span className="min-w-0 truncate">{`\u00b7 ${preview}`}</span> : null}
            </button>
            {expanded ? (
                <div className="mt-[6px] rounded-[2px] bg-[color:var(--control-bg)] px-[12px] py-[8px] text-[12px] leading-[1.55] text-[color:var(--muted)] [overflow-wrap:anywhere]">
                    <Markdown text={node.text} />
                </div>
            ) : null}
        </div>
    );
}

export function ToolNode({ node, toggled, onToggle, onOpenScope, linkifyProse }) {
    if (node.details?.kind === "kernelSuggestion" || node.name === "suggest_kernel") return null;
    const scopes = Array.isArray(node.details?.scopes) ? node.details.scopes : null;
    // A pivot is an analytics_query the harness wrote; its rows render the
    // same way.
    const isQueryResult =
        (node.name === "analytics_query" || node.name === "pivot") &&
        node.status === "done" &&
        Array.isArray(node.details?.columns) &&
        node.details.columns.length > 0 &&
        Array.isArray(node.details?.rows) &&
        node.details.rows.length > 0;
    const isSubAgent =
        node.details?.kind === "subagent" || node.name === "spawn_subagent";
    const isBatch =
        node.details?.kind === "batch" || node.name === "spawn_batch";
    const isKernelBlock =
        node.details?.kind === "kernelBlock" || node.name === "render_block";
    const isNav =
        node.details?.kind === "navSuggestion" || node.details?.kind === "navApplied";
    // A suggested standard, or a question put to the reader, is theirs to act on.
    const isStandardSuggestion = node.details?.kind === "standardSuggestion" || node.details?.kind === "standardRevision";
    const isOperatorQuestion = node.details?.kind === "operatorQuestion";
    // Notes it pinned on the board, each pointing at its pin there.
    const pins = chatPinsOf(node);
    const isPins = !!pins;
    // A job's card: a walkthrough's story, a trace's reading, the cases here.
    const jobCard = node.status === "done" && JOB_CARDS.has(node.details?.kind) ? node.details.kind : null;
    // A chart, a save card or a navigation is output for the reader, so it
    // reads open, as do a suggested standard, a question for the person, the
    // pins it put on the board and a call whose details say they are for the
    // reader (investigators at work); every other call is the agent's own and
    // folds to its line.
    const openKind = isKernelBlock || isNav || isStandardSuggestion || isOperatorQuestion || isPins || jobCard || node.details?.forReader === true ? "toolOpen" : "tool";
    const expanded = isNodeExpanded(openKind, node.id, toggled);
    const isExpand =
        node.details?.kind === "expand" || node.details?.kind === "expand_all" || node.name === "expand" || node.name === "expand_all";
    const isRecipe = node.details?.kind === "recipe" || node.name === "run_recipe";
    // The triage kit's results carry the listing the agent read; a verdict's
    // arguments are its case file.
    const isTriage = node.details?.kind === "triage";
    // Row-producing tools attach the investigation graph as it stood after
    // the call; when the call moved the graph a strip under the row shows
    // what changed, and the detail dump leaves the snapshot out.
    const graphSnapshot =
        node.details?.graph && typeof node.details.graph === "object" && node.details.graph.kind === "investigationGraph"
            ? node.details.graph
            : null;
    const detailsForView = graphSnapshot ? omitKey(node.details, "graph") : node.details;

    const hasDetail =
        scopes ||
        Boolean(graphSnapshot?.changed) ||
        isQueryResult ||
        isSubAgent ||
        isBatch ||
        isKernelBlock ||
        isNav ||
        isStandardSuggestion ||
        isOperatorQuestion ||
        isPins ||
        isExpand ||
        isRecipe ||
        node.argsStreaming ||
        (node.args != null && (typeof node.args === "string"
            ? node.args.length > 0
            : Object.keys(node.args || {}).length > 0)) ||
        (node.details != null && !isQueryResult && !isSubAgent && !isBatch && !isKernelBlock && !isNav && !isExpand && !isRecipe);

    // Sub-agent / batch tools provide their own progress summary.
    // Prefer it over the generic preparing/queued/running labels so
    // the collapsed row stays informative once fan-out starts.
    const subAgentLabel = isBatch
        ? batchProgressLabel(node.details)
        : isSubAgent
            ? subAgentProgressLabel(node.details)
            : null;
    const expandLabel = node.status !== "error"
        ? (isExpand ? expandProgressLabel(node.details) : isRecipe ? recipeProgressLabel(node.details) : null)
        : null;
    const statusLabel = subAgentLabel
        ? subAgentLabel
        : expandLabel
            ? expandLabel
        : node.status === "preparing"
            ? "preparing…"
            : node.status === "queued"
                ? "queued…"
                : node.status === "running"
                    ? (typeof node.details?.activity === "string" && node.details.activity) || "running…"
                    : node.status === "done" || node.status === "error"
                        ? (node.summary && !JSONISH.test(node.summary) ? node.summary : plainToolOutcome(node.details) || (node.status === "error" ? "failed" : "done"))
                        : node.summary || node.status;

    return (
        <div>
            <button
                type="button"
                onClick={() => onToggle(node.id, openKind)}
                disabled={!hasDetail}
                className={[
                    "flex w-full items-center gap-[6px] bg-transparent text-left text-[12px] leading-[1.3]",
                    hasDetail ? "cursor-pointer hover:text-[color:var(--ink)]" : "cursor-default",
                    "text-[color:var(--muted)]",
                ].join(" ")}
            >
                {hasDetail ? (
                    <Chevron expanded={expanded} />
                ) : (
                    <span className="inline-block h-[9px] w-[9px] shrink-0" aria-hidden="true" />
                )}
                {node.status === "done" ? null : <StatusDot status={node.status} />}
                <span className="shrink-0 whitespace-nowrap text-[color:var(--ink)]">{toolWord(node.name)}</span>
                <span className="min-w-0 flex-1 truncate text-[color:var(--muted-soft)]">{statusLabel}</span>
                <GraphDeltaMarker
                    snapshot={graphSnapshot}
                    onOpen={() => {
                        if (!expanded) onToggle(node.id, openKind);
                        if (!isNodeExpanded("graph", `${node.id}:graph`, toggled)) onToggle(`${node.id}:graph`, "graph");
                    }}
                />
            </button>
            {expanded && hasDetail ? (
                <div className="mt-[6px] flex flex-col gap-[8px] rounded-[2px] bg-[color:var(--control-bg)] px-[10px] py-[8px]" data-tool-detail="">
                    {/* The graph reads as the call's metadata, right under the
                        title line, even though it is the state after the call. */}
                    {graphSnapshot?.changed ? (
                        <GraphCollapsible ownerId={node.id} snapshot={graphSnapshot} toggled={toggled} onToggle={onToggle} />
                    ) : null}
                    {node.args != null && openKind !== "toolOpen" ? (
                        <ArgsCollapsible
                            ownerId={node.id}
                            args={node.args}
                            streaming={!!node.argsStreaming}
                            toggled={toggled}
                            onToggle={onToggle}
                        />
                    ) : null}
                    {Array.isArray(node.details?.resolvedInputs) && node.details.resolvedInputs.length ? (
                        <ResolvedInputsView entries={node.details.resolvedInputs} />
                    ) : null}
                    {scopes && scopes.length ? (
                        <ScopeLinks scopes={scopes} onOpenScope={onOpenScope} />
                    ) : null}
                    {isQueryResult ? (
                        <ExportableBlock filenamePrefix={`table-${node.id || "block"}`}>
                            <AnalyticsResultTable result={node.details} onOpenScope={onOpenScope} />
                        </ExportableBlock>
                    ) : null}
                    {isBatch ? (
                        <SubAgentBatch details={node.details} />
                    ) : null}
                    {isSubAgent && !isBatch ? (
                        <SubAgentSingle details={node.details} />
                    ) : null}
                    {isKernelBlock ? (
                        <ExportableBlock filenamePrefix={`block-${node.id || "kernel"}`}>
                            <KernelBlock
                                spec={node.details?.spec}
                                data={node.details?.data}
                                onOpenScope={openKernelLink}
                                linkifyText={linkifyProse}
                            />
                        </ExportableBlock>
                    ) : null}

                    {isNav ? <NavCard details={node.details} /> : null}
                    {isExpand && node.details ? <ExpandCard details={node.details} /> : null}
                    {isRecipe && node.details ? <RecipeCard details={node.details} /> : null}
                    {isTriage ? <TriageDetail node={node} /> : null}
                    {isStandardSuggestion && node.details?.kind === "standardSuggestion" ? <StandardSuggestionCard nodeId={node.id} details={node.details} /> : null}
                    {isStandardSuggestion && node.details?.kind === "standardRevision" ? <StandardRevisionCard nodeId={node.id} details={node.details} /> : null}
                    {isOperatorQuestion ? <ChatQuestionCard nodeId={node.id} details={node.details} /> : null}
                    {isPins ? <ChatPinsCard pins={pins} /> : null}
                    {jobCard === "walkthrough" ? <WalkthroughCard details={node.details} /> : null}
                    {jobCard === "sessionReading" ? <ReadingCard details={node.details} /> : null}
                    {jobCard === "boardCases" ? <BoardCasesCard details={node.details} nodeId={node.id} /> : null}
                    {node.details != null && !isQueryResult && !isSubAgent && !isBatch && !isKernelBlock && !isNav && !isExpand && !isRecipe && !isTriage && !isStandardSuggestion && !isOperatorQuestion && !isPins && !jobCard ? (
                        <ToolDetails details={detailsForView} />
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

// A triage kit result: what the agent read, in full, under its one-line
// summary; for a verdict, the case file the agent wrote (rung, why, check,
// numbered evidence, gated claims) read from the call's arguments.
export function parseTriageVerdict(args) {
    let a = args;
    if (typeof a === "string") { try { a = JSON.parse(a); } catch { return null; } }
    if (!a || typeof a !== "object") return null;
    const target = a.target && typeof a.target === "object" ? a.target : {};
    const ids = Array.isArray(target.ids) ? target.ids.map(String) : [];
    return {
        rung: typeof a.rung === "string" ? a.rung : null,
        kind: typeof target.kind === "string" ? target.kind : "finding",
        ids,
        name: typeof a.name === "string" ? a.name : "",
        why: typeof a.why === "string" ? a.why : "",
        check: typeof a.check === "string" ? a.check : "",
        evidence: Array.isArray(a.evidence) ? a.evidence.filter((e) => e && typeof e === "object") : [],
        claims: Array.isArray(a.claims) ? a.claims.filter((c) => c && typeof c === "object") : [],
    };
}

export function TriageDetail({ node }) {
    const details = node.details || {};
    const body = typeof details.text === "string" ? details.text : "";
    const verdict = node.name === "triage_verdict" ? parseTriageVerdict(node.args) : null;
    // A long listing starts folded to its header and, opened, flows with the
    // transcript rather than scrolling inside it.
    const isLong = body.length > COLLAPSE_LONG_CHARS;
    const [openBody, setOpenBody] = useState(!isLong);
    if (!body && !verdict) return null;
    return (
        <div className="flex flex-col gap-[6px]" data-triage-detail="">
            {verdict ? <TriageVerdictCard verdict={verdict} /> : null}
            {body ? (
                <div>
                    <button
                        type="button"
                        onClick={() => setOpenBody((v) => !v)}
                        aria-expanded={openBody}
                        data-triage-result-toggle=""
                        className="mb-[2px] flex w-full items-center gap-[6px] bg-transparent text-left text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] cursor-pointer hover:text-[color:var(--ink)]"
                    >
                        <Chevron expanded={openBody} />
                        <span className="shrink-0 whitespace-nowrap">Result</span>
                        <span className="min-w-0 truncate normal-case tracking-normal">
                            {details.summary ? `· ${String(details.summary)} ` : ""}· {body.length.toLocaleString()} chars
                        </span>
                    </button>
                    {openBody ? <CollapsibleBlock text={body} streaming={false} long={false} onCollapse={null} /> : null}
                </div>
            ) : null}
        </div>
    );
}

// The rungs the earlier in-browser triage pass placed findings on, as its
// chats' verdicts name them: a chat kept from then still reads its own.
const OLD_RUNG_LABEL = { look_now: "Look now", worth_a_look: "Worth a look", routine: "Routine", noise: "Noise" };
function TriageVerdictCard({ verdict }) {
    const where = verdict.kind === "batch"
        ? `batch of ${verdict.ids.length}`
        : `${verdict.kind}${verdict.ids.length ? ` ${verdict.ids[0].slice(0, 14)}${verdict.ids.length > 1 ? ` +${verdict.ids.length - 1}` : ""}` : ""}`;
    return (
        <div data-triage-verdict="" className="grid gap-[6px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[10px] text-[11px] leading-[1.45] text-[color:var(--ink)]">
            <div className="flex flex-wrap items-baseline gap-x-[8px] gap-y-[2px]">
                <span className="font-medium">{OLD_RUNG_LABEL[verdict.rung] || "Unplaced"}</span>
                <span className="font-mono text-[10px] text-[color:var(--muted)]">{where}</span>
                {verdict.name ? <span className="text-[color:var(--muted)]">{verdict.name}</span> : null}
                {verdict.check ? <span className="text-[color:var(--muted-soft)]">check: {verdict.check}</span> : null}
            </div>
            {verdict.why ? <p className="m-0 [overflow-wrap:anywhere]">{verdict.why}</p> : null}
            {verdict.evidence.length ? (
                <div className="grid gap-[3px]">
                    <span className="text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">Evidence</span>
                    {verdict.evidence.map((e, i) => (
                        <div key={i} className="flex gap-[8px]">
                            <span className="shrink-0 font-mono text-[10px] text-[color:var(--muted)]">E{i + 1}</span>
                            <span className="min-w-0 [overflow-wrap:anywhere]">
                                <span className="text-[color:var(--muted)]">
                                    {[e.kind, e.source, e.turn != null ? `T${e.turn}${e.role ? ` ${e.role}` : ""}` : null, e.session ? String(e.session).slice(0, 8) : null].filter(Boolean).join(" \u00b7 ")}
                                    {": "}
                                </span>
                                {String(e.text || "")}
                            </span>
                        </div>
                    ))}
                </div>
            ) : null}
            {verdict.claims.length ? (
                <div className="grid gap-[3px]">
                    <span className="text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">Claims</span>
                    {verdict.claims.map((c, i) => (
                        <div key={i} className="flex gap-[8px]">
                            <span className="shrink-0 font-mono text-[10px] text-[color:var(--muted)]">C{i + 1}</span>
                            <span className="min-w-0 [overflow-wrap:anywhere]">
                                <span className="text-[color:var(--muted)]">{[c.kind, c.status, Array.isArray(c.evidence) && c.evidence.length ? c.evidence.join(",") : null].filter(Boolean).join(" \u00b7 ")}{": "}</span>
                                {String(c.statement || "")}
                                {c.reason ? <span className="text-[color:var(--muted)]"> ({String(c.reason)})</span> : null}
                            </span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

// Threshold above which we cap the height of an expanded block to a
// scrollable viewport with a sticky collapse button. Big enough that
// most tool args / results don't trigger it; tight enough that
// 20+-line python code blocks and pandas dumps don't blow past the
// chat surface.
const COLLAPSE_LONG_CHARS = 600;

// The investigation graph as it stood after this call, behind the same
// collapsible as ARGS and RESULT and closed by default: the row's marker
// says what moved, opening this shows it.
function GraphCollapsible({ ownerId, snapshot, toggled, onToggle }) {
    const id = `${ownerId}:graph`;
    const expanded = isNodeExpanded("graph", id, toggled);
    return (
        <div>
            <button
                type="button"
                onClick={() => onToggle(id, "graph")}
                aria-expanded={expanded}
                className="flex w-full items-center gap-[6px] bg-transparent text-left text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] cursor-pointer hover:text-[color:var(--ink)]"
            >
                <Chevron expanded={expanded} />
                <span className="shrink-0 whitespace-nowrap">Graph</span>
                <OverflowTooltip
                    text={`\u00b7 ${addedText(snapshot)}, ${snapshot.nodes} anchor${snapshot.nodes === 1 ? "" : "s"} total`}
                    className="min-w-0 flex-1 normal-case tracking-normal text-[color:var(--muted-soft)]"
                >
                    · {addedText(snapshot)}, {snapshot.nodes} anchor{snapshot.nodes === 1 ? "" : "s"} total
                </OverflowTooltip>
            </button>
            {expanded ? (
                <div className="mt-[6px]">
                    <GraphBlock snapshot={snapshot} nodeId={ownerId} toolCallId={ownerId} />
                </div>
            ) : null}
        </div>
    );
}

function ArgsCollapsible({ ownerId, args, streaming, toggled, onToggle }) {
    const id = `${ownerId}:args`;
    // Force args open while the LLM is still streaming them in — the
    // live typing effect is the whole point. Once the call is sealed
    // (toolcall_end → status: queued), fall back to per-user toggle.
    const expanded = streaming || isNodeExpanded("args", id, toggled);
    const text = useMemo(() => {
        if (typeof args === "string") return args;
        try {
            return JSON.stringify(args, null, 2);
        } catch {
            return String(args);
        }
    }, [args]);
    if (!text) return null;
    const isLong = text.length > COLLAPSE_LONG_CHARS;
    const onCollapse = () => onToggle(id, "args");
    return (
        <div>
            <button
                type="button"
                onClick={onCollapse}
                disabled={streaming}
                className={[
                    "flex w-full items-center gap-[6px] bg-transparent text-left text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]",
                    streaming ? "cursor-default" : "cursor-pointer hover:text-[color:var(--ink)]",
                ].join(" ")}
            >
                <Chevron expanded={expanded} />
                <span className="shrink-0 whitespace-nowrap">Args</span>
                {streaming ? (
                    <span className="animate-pulse text-[color:var(--muted)] normal-case tracking-normal">
                        streaming…
                    </span>
                ) : null}
                <span className="min-w-0 truncate normal-case tracking-normal text-[color:var(--muted-soft)]">
                    · {text.length.toLocaleString()} chars
                </span>
            </button>
            {expanded ? (
                <CollapsibleBlock
                    text={text}
                    streaming={streaming}
                    long={isLong}
                    onCollapse={streaming ? null : onCollapse}
                />
            ) : null}
        </div>
    );
}

// Shared shell for "expanded code/text block, scrollable when long,
// with a sticky collapse handle at the bottom-right." Used by both
// ArgsCollapsible and ToolDetails so the python args (code) AND the
// python result (giant pandas dumps, tracebacks, stdout) get the
// same affordance. Short blocks render inline as before, no sticky
// chrome.
function CollapsibleBlock({ text, streaming, long, onCollapse }) {
    return (
        <div
            className={[
                "relative mt-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]",
                long ? "max-h-[60vh] overflow-y-auto" : "",
            ].join(" ")}
        >
            <pre className="overflow-x-auto whitespace-pre-wrap px-[8px] py-[6px] text-[10.5px] leading-[1.4] text-[color:var(--muted)] [overflow-wrap:anywhere]">
                {text}
                {streaming ? <span className="animate-pulse text-[color:var(--ink)]">▍</span> : null}
            </pre>
            {long && onCollapse ? (
                // Sticky to the SCROLL container's bottom — stays in
                // view as the user scrolls inside the block, so the
                // way out is always one click away regardless of how
                // far they've scrolled. pointer-events-none on the
                // gradient ensures only the button is clickable.
                <div className="sticky bottom-0 z-[1] flex justify-end pb-[4px] pr-[4px]">
                    <button
                        type="button"
                        onClick={onCollapse}
                        className="cursor-pointer rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[2px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)] hover:text-[color:var(--ink)] shadow-sm"
                    >
                        Collapse ▴
                    </button>
                </div>
            ) : null}
        </div>
    );
}

// ── sub-agent + batch progress renderers ─────────────────────────
//
// Both spawn_subagent and spawn_batch emit live `details` snapshots
// via the tool's onUpdate callback. The reducer at the top of the
// file merges those into the running tool node; the components below
// turn the snapshots into UI. Shapes:
//
//   subagent: { kind, taskSummary, status, activity?, tools?[],
//               preview?, result?, summary? }
//   batch   : { kind, taskSummary, total, done, errored,
//               items: [{ id, label, status, activity?, tools?[],
//                         preview?, result?, summary? }] }

function batchProgressLabel(details) {
    if (!details) return null;
    const total = details.total ?? details.items?.length ?? 0;
    if (!total) return "no items";
    const items = details.items || [];
    const done = items.filter((i) => i.status === "done").length;
    const errored = items.filter((i) => i.status === "error").length;
    const stopped = items.filter((i) => i.status === "stopped").length;
    const settled = done + errored + stopped;
    if (settled >= total) {
        return [
            `${done}/${total} ok`,
            errored ? `${errored} errored` : null,
            stopped ? `${stopped} stopped` : null,
        ].filter(Boolean).join(" · ");
    }
    const running = items.filter((i) => i.status === "running").length;
    return `${done}/${total} ok · ${running} running`;
}

function subAgentProgressLabel(details) {
    if (!details) return null;
    if (details.status === "done") return details.summary || "done";
    if (details.status === "error") return `error · ${details.summary || ""}`;
    return details.activity || "running…";
}

// An expand's own view: what the harness ran for the anchor, and the
// reader that turned the rows into claims, shown the way a spawned child
// is shown (model, turns, transcript). Live while it runs.
const EXPAND_STEP_COLUMNS = [
    { id: "to", label: "entity", weight: 2 },
    { id: "rows", label: "rows", align: "right" },
    { id: "toolCallId", label: "stored as", weight: 2 },
    { id: "error", label: "error", weight: 3, emptyText: "" },
];

// A tool row's detail is a tree. Caret rows (Graph, Args, and the expand
// card's sections) start at the edge and put their label 15px in (a 9px
// chevron and a 6px gap), which is also where the tool's name sits in the
// header. A section's text content sits on that column; a caret row nested
// in it starts there too, so each indent step is one level of nesting.
// Bordered blocks (tables, code) start at their section's edge.
const DETAIL_TEXT_COLUMN = "pl-[15px]";

// One collapsible section of the expand card: a caret row of the same shape
// as Args and Graph (chevron, small uppercase label, details in the muted
// body type, anything on the right) with its content beneath while open.
// A section's head stays one line however narrow the column: its label and
// meta give way with an ellipsis (the whole of it on hover), and so does what
// it says on the right, up to half the line. Wrapping instead, an id with no
// spaces fell into a column of single letters beside a long note (found in
// the side dock).
function ExpandSection({ label, meta, right = null, open, onToggle, children }) {
    return (
        <div>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggle();
                }}
                aria-expanded={open}
                className="flex w-full min-w-0 items-center gap-[6px] bg-transparent text-left text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] cursor-pointer hover:text-[color:var(--ink)]"
            >
                <Chevron expanded={open} />
                <OverflowTooltip
                    text={typeof meta === "string" ? `${label} \u00b7 ${meta}` : String(label)}
                    tooltipContent={meta && typeof meta !== "string" ? <span className="normal-case tracking-normal">{label}{" \u00b7 "}{meta}</span> : undefined}
                    className="min-w-0 flex-1"
                >
                    {label}
                    {meta ? <span className="normal-case tracking-normal text-[color:var(--muted)]">{" \u00b7 "}{meta}</span> : null}
                </OverflowTooltip>
                {right ? <span className="min-w-0 max-w-[50%] truncate" data-expand-section-right="">{right}</span> : null}
            </button>
            {open ? <div className="mt-[6px] space-y-[6px]">{children}</div> : null}
        </div>
    );
}

function ExpandReader({ reader }) {
    const [open, setOpen] = useState(true);
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    if (!reader) return null;
    const messages = Array.isArray(reader.messages) ? reader.messages : [];
    const claims = Array.isArray(reader.claims) ? reader.claims.filter((c) => c && c.statement) : [];
    const elapsed = Number.isFinite(reader.elapsedMs) ? `${(reader.elapsedMs / 1000).toFixed(1)}s` : null;
    const meta = [
        reader.model || "task model",
        reader.turns ? `${reader.turns} turn${reader.turns === 1 ? "" : "s"}` : null,
        elapsed,
        reader.status === "running" ? "running\u2026" : reader.status,
    ].filter(Boolean).join(" \u00b7 ");
    return (
        <ExpandSection label="Reader" meta={meta} open={open} onToggle={() => setOpen((v) => !v)}>
            {/* Claims, then the reader's notes, then its transcript behind a
                nested caret row, all one level in. */}
            <div className={`space-y-[6px] ${DETAIL_TEXT_COLUMN}`}>
                {reader.error ? <div className="text-[11px] text-[color:var(--red,#c0392b)]">{String(reader.error)}</div> : null}
                {claims.length ? (
                    <KernelBlock spec={{ kind: "rows", primaryField: "statement", secondaryField: "kind", rightField: "status", maxRows: 12, wrap: true }} data={claimDisplayRows(claims)} />
                ) : reader.status !== "running" ? (
                    <div className="text-[11px] text-[color:var(--muted-soft)]">no claims</div>
                ) : null}
                {reader.notes ? (
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">Reader notes</div>
                        <div className="mt-[2px] text-[11px] leading-[1.4] text-[color:var(--muted)]">{reader.notes}</div>
                    </div>
                ) : null}
                {messages.length ? (
                    <div data-reader-transcript>
                        <ExpandSection
                            label="Transcript"
                            meta={`${messages.length} message${messages.length === 1 ? "" : "s"}`}
                            open={transcriptOpen}
                            onToggle={() => setTranscriptOpen((v) => !v)}
                        >
                            <div className={DETAIL_TEXT_COLUMN}>
                                <SubAgentTranscript messages={messages} systemPrompt={reader.systemPrompt} task={reader.task} flush />
                            </div>
                        </ExpandSection>
                    </div>
                ) : null}
            </div>
        </ExpandSection>
    );
}

export function ExpandResult({ details, compact = false }) {
    const [open, setOpen] = useState(true);
    if (!details) return null;
    const steps = Array.isArray(details.steps) ? details.steps : [];
    const skipped = Array.isArray(details.skipped) ? details.skipped : [];
    // An error is the section's first line; the head does not say it again.
    const label = details.error ? null : expandProgressLabel({ ...details, kind: "expand" });
    return (
        <div className="space-y-[6px]" data-expand-result>
            <ExpandSection
                label={compact ? "Anchor" : "Expand"}
                meta={<span className="font-mono">{details.anchor}</span>}
                right={label ? <span className="text-[10px] normal-case tracking-normal text-[color:var(--muted-soft)]">{label}</span> : null}
                open={open}
                onToggle={() => setOpen((v) => !v)}
            >
                {details.error ? <div className={`${DETAIL_TEXT_COLUMN} text-[11px] text-[color:var(--red,#c0392b)]`}>{String(details.error)}</div> : null}
                {steps.length ? (
                    <KernelBlock spec={{ kind: "table", columns: EXPAND_STEP_COLUMNS, maxRows: 12 }} data={steps.map((s) => ({ to: s.to, rows: s.rows ?? 0, toolCallId: s.toolCallId || "", error: s.error ? String(s.error).split("\n")[0].slice(0, 200) : "" }))} />
                ) : details.phase === "pivots" ? (
                    <div className={`${DETAIL_TEXT_COLUMN} text-[11px] text-[color:var(--muted-soft)]`}>running pivots{"\u2026"}</div>
                ) : null}
                {skipped.length ? (
                    <div className={`${DETAIL_TEXT_COLUMN} text-[11px] text-[color:var(--muted-soft)]`}>
                        skipped: {skipped.map((s) => `${s.to || "plan"} (${s.reason})`).join("; ")}
                    </div>
                ) : null}
            </ExpandSection>
            <ExpandReader reader={details.reader} />
        </div>
    );
}

// A recipe run: the parameters it took, the search with the anchors it
// produced, then one expand section per anchor, then every claim gathered.
const RECIPE_SEARCH_COLUMNS = [
    { id: "entity", label: "entity", weight: 2 },
    { id: "term", label: "term", weight: 2 },
    { id: "rows", label: "rows", align: "right" },
    { id: "anchors", label: "anchors found", align: "right" },
    { id: "toolCallId", label: "stored as", weight: 2 },
    { id: "error", label: "error", weight: 3, emptyText: "" },
];

function RecipeCard({ details }) {
    const [open, setOpen] = useState(true);
    const [claimsOpen, setClaimsOpen] = useState(true);
    if (!details) return null;
    const steps = Array.isArray(details.steps) ? details.steps : [];
    const searches = steps.filter((s) => s.kind === "search" || s.kind === "query");
    const expands = steps.filter((s) => s.kind === "expand");
    const claims = (Array.isArray(details.claims) ? details.claims : []).filter((c) => c && c.statement).map((c) => ({ statement: c.statement, kind: c.kind, status: c.status }));
    const params = details.params && typeof details.params === "object"
        ? Object.entries(details.params).filter(([, v]) => v != null && v !== "").map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`).join("; ")
        : "";
    const label = recipeProgressLabel(details);
    const supported = claims.filter((c) => c.status === "supported").length;
    // Sibling caret rows on the tool row's grid: the recipe (its search and
    // the anchors it ranked), one Anchor section per expansion with its
    // reader, then the claims they produced.
    return (
        <div className="space-y-[6px]" data-recipe-card>
            <ExpandSection
                label="Recipe"
                meta={<span>{details.name}{details.source ? ` (${details.source})` : ""}{params ? <span className="ml-[6px] font-mono">{params}</span> : null}</span>}
                right={label ? <span className="text-[10px] normal-case tracking-normal text-[color:var(--muted-soft)]">{label}</span> : null}
                open={open}
                onToggle={() => setOpen((v) => !v)}
            >
                {details.error && details.phase === "error" ? <div className={`${DETAIL_TEXT_COLUMN} text-[11px] text-[color:var(--red,#c0392b)]`}>{String(details.error)}</div> : null}
                {searches.length ? (
                    <KernelBlock spec={{ kind: "table", columns: RECIPE_SEARCH_COLUMNS, maxRows: 6 }} data={searches.map((s) => ({ entity: s.entity, term: s.term || "", rows: s.rows ?? 0, anchors: (s.anchors || []).length, toolCallId: s.toolCallId || "", error: s.error ? String(s.error).split("\n")[0].slice(0, 200) : "" }))} />
                ) : null}
                {searches.some((s) => s.anchors?.length) ? (
                    <div className={DETAIL_TEXT_COLUMN}>
                        <KernelBlock spec={{ kind: "rows", primaryField: "key", rightField: "rows", rightFormat: "number", maxRows: 8 }} data={searches.flatMap((s) => (s.anchors || []).map((a) => ({ key: a.key, rows: a.rows })))} />
                    </div>
                ) : null}
                {expands.filter((e) => e.error).map((e, i) => (
                    <div key={i} className={`${DETAIL_TEXT_COLUMN} text-[11px] text-[color:var(--muted-soft)]`}>expand: {e.error}</div>
                ))}
            </ExpandSection>
            {expands.flatMap((e, i) => (e.results || []).map((r, j) => <ExpandResult key={r?.anchor || `${i}:${j}`} details={r} compact />))}
            {claims.length ? (
                <ExpandSection label="Claims" meta={`${supported} of ${claims.length} supported`} open={claimsOpen} onToggle={() => setClaimsOpen((v) => !v)}>
                    <div className={DETAIL_TEXT_COLUMN}>
                        <KernelBlock spec={{ kind: "rows", primaryField: "statement", secondaryField: "kind", rightField: "status", maxRows: 12, wrap: true }} data={claims} />
                    </div>
                </ExpandSection>
            ) : null}
        </div>
    );
}

function ExpandCard({ details }) {
    if (!details) return null;
    if (details.kind === "expand_all") {
        const results = Array.isArray(details.results) ? details.results : [];
        return (
            <div className="space-y-[10px]" data-expand-card>
                {results.map((r, i) => <ExpandResult key={r?.anchor || i} details={r} compact />)}
            </div>
        );
    }
    return <div data-expand-card><ExpandResult details={details} /></div>;
}

function SubAgentSingle({ details }) {
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    if (!details) return null;
    const canStop = details.status === "running" && typeof details.abort === "function";
    const messages = Array.isArray(details.messages) ? details.messages : [];
    const hasTranscript = messages.length > 0;
    return (
        <div className="space-y-[6px]">
            <div className="flex items-baseline gap-[8px]">
                {details.taskSummary ? (
                    <div className="min-w-0 flex-1 text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        Task
                        <span className="ml-[6px] normal-case tracking-normal text-[color:var(--muted)]">
                            {details.taskSummary}
                        </span>
                    </div>
                ) : <div className="flex-1" />}
                {hasTranscript ? (
                    <SubAgentTranscriptToggle
                        open={transcriptOpen}
                        onToggle={() => setTranscriptOpen((v) => !v)}
                        count={messages.length}
                    />
                ) : null}
                {canStop ? <SubAgentStopButton onClick={details.abort} /> : null}
            </div>
            <SubAgentSlot slot={details} />
            {transcriptOpen && hasTranscript ? (
                <SubAgentTranscript
                    messages={messages}
                    systemPrompt={details.systemPrompt}
                    task={details.task}
                />
            ) : null}
            {details.status === "done" && details.result != null ? (
                <SubAgentResultBlock result={details.result} />
            ) : null}
        </div>
    );
}

function SubAgentBatch({ details }) {
    if (!details) return null;
    const items = details.items || [];
    const successes = items.filter((i) => i.status === "done");
    return (
        <div className="space-y-[10px]">
            {details.taskSummary ? (
                <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                    Task
                    <span className="ml-[6px] normal-case tracking-normal text-[color:var(--muted)]">
                        {details.taskSummary}
                    </span>
                </div>
            ) : null}
            <div className="space-y-[3px]">
                {items.length === 0 ? (
                    <div className="text-[11px] text-[color:var(--muted)]">
                        no items
                    </div>
                ) : (
                    items.map((item) => <SubAgentRow key={item.id} item={item} />)
                )}
            </div>
            {successes.length ? (
                <div>
                    <div className="mb-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        Results
                    </div>
                    <div className="space-y-[4px]">
                        {successes.map((item) => (
                            <SubAgentResultCard key={`${item.id}:result`} item={item} />
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

// Single row in a batch — collapsed status, expandable live detail.
// A sub-agent working in a session (an investigator) draws that session's
// workspace card under its row: open while it works, folded to its line once
// it is done.
const SubAgentSessionCard = lazy(() => import("./SubAgentSessionCard.jsx"));

function SubAgentRow({ item }) {
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    const [cardToggled, setCardToggled] = useState(null);
    const focus = item.focus?.session ? item.focus : null;
    const cardOpen = Boolean(focus) && (cardToggled ?? item.status === "running");
    const dot =
        item.status === "done"
            ? "var(--green, #27ae60)"
            : item.status === "error"
                ? "var(--red, #c0392b)"
                : item.status === "stopped"
                    ? "var(--muted-soft)"
                    : item.status === "running"
                        ? "var(--ink)"
                        : "var(--line)";
    const headLabel =
        item.status === "running"
            ? item.activity || "running…"
            : item.summary || item.status;
    const showDetail = item.status === "running" && ((item.tools?.length || 0) > 0 || item.preview);
    const canStop = item.status === "running" && typeof item.abort === "function";
    const messages = Array.isArray(item.messages) ? item.messages : [];
    const hasTranscript = messages.length > 0;
    return (
        <div className="space-y-[2px]">
            <div className="flex items-baseline gap-[8px] text-[11px] leading-[1.3]">
                <span
                    className={`relative top-[1px] inline-block h-[7px] w-[7px] shrink-0 rounded-full ${item.status === "running" ? "animate-pulse" : ""}`}
                    style={{ background: dot }}
                    aria-hidden="true"
                />
                {/* The label gives way before the buttons do: a narrow column
                    cuts it, never them. With its card open, the card's caption
                    says what it is doing. */}
                <span
                    className="min-w-0 truncate font-mono text-[color:var(--ink)]"
                    title={item.id}
                >
                    {item.label || item.id}
                </span>
                <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]" data-sub-agent-activity="">
                    {cardOpen ? null : headLabel}
                </span>
                {focus ? (
                    <button
                        type="button"
                        data-sub-agent-card-toggle=""
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCardToggled(!cardOpen); }}
                        className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[6px] py-[1px] text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
                    >
                        {cardOpen ? "hide card" : "card"}
                    </button>
                ) : null}
                {hasTranscript ? (
                    <SubAgentTranscriptToggle
                        open={transcriptOpen}
                        onToggle={() => setTranscriptOpen((v) => !v)}
                        count={messages.length}
                    />
                ) : null}
                {canStop ? <SubAgentStopButton onClick={item.abort} /> : null}
            </div>
            {cardOpen ? (
                <Suspense fallback={null}>
                    <SubAgentSessionCard focus={focus} caption={headLabel} />
                </Suspense>
            ) : null}
            {showDetail && !cardOpen ? <SubAgentLiveDetail slot={item} /> : null}
            {transcriptOpen && hasTranscript ? (
                <SubAgentTranscript
                    messages={messages}
                    systemPrompt={item.systemPrompt}
                    task={item.task}
                />
            ) : null}
        </div>
    );
}

function SubAgentTranscriptToggle({ open, onToggle, count }) {
    return (
        <button
            type="button"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggle();
            }}
            title={`View the sub-agent's full transcript (${count} message${count === 1 ? "" : "s"})`}
            className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[6px] py-[1px] text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
        >
            {open ? "hide" : `transcript · ${count}`}
        </button>
    );
}

function SubAgentStopButton({ onClick }) {
    return (
        <button
            type="button"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try { onClick?.(); } catch { /* noop */ }
            }}
            title="Stop this sub-agent"
            className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[6px] py-[1px] text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:border-[color:var(--red)] hover:text-[color:var(--red)]"
        >
            stop
        </button>
    );
}

// The streaming view of a running sub-agent (single or batch row):
// tool pills + rolling preview of streamed text.
function SubAgentSlot({ slot }) {
    if (slot.status === "done") {
        return (
            <div className="flex items-baseline gap-[8px] text-[11px] leading-[1.3]">
                <span
                    className="relative top-[1px] inline-block h-[7px] w-[7px] shrink-0 rounded-full"
                    style={{ background: "var(--green, #27ae60)" }}
                    aria-hidden="true"
                />
                <span className="text-[color:var(--ink)]">done</span>
                {slot.summary ? (
                    <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]">
                        {slot.summary}
                    </span>
                ) : null}
            </div>
        );
    }
    if (slot.status === "error") {
        return (
            <div className="flex items-baseline gap-[8px] text-[11px] leading-[1.3]">
                <span
                    className="relative top-[1px] inline-block h-[7px] w-[7px] shrink-0 rounded-full"
                    style={{ background: "var(--red, #c0392b)" }}
                    aria-hidden="true"
                />
                <span className="text-[color:var(--ink)]">error</span>
                <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]">
                    {slot.summary || ""}
                </span>
            </div>
        );
    }
    return <SubAgentLiveDetail slot={slot} />;
}

function SubAgentLiveDetail({ slot }) {
    const tools = slot.tools || [];
    return (
        <div className="ml-[15px] space-y-[1px] border-l border-[color:var(--line)] pl-[8px] text-[10.5px] leading-[1.4]">
            {tools.map((t, i) => (
                <SubAgentToolPill key={`${t.name}:${i}`} tool={t} />
            ))}
            {slot.preview ? (
                <div className="text-[color:var(--muted-soft)] [overflow-wrap:anywhere]">
                    {slot.preview}
                </div>
            ) : null}
            {!tools.length && !slot.preview && slot.activity ? (
                <div className="text-[color:var(--muted-soft)]">{slot.activity}</div>
            ) : null}
        </div>
    );
}

function SubAgentToolPill({ tool }) {
    const isRunning = tool.status === "running";
    const isError = tool.status === "error";
    const colorClass = isError
        ? "text-[color:var(--muted-soft)]"
        : isRunning
            ? "text-[color:var(--ink)]"
            : "text-[color:var(--muted)]";
    const dotColor = isError
        ? "var(--muted-soft)"
        : isRunning
            ? "var(--ink)"
            : "var(--green, #27ae60)";
    return (
        <div className={`flex items-baseline gap-[6px] ${colorClass}`}>
            <span
                className={`relative top-[1px] inline-block h-[4px] w-[4px] shrink-0 rounded-full ${isRunning ? "animate-pulse" : ""}`}
                style={{ background: dotColor }}
                aria-hidden="true"
            />
            <span className="shrink-0 whitespace-nowrap font-mono">{tool.name}</span>
            <span className="min-w-0 flex-1 truncate text-[color:var(--muted-soft)]">
                {isRunning ? "running…" : tool.summary || tool.status}
            </span>
        </div>
    );
}

// Per-result card in a batch. Shows the label + structured result
// (pretty-printed JSON, capped) or assistant text.
function SubAgentResultCard({ item }) {
    return (
        <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[10px] py-[8px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
            <div className="mb-[4px] flex items-baseline gap-[8px]">
                <span
                    className="relative top-[1px] inline-block h-[7px] w-[7px] shrink-0 rounded-full"
                    style={{ background: "var(--green, #27ae60)" }}
                    aria-hidden="true"
                />
                <span
                    className="min-w-0 truncate font-mono text-[color:var(--ink)]"
                    title={item.id}
                >
                    {item.label || item.id}
                </span>
                {item.summary ? (
                    <span className="min-w-0 flex-1 truncate text-[color:var(--ink)]">
                        {item.summary}
                    </span>
                ) : null}
            </div>
            <SubAgentResultBlock result={item.result} />
        </div>
    );
}

// Sub-agent's final result — single agent's return value or per-item
// result in a batch. Uses the same CollapsibleBlock affordance as
// ToolDetails / ArgsCollapsible so megabyte JSON returns from a
// child agent don't blow past the chat surface. Collapsed by default
// when long; sticky bottom collapse button while scrolling through.
function SubAgentResultBlock({ result }) {
    const text = useMemo(() => {
        if (result == null) return "";
        if (typeof result === "string") return result;
        try { return JSON.stringify(result, null, 2); }
        catch { return String(result); }
    }, [result]);
    const [expanded, setExpanded] = useState(false);
    if (!text) return null;
    const isLong = text.length > COLLAPSE_LONG_CHARS;
    const show = !isLong || expanded;
    return (
        <div>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                disabled={!isLong}
                className={[
                    "mb-[2px] flex w-full items-center gap-[6px] bg-transparent text-left text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]",
                    isLong ? "cursor-pointer hover:text-[color:var(--ink)]" : "cursor-default",
                ].join(" ")}
            >
                {isLong ? <Chevron expanded={expanded} /> : null}
                <span className="shrink-0 whitespace-nowrap">Result</span>
                <span className="min-w-0 truncate normal-case tracking-normal text-[color:var(--muted-soft)]">
                    · {text.length.toLocaleString()} chars
                </span>
            </button>
            {show ? (
                <CollapsibleBlock
                    text={text}
                    long={isLong}
                    onCollapse={isLong ? () => setExpanded(false) : null}
                />
            ) : null}
        </div>
    );
}

// Full inline transcript of a sub-agent's run. Renders the live
// pi-agent messages array — committed user / assistant / toolResult
// turns. The streaming portion of an in-flight assistant message
// lives in `state.streamingMessage` which we don't surface here; the
// existing rolling preview covers that case. The system prompt is
// behind a sub-toggle because it's typically 5KB+ of analytics
// schema examples and crowds out what the model actually did.
function SubAgentTranscript({ messages, systemPrompt, task, flush = false }) {
    const [showSystem, setShowSystem] = useState(false);
    return (
        <div className={`${flush ? "" : "ml-[15px] border-l border-[color:var(--line)] pl-[8px] "}space-y-[6px] text-[10.5px] leading-[1.45] text-[color:var(--muted)]`}>
            {systemPrompt ? (
                <div>
                    <button
                        type="button"
                        onClick={() => setShowSystem((v) => !v)}
                        className="flex items-center gap-[4px] bg-transparent text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer"
                    >
                        <Chevron expanded={showSystem} />
                        <span className="min-w-0 truncate whitespace-nowrap">system prompt · {systemPrompt.length} chars</span>
                    </button>
                    {showSystem ? (
                        <pre className="mt-[4px] max-h-[200px] overflow-y-auto whitespace-pre-wrap rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[6px] py-[4px] [overflow-wrap:anywhere]">
                            {systemPrompt}
                        </pre>
                    ) : null}
                </div>
            ) : null}
            {messages.map((m, i) => (
                <SubAgentTranscriptTurn key={i} message={m} />
            ))}
        </div>
    );
}

function SubAgentTranscriptTurn({ message }) {
    if (!message || !message.role) return null;
    const role = message.role;
    if (role === "user") {
        return (
            <TranscriptBlock label="user" tone="user">
                <TranscriptText text={extractTurnText(message.content)} />
            </TranscriptBlock>
        );
    }
    if (role === "assistant") {
        const parts = Array.isArray(message.content) ? message.content : [];
        return (
            <TranscriptBlock label="assistant" tone="assistant">
                {parts.map((part, i) => {
                    if (!part) return null;
                    if (part.type === "thinking" && part.text) {
                        return (
                            <div key={i} className="text-[color:var(--muted-soft)] [overflow-wrap:anywhere]">
                                <span className="text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">thinking</span>
                                <div className="mt-[2px] whitespace-pre-wrap">{truncate(part.text)}</div>
                            </div>
                        );
                    }
                    if (part.type === "text" && part.text) {
                        return (
                            <div key={i} className="whitespace-pre-wrap text-[color:var(--ink)] [overflow-wrap:anywhere]">
                                {truncate(part.text)}
                            </div>
                        );
                    }
                    if (part.type === "toolCall") {
                        return <TranscriptToolCall key={i} part={part} />;
                    }
                    return null;
                })}
            </TranscriptBlock>
        );
    }
    if (role === "toolResult") {
        const text = extractTurnText(message.content);
        return (
            <TranscriptBlock label={`tool · ${message.toolName || "result"}`} tone="tool">
                <TranscriptText text={text} mono />
            </TranscriptBlock>
        );
    }
    if (role === "notification" && message.text) {
        return (
            <TranscriptBlock label="context" tone="context">
                <TranscriptText text={message.text} />
            </TranscriptBlock>
        );
    }
    return null;
}

function TranscriptBlock({ label, tone, children }) {
    const toneClass =
        tone === "user"
            ? "border-[color:var(--line)]"
            : tone === "assistant"
                ? "border-[color:var(--line)]"
                : tone === "tool"
                    ? "border-[color:var(--line)] bg-[color:var(--plot-bg)]/30"
                    : "border-[color:var(--line)]";
    return (
        <div className={`rounded-[2px] border ${toneClass} px-[6px] py-[4px]`}>
            <div className="mb-[2px] text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                {label}
            </div>
            <div className="space-y-[3px]">{children}</div>
        </div>
    );
}

function TranscriptText({ text, mono = false }) {
    if (!text) return null;
    const shown = truncate(text);
    return (
        <div
            className={[
                "whitespace-pre-wrap [overflow-wrap:anywhere]",
                mono ? "font-mono" : "",
                "text-[color:var(--muted)]",
            ].filter(Boolean).join(" ")}
        >
            {shown}
        </div>
    );
}

function TranscriptToolCall({ part }) {
    const [open, setOpen] = useState(false);
    // Sub-agent transcripts iterate the child's state.messages, where
    // pi-ai stores tool-call args under `arguments`; staged/SDK shapes
    // use `input`/`args`. Miss `arguments` and every child tool call
    // renders as a bare name with no args preview / no expand.
    const args = part.arguments ?? part.input ?? part.args ?? null;
    const argsText = useMemo(() => {
        if (args == null) return "";
        if (typeof args === "string") return args;
        try { return JSON.stringify(args, null, 2); } catch { return String(args); }
    }, [args]);
    const hasArgs = !!argsText;
    return (
        <div>
            <button
                type="button"
                onClick={() => hasArgs && setOpen((v) => !v)}
                disabled={!hasArgs}
                className="flex w-full items-center gap-[4px] bg-transparent text-left font-mono text-[color:var(--ink)] disabled:cursor-default hover:text-[color:var(--ink)] cursor-pointer"
            >
                {hasArgs ? <Chevron expanded={open} /> : null}
                <span className="shrink-0 whitespace-nowrap">{part.name || "tool"}</span>
                {!open && hasArgs ? (
                    <span className="min-w-0 truncate text-[color:var(--muted-soft)]">
                        {argsText.slice(0, 80)}{argsText.length > 80 ? "…" : ""}
                    </span>
                ) : null}
            </button>
            {open && hasArgs ? (
                <pre className="mt-[2px] max-h-[160px] overflow-y-auto whitespace-pre-wrap rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[6px] py-[3px] text-[10px] font-mono [overflow-wrap:anywhere]">
                    {truncate(argsText, 3000)}
                </pre>
            ) : null}
        </div>
    );
}

// Pull a printable string out of a content field that may be a
// string, an array of content parts, or some mix the SDK produced.
// Drops images and other non-textual parts; concatenates text bits.
function extractTurnText(content) {
    if (typeof content === "string") return content;
    if (!Array.isArray(content)) return "";
    return content
        .map((p) => {
            if (!p) return "";
            if (typeof p === "string") return p;
            if (p.type === "text" && typeof p.text === "string") return p.text;
            if (p.type === "thinking" && typeof p.text === "string") return p.text;
            return "";
        })
        .filter(Boolean)
        .join("\n");
}

const TRANSCRIPT_TURN_MAX = 4000;
function truncate(text, max = TRANSCRIPT_TURN_MAX) {
    if (!text) return "";
    if (text.length <= max) return text;
    return `${text.slice(0, max)}\n\n…[truncated ${text.length - max} chars]`;
}

// Renders `details.resolvedInputs` — per-entry resolution metadata
// stamped by execute_python when it looks up `from_tool_calls`
// refs. Sits between ARGS and RESULT in the tool node so the
// debugging rhythm is "what the LLM emitted → what we actually
// injected → what came back." Successful entries read as
// `name ← refId · N rows from source`; failed lookups surface
// inline with a ❌ marker so a typo'd id is immediately visible.
function ResolvedInputsView({ entries }) {
    return (
        <div>
            <div className="mb-[2px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                Resolved inputs
            </div>
            <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[8px] py-[6px] text-[10.5px] leading-[1.45] text-[color:var(--muted)]">
                {entries.map((entry, idx) => (
                    <ResolvedInputRow key={`${entry.name}:${entry.refId}:${idx}`} entry={entry} />
                ))}
            </div>
        </div>
    );
}

function ResolvedInputRow({ entry }) {
    const refShort = truncateRefId(entry.refId);
    if (entry.error) {
        return (
            <div className="flex items-baseline gap-[6px] [overflow-wrap:anywhere]">
                <span className="font-mono text-[color:var(--ink)]">{entry.name}</span>
                <span className="text-[color:var(--muted-soft)]">←</span>
                <span
                    className="font-mono text-[color:var(--muted)]"
                    title={entry.refId}
                >
                    {refShort}
                </span>
                <span className="text-[color:var(--muted-soft)]">·</span>
                <span className="text-[color:var(--red,#c0392b)]">❌ {entry.error}</span>
            </div>
        );
    }
    const rowLabel = `${entry.rowCount?.toLocaleString?.() ?? entry.rowCount} row${entry.rowCount === 1 ? "" : "s"}`;
    return (
        <div className="flex items-baseline gap-[6px] [overflow-wrap:anywhere]">
            <span className="font-mono text-[color:var(--ink)]">{entry.name}</span>
            <span className="text-[color:var(--muted-soft)]">←</span>
            <span
                className="font-mono text-[color:var(--muted)]"
                title={entry.refId}
            >
                {refShort}
            </span>
            <span className="text-[color:var(--muted-soft)]">·</span>
            <span className="text-[color:var(--muted)]">{rowLabel}</span>
            {entry.source ? (
                <>
                    <span className="text-[color:var(--muted-soft)]">from</span>
                    <span className="font-mono text-[color:var(--muted)]">{entry.source}</span>
                </>
            ) : null}
        </div>
    );
}

// Tool call ids are ~25-30 chars and not meaningful at a glance.
// Show head + tail with an ellipsis; full id is on the row's
// title attribute for copy-on-hover.
function truncateRefId(id) {
    if (typeof id !== "string") return "";
    if (id.length <= 14) return id;
    return `${id.slice(0, 9)}…${id.slice(-4)}`;
}

// Generic tool result renderer (execute_python's giant pandas dumps,
// MCP tool outputs, anything without a custom view). Long results
// collapse behind a chevron toggle by default; expanded view shares
// CollapsibleBlock's scroll-capped shell + sticky bottom collapse
// button so a megabyte traceback can't dominate the transcript.
function omitKey(obj, key) {
    if (!obj || typeof obj !== "object") return obj;
    const { [key]: _dropped, ...rest } = obj;
    return rest;
}

function ToolDetails({ details }) {
    const text = useMemo(() => {
        try { return JSON.stringify(details, null, 2); }
        catch { return String(details); }
    }, [details]);
    const [expanded, setExpanded] = useState(false);
    if (!text) return null;
    const isLong = text.length > COLLAPSE_LONG_CHARS;
    const show = !isLong || expanded;
    return (
        <div>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                disabled={!isLong}
                className={[
                    "mb-[2px] flex w-full items-center gap-[6px] bg-transparent text-left text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]",
                    isLong ? "cursor-pointer hover:text-[color:var(--ink)]" : "cursor-default",
                ].join(" ")}
            >
                {isLong ? <Chevron expanded={expanded} /> : null}
                <span className="shrink-0 whitespace-nowrap">Result</span>
                <span className="min-w-0 truncate normal-case tracking-normal text-[color:var(--muted-soft)]">
                    · {text.length.toLocaleString()} chars
                </span>
            </button>
            {show ? (
                <CollapsibleBlock
                    text={text}
                    long={isLong}
                    onCollapse={isLong ? () => setExpanded(false) : null}
                />
            ) : null}
        </div>
    );
}

// Render an analytics_query result as a DataTable inline in the chat
// transcript. Truncated to the first VISIBLE_LIMIT rows by default so
// a single tool call doesn't flood the chat — an inline footer lets
// the user expand to see everything, with a sticky-bottom "Collapse"
// affordance to put it back. No internal Y scroll: the table flows in
// the chat's single scroll surface, which avoids the nested-scroll
// jankiness of trying to scroll a small viewport inside a larger one.
const ANALYTICS_VISIBLE_LIMIT = 8;

function AnalyticsResultTable({ result, onOpenScope }) {
    const [expanded, setExpanded] = useState(false);

    const columns = useMemo(
        () => (result.columns || []).map((id) => ({ id, label: id, width: 140 })),
        [result.columns],
    );
    const allRows = useMemo(() => rowsToObjects(result), [result]);
    const visibleRows = expanded ? allRows : allRows.slice(0, ANALYTICS_VISIBLE_LIMIT);
    const hidden = allRows.length - visibleRows.length;
    const truncated = allRows.length > ANALYTICS_VISIBLE_LIMIT;

    const cols = useDataTableColumns({
        columns,
        defaultVisible: columns.map((c) => c.id),
    });

    // Precompute per-column linking info so renderCell stays cheap.
    //   columnDim[colId]   → dim string when the column is a valid
    //                        scope-id source; null otherwise
    //   columnLabelCol[colId] → name of the sibling label column to
    //                        prefer for that dim, if present in this
    //                        result, so the link's hover title can
    //                        carry a human-readable label
    const { columnDim, columnLabelCol } = useMemo(() => {
        const dim = {};
        const labelCol = {};
        const colList = result.columns || [];
        for (const cid of colList) {
            const d = getScopeDimForColumn(cid);
            dim[cid] = d;
            if (d) labelCol[cid] = pickLabelColumnForDim(d, colList);
        }
        return { columnDim: dim, columnLabelCol: labelCol };
    }, [result.columns]);

    const renderCell = useCallback(
        (row, col) => {
            const v = row[col.id];
            if (v == null || v === "") {
                return <span className="text-[color:var(--muted-soft)]">—</span>;
            }
            const text = typeof v === "object" ? JSON.stringify(v) : String(v);
            const numeric = typeof v === "number" || /^\s*-?\d[\d,]*(\.\d+)?\s*%?\s*$/.test(text);

            const dim = columnDim[col.id];
            if (dim && onOpenScope) {
                const labelKey = columnLabelCol[col.id];
                const label = labelKey && row[labelKey] != null ? String(row[labelKey]) : null;
                return (
                    <button
                        type="button"
                        onClick={() => onOpenScope(dim, [text])}
                        title={label ? `${label} · ${text}` : text}
                        className={[
                            "block w-full cursor-pointer truncate border-0 bg-transparent p-0 text-left text-[11px] leading-[1.2] text-[color:var(--ink)] hover:text-[color:var(--green)] hover:underline",
                            numeric ? "tabular-nums" : "",
                        ].join(" ")}
                    >
                        {text}
                    </button>
                );
            }

            return (
                <span
                    className={[
                        "truncate text-[11px] leading-[1.2] text-[color:var(--ink)]",
                        numeric ? "tabular-nums" : "",
                    ].join(" ")}
                    title={text}
                >
                    {text}
                </span>
            );
        },
        [columnDim, columnLabelCol, onOpenScope],
    );

    if (!columns.length || !allRows.length) return null;

    return (
        <div className="mt-[6px] overflow-x-auto rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]">
            <DataTable
                cols={cols}
                rows={visibleRows}
                getRowId={(_, i) => String(i)}
                renderCell={renderCell}
                estimatedRowHeight={26}
            />
            {truncated && !expanded ? (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="block w-full border-t border-[color:var(--line)] bg-transparent px-[10px] py-[5px] text-left text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:bg-[color:var(--control-bg)] cursor-pointer"
                >
                    Show {hidden} more {hidden === 1 ? "row" : "rows"}
                </button>
            ) : null}
            {truncated && expanded ? (
                // Sticky-bottom relative to the chat's ScrollArea viewport,
                // so the collapse affordance stays in reach while the user
                // reads down the expanded table. `left: 0` keeps it pinned
                // when the table horizontal-scrolls. When the entire table
                // fits in view (short expand), this just sits at its
                // natural bottom — no harm done.
                <div
                    className="border-t border-[color:var(--line)] bg-[color:var(--plot-bg)]"
                    style={{ position: "sticky", bottom: 0, left: 0 }}
                >
                    <button
                        type="button"
                        onClick={() => setExpanded(false)}
                        className="block w-full bg-transparent px-[10px] py-[5px] text-left text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:bg-[color:var(--control-bg)] cursor-pointer"
                    >
                        Collapse · showing all {allRows.length} {allRows.length === 1 ? "row" : "rows"}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

// Group detected scopes by dim and render one row per dim, each with a
// horizontal stack of clickable chips. Click → open as a new dashboard
// tab and close the chat console (so the user lands directly in the
// scope view).
//
// Labels prefer the dashboard-wide lazy label cache (via `useLabels` /
// `scopeStepLabel`) — same source the tab strip / breadcrumbs use, so
// hostnames / cluster names / etc. resolve once and are shared across
// chips, page, and any future panels. The row-derived label (carried
// in `s.label`) is the seed shown until the cached label arrives;
// after that, scopeStepLabel takes over.
function ScopeLinks({ scopes, onOpenScope }) {
    // Convert each scope to a step so the cache hook can pre-request
    // and subscribe to label updates in one batched call.
    const steps = useMemo(
        () => scopes.map((s) => ({ dim: s.dim, id: s.ids[0], ids: s.ids })),
        [scopes],
    );
    const labelTick = useLabels(steps);

    const grouped = useMemo(() => {
        const byDim = new Map();
        scopes.forEach((s, i) => {
            const cached = scopeStepLabel(steps[i]);
            // scopeStepLabel falls back to the raw id when the cache
            // misses; in that case prefer the row-derived label
            // (which often already has the sibling name column).
            const label = cached && cached !== s.ids[0] ? cached : s.label;
            if (!byDim.has(s.dim)) byDim.set(s.dim, []);
            byDim.get(s.dim).push({ ...s, displayLabel: label });
        });
        return [...byDim.entries()];
        // labelTick is intentionally in deps — the hook bumps it when a
        // batched label fetch resolves, so the memo recomputes off the
        // newly-cached values rather than staying on the id fallback.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopes, steps, labelTick]);

    if (!grouped.length) return null;
    return (
        <div className="mt-[6px] flex flex-col gap-[4px]">
            {grouped.map(([dim, list]) => (
                <div key={dim} className="flex items-center gap-[6px] flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        {DIMENSIONS[dim]?.label || dim}
                    </span>
                    {list.map((s, i) => (
                        <button
                            key={`${s.ids[0]}-${i}`}
                            type="button"
                            onClick={() => onOpenScope?.(s.dim, s.ids)}
                            title={s.ids[0]}
                            className="cursor-pointer border border-[color:var(--line)] bg-transparent px-[6px] py-[2px] text-[11px] leading-[1.2] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] [overflow-wrap:anywhere] max-w-full truncate"
                        >
                            {s.displayLabel}
                        </button>
                    ))}
                </div>
            ))}
        </div>
    );
}
